import { g as getAssetPath, r as registerInstance, E as Env } from './index-9a1fba2a.js';

/**
 * Compares the active acting-on-behalf-of with the required ones.
 * If `required` is nullish or empty, true is returned.
 * If `active` is nullish or empty, required must be nullish or empty as well.
 * If `required` is not empty, all the JWT's acting-on-behalf-of (normally just 1)
 * must match at least one of the given `required`.
 * @param baforPrefixes list of BAFOR prefixes in actingOnBehalfOf
 * @returns true if condition fulfilled
 */
const checkActingOnBehalfOf = (active, required) => {
    if (!required || !required.length) {
        // Caller doesn't care for actingOnBehalfOf
        return true;
    }
    if (!active || active.length === 0) {
        // only fine as long as explicitly no BaforPattern wanted
        return required.length === 0;
    }
    let matchAnyWantedPattern = false;
    for (const req of required) {
        let matchAllBehalfsToWanted = true;
        for (const act of active) {
            matchAllBehalfsToWanted && (matchAllBehalfsToWanted = act.startsWith(req));
        }
        matchAnyWantedPattern || (matchAnyWantedPattern = matchAllBehalfsToWanted);
    }
    return matchAnyWantedPattern;
};
/**
 * Checks whether the active acting-type is listed in `required`.
 * If `required` is nullish, this check is skipped and true is returned.
 * If `required` is empty, `active` must be nullish.
 * @param active the active/actual/current acting-type of the JWT
 * @param required the acting-type to compare against
 * @returns true in case of a match
 */
const checkActingType = (active, required) => {
    if (!required) {
        // caller appearently doesn't care.
        return true;
    }
    if (!active) {
        // No acting-type is only fine of explicitly none is wanted
        return required.length === 0;
    }
    return required.includes(active);
};
/**
 * Compare the actuve authn-level with `required` and return whether it is at least as high.
 * If no authn-level is required (`!required`), true is returned.
 * @param active the active/actual/current authentication level
 * @param required the required minimum authentication level
 * @returns true if at least this level is present
 */
const checkAuthnLevel = (active, required) => {
    if (!required) {
        return true;
    }
    if (active == null) {
        return false;
    }
    if (active === required) {
        return true;
    }
    // this only works as long as the AuthnLevels' hierarchy corresponds to their alphabetic order
    return active >= required;
};
/**
 * Compare active authn-method with required and return if it matches.
 * If no authn-method is required, true is returned.
 * @param active current authn-method (amr claim value)
 * @param required the required authn-method
 */
const checkAuthnMethod = (active, required) => {
    if (!required || required.length === 0) {
        return true;
    }
    return active.includes(required[0]);
};

class BaJwt {
    constructor(raw) {
        this._raw = raw;
        const ajwt = raw.split('.');
        if (ajwt.length === 3) {
            const decodedJwt = this.decodeJwt(ajwt[1]);
            this._payload = JSON.parse(decodedJwt);
        }
    }
    get raw() {
        return this._raw;
    }
    get payload() {
        return this._payload;
    }
    /**
     * Compare the JWT's authn-level with `a` and return whether it is at least as high.
     * If no authn-level is required (`!a`), true is returned.
     * @param a the required minimum authentication level
     * @returns true if at least this level is present
     */
    hasAtLeastAuthnLevel(a) {
        return checkAuthnLevel(this.effectiveAuthnLevel(), a);
    }
    effectiveAuthnLevel() {
        return this._payload?.['authn-level'];
    }
    /**
     * Compares the JWT's acting-on-behalf-of with the given baforPatterns.
     * If baforPatterns is undefined/null, true is returned.
     * If baforPatterns is empty, the JWT must be silver/bronze with no or empty
     * acting-on-behalf-of, thus return true if acting-on-behalf-of is missing there
     * as well.
     * If baforPatterns is not empty, all the JWT's acting-on-behalf-of (normally just 1)
     * must match at least one of the given baforPatterns.
     * @param baforPrefixes list of BAFOR prefixes in actingOnBehalfOf
     * @returns true if condition fulfilled
     */
    hasMatchingActingOnBehalfOf(baforPrefixes) {
        return checkActingOnBehalfOf(this.effectingActingOnBehalfOf(), baforPrefixes);
    }
    effectingActingOnBehalfOf() {
        return this._payload?.['acting-on-behalf-of'];
    }
    /**
     * Checks whether the JWT's acting-type is listed in af.
     * If af is undefined, this check is skipped and true is returned.
     * If af is empty, the JWT must not have an acting-type (Bronze).
     * @param af the acting-type to compare against
     * @returns true in case of a match
     */
    hasMatchingActingType(af) {
        return checkActingType(this.effectiveActingType(), af);
    }
    /**
     * Checks whether the JWT's amr matches the required authn-method
     * If am is undefined or empty, true is returned
     * @param am
     */
    hasMatchingAuthnMethod(am) {
        return checkAuthnMethod(this.effectiveAuthnMethod(), am);
    }
    effectiveAuthnMethod() {
        return this._payload?.amr ? this._payload.amr : [];
    }
    fulfills(ao) {
        if (ao.authnLevel && !this.hasAtLeastAuthnLevel(ao.authnLevel)) {
            return false;
        }
        if (ao.actingOnBehalfOf && !this.hasMatchingActingOnBehalfOf(ao.actingOnBehalfOf)) {
            return false;
        }
        if (ao.actingType && !this.hasMatchingActingType(ao.actingType)) {
            return false;
        }
        if (ao.authnMethod && !this.hasMatchingAuthnMethod(ao.authnMethod)) {
            return false;
        }
        return true;
    }
    /**
     * Return the expiration date as unix timestamp in seconds
     * (NumericDate in JWT speak).
     * Basically 1:1 the value `exp` from the JWT, except that 0 is returned when this
     * instance has been initialized with a malformed JWT.
     */
    get expirationDate() {
        return this._payload?.exp || 0;
    }
    effectiveActingType() {
        return this._payload?.['acting-type'];
    }
    decodeJwt(base64urlEncoded) {
        const base64NonUrlEncoded = base64urlEncoded
            .replace(/\s+/g, '')
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        const utf8BytesAsString = atob(base64NonUrlEncoded);
        const utf8ByteArray = Uint8Array.from(utf8BytesAsString, (m) => m.codePointAt(0) || 0);
        return new TextDecoder().decode(utf8ByteArray);
    }
}

const AccountTypes = ['bundid', 'passkey', 'totp', 'muk'];
const isSupportedAccountType = (t) => {
    return AccountTypes.includes(t);
};

var OiamEvents;
(function (OiamEvents) {
    OiamEvents["LOADED"] = "oiamLoadedEvent";
    OiamEvents["READYFORLOGIN"] = "oiamReadyForLoginEvent";
    OiamEvents["SETCONFIG"] = "oiamSetConfigEvent";
    OiamEvents["TOKEN"] = "oiamTokenEvent";
    OiamEvents["SENDTOKEN"] = "oiamSendTokenEvent";
    OiamEvents["DOLOGIN"] = "oiamLoginEvent";
    OiamEvents["DOLOGOUT"] = "oiamLogoutEvent";
    OiamEvents["ERROR"] = "oiamErrorEvent";
    /**
     * @deprecated replaced by IDLESESSIONEXPIRATIONWARNING
     */
    OiamEvents["IDLESESSIONEXPIREWARNING"] = "oiamIdleSessionExpireWarnEvent";
    OiamEvents["IDLESESSIONEXPIRATIONWARNING"] = "oiamIdleSessionExpirationWarnEvent";
    /**
     * @deprecated replaced by IDLESESSIONEXPIRATION
     */
    OiamEvents["IDLESESSIONEXPIRED"] = "oiamIdleSessionExpiredEvent";
    OiamEvents["IDLESESSIONEXPIRATION"] = "oiamIdleSessionExpirationEvent";
    OiamEvents["MAXSESSIONEXPIRATIONWARNING"] = "oiamMaxSessionExpirationWarnEvent";
    OiamEvents["MAXSESSIONEXPIRATION"] = "oiamMaxSessionExpirationEvent";
    OiamEvents["CUSTOMSTATE"] = "oiamCustomStateEvent";
    OiamEvents["SESSION"] = "oiamSessionEvent";
    OiamEvents["SENDSESSION"] = "oiamSendSessionEvent";
    OiamEvents["BEFORENAVIGATION"] = "oiamBeforeNavigationEvent";
})(OiamEvents || (OiamEvents = {}));

/**
 * Class which answers with a token, which is valid enough (at least for 30s) for the next API call.
 * If the input token fulfills this requirement, it is synchronously returned.
 * If the token is not valid enough, a token refresh is initiated and the refreshed token is
 * (asynchronously) returned.
 */
class TokenEnsurer {
    constructor() {
        this.cachedToken = undefined;
        /**
         * Checks a given token for validity and responds either with this token or a fresh one.
         * Must only be called when the user is logged after the eService has received a JWT
         * in a oiamTokenEvent for the first time.
         * If called before oiamSetConfigEvent with an empty input, this method will wait until
         * oiam-oauth-wc sends out its first oiamTokenEvent. If no oiamSetConfigEvent is sent
         * by the eService (which is a bug in itself), this method will wait indefinitely.
         * Internally, if needed, this method triggers a SendToken event and waits for the resulting
         * Token event and returns that.
         *
         * @param t an input JWT which is checked for validity and directly returned, if valid long enough.
         *
         * @returns a JWT which is valid and won't expire within the next 30s. If not logged in, returns ''
         * (but you shouldn't try call protected APIs in this state anyway!).
         */
        this.ensureValidToken = async (t) => {
            const b = t instanceof BaJwt ? t : new BaJwt(t);
            if (TokenEnsurer.validLongEnough(b)) {
                this.cachedToken = b;
                return b.raw;
            }
            if (TokenEnsurer.validLongEnough(this.cachedToken)) {
                return this.cachedToken.raw;
            }
            return new Promise((resolve) => {
                // we assume that newly sent tokens are valid enough
                window.self.addEventListener(OiamEvents.TOKEN, (e) => {
                    const raw = e.detail.jwt;
                    this.update(raw);
                    resolve(raw);
                }, { once: true });
                const sendTokenEvent = new CustomEvent(OiamEvents.SENDTOKEN);
                window.self.dispatchEvent(sendTokenEvent);
            });
        };
    }
    /**
     * Updates the cached token to be able to answer faster on the next call of ensureValidToken.
     * If t is not valid or empty, the cache is emptied.
     * @param t the new token, either already parsed or as a raw string
     */
    update(t) {
        const b = t instanceof BaJwt ? t : new BaJwt(t);
        if (TokenEnsurer.validLongEnough(b)) {
            this.cachedToken = b;
        }
        else {
            this.cachedToken = undefined;
        }
    }
    static validLongEnough(t) {
        return !!t && t.expirationDate * 1000 > new Date().getTime() + 30000;
    }
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getAugmentedNamespace(n) {
	if (n.__esModule) return n;
	var a = Object.defineProperty({}, '__esModule', {value: true});
	Object.keys(n).forEach(function (k) {
		var d = Object.getOwnPropertyDescriptor(n, k);
		Object.defineProperty(a, k, d.get ? d : {
			enumerable: true,
			get: function () {
				return n[k];
			}
		});
	});
	return a;
}

var loglevel = {exports: {}};

/*
* loglevel - https://github.com/pimterry/loglevel
*
* Copyright (c) 2013 Tim Perry
* Licensed under the MIT license.
*/

(function (module) {
(function (root, definition) {
    if (module.exports) {
        module.exports = definition();
    } else {
        root.log = definition();
    }
}(commonjsGlobal, function () {

    // Slightly dubious tricks to cut down minimized file size
    var noop = function() {};
    var undefinedType = "undefined";
    var isIE = (typeof window !== undefinedType) && (typeof window.navigator !== undefinedType) && (
        /Trident\/|MSIE /.test(window.navigator.userAgent)
    );

    var logMethods = [
        "trace",
        "debug",
        "info",
        "warn",
        "error"
    ];

    var _loggersByName = {};
    var defaultLogger = null;

    // Cross-browser bind equivalent that works at least back to IE6
    function bindMethod(obj, methodName) {
        var method = obj[methodName];
        if (typeof method.bind === 'function') {
            return method.bind(obj);
        } else {
            try {
                return Function.prototype.bind.call(method, obj);
            } catch (e) {
                // Missing bind shim or IE8 + Modernizr, fallback to wrapping
                return function() {
                    return Function.prototype.apply.apply(method, [obj, arguments]);
                };
            }
        }
    }

    // Trace() doesn't print the message in IE, so for that case we need to wrap it
    function traceForIE() {
        if (console.log) {
            if (console.log.apply) {
                console.log.apply(console, arguments);
            } else {
                // In old IE, native console methods themselves don't have apply().
                Function.prototype.apply.apply(console.log, [console, arguments]);
            }
        }
        if (console.trace) console.trace();
    }

    // Build the best logging method possible for this env
    // Wherever possible we want to bind, not wrap, to preserve stack traces
    function realMethod(methodName) {
        if (methodName === 'debug') {
            methodName = 'log';
        }

        if (typeof console === undefinedType) {
            return false; // No method possible, for now - fixed later by enableLoggingWhenConsoleArrives
        } else if (methodName === 'trace' && isIE) {
            return traceForIE;
        } else if (console[methodName] !== undefined) {
            return bindMethod(console, methodName);
        } else if (console.log !== undefined) {
            return bindMethod(console, 'log');
        } else {
            return noop;
        }
    }

    // These private functions always need `this` to be set properly

    function replaceLoggingMethods() {
        /*jshint validthis:true */
        var level = this.getLevel();

        // Replace the actual methods.
        for (var i = 0; i < logMethods.length; i++) {
            var methodName = logMethods[i];
            this[methodName] = (i < level) ?
                noop :
                this.methodFactory(methodName, level, this.name);
        }

        // Define log.log as an alias for log.debug
        this.log = this.debug;

        // Return any important warnings.
        if (typeof console === undefinedType && level < this.levels.SILENT) {
            return "No console available for logging";
        }
    }

    // In old IE versions, the console isn't present until you first open it.
    // We build realMethod() replacements here that regenerate logging methods
    function enableLoggingWhenConsoleArrives(methodName) {
        return function () {
            if (typeof console !== undefinedType) {
                replaceLoggingMethods.call(this);
                this[methodName].apply(this, arguments);
            }
        };
    }

    // By default, we use closely bound real methods wherever possible, and
    // otherwise we wait for a console to appear, and then try again.
    function defaultMethodFactory(methodName, _level, _loggerName) {
        /*jshint validthis:true */
        return realMethod(methodName) ||
               enableLoggingWhenConsoleArrives.apply(this, arguments);
    }

    function Logger(name, factory) {
      // Private instance variables.
      var self = this;
      /**
       * The level inherited from a parent logger (or a global default). We
       * cache this here rather than delegating to the parent so that it stays
       * in sync with the actual logging methods that we have installed (the
       * parent could change levels but we might not have rebuilt the loggers
       * in this child yet).
       * @type {number}
       */
      var inheritedLevel;
      /**
       * The default level for this logger, if any. If set, this overrides
       * `inheritedLevel`.
       * @type {number|null}
       */
      var defaultLevel;
      /**
       * A user-specific level for this logger. If set, this overrides
       * `defaultLevel`.
       * @type {number|null}
       */
      var userLevel;

      var storageKey = "loglevel";
      if (typeof name === "string") {
        storageKey += ":" + name;
      } else if (typeof name === "symbol") {
        storageKey = undefined;
      }

      function persistLevelIfPossible(levelNum) {
          var levelName = (logMethods[levelNum] || 'silent').toUpperCase();

          if (typeof window === undefinedType || !storageKey) return;

          // Use localStorage if available
          try {
              window.localStorage[storageKey] = levelName;
              return;
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=" + levelName + ";";
          } catch (ignore) {}
      }

      function getPersistedLevel() {
          var storedLevel;

          if (typeof window === undefinedType || !storageKey) return;

          try {
              storedLevel = window.localStorage[storageKey];
          } catch (ignore) {}

          // Fallback to cookies if local storage gives us nothing
          if (typeof storedLevel === undefinedType) {
              try {
                  var cookie = window.document.cookie;
                  var cookieName = encodeURIComponent(storageKey);
                  var location = cookie.indexOf(cookieName + "=");
                  if (location !== -1) {
                      storedLevel = /^([^;]+)/.exec(
                          cookie.slice(location + cookieName.length + 1)
                      )[1];
                  }
              } catch (ignore) {}
          }

          // If the stored level is not valid, treat it as if nothing was stored.
          if (self.levels[storedLevel] === undefined) {
              storedLevel = undefined;
          }

          return storedLevel;
      }

      function clearPersistedLevel() {
          if (typeof window === undefinedType || !storageKey) return;

          // Use localStorage if available
          try {
              window.localStorage.removeItem(storageKey);
          } catch (ignore) {}

          // Use session cookie as fallback
          try {
              window.document.cookie =
                encodeURIComponent(storageKey) + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC";
          } catch (ignore) {}
      }

      function normalizeLevel(input) {
          var level = input;
          if (typeof level === "string" && self.levels[level.toUpperCase()] !== undefined) {
              level = self.levels[level.toUpperCase()];
          }
          if (typeof level === "number" && level >= 0 && level <= self.levels.SILENT) {
              return level;
          } else {
              throw new TypeError("log.setLevel() called with invalid level: " + input);
          }
      }

      /*
       *
       * Public logger API - see https://github.com/pimterry/loglevel for details
       *
       */

      self.name = name;

      self.levels = { "TRACE": 0, "DEBUG": 1, "INFO": 2, "WARN": 3,
          "ERROR": 4, "SILENT": 5};

      self.methodFactory = factory || defaultMethodFactory;

      self.getLevel = function () {
          if (userLevel != null) {
            return userLevel;
          } else if (defaultLevel != null) {
            return defaultLevel;
          } else {
            return inheritedLevel;
          }
      };

      self.setLevel = function (level, persist) {
          userLevel = normalizeLevel(level);
          if (persist !== false) {  // defaults to true
              persistLevelIfPossible(userLevel);
          }

          // NOTE: in v2, this should call rebuild(), which updates children.
          return replaceLoggingMethods.call(self);
      };

      self.setDefaultLevel = function (level) {
          defaultLevel = normalizeLevel(level);
          if (!getPersistedLevel()) {
              self.setLevel(level, false);
          }
      };

      self.resetLevel = function () {
          userLevel = null;
          clearPersistedLevel();
          replaceLoggingMethods.call(self);
      };

      self.enableAll = function(persist) {
          self.setLevel(self.levels.TRACE, persist);
      };

      self.disableAll = function(persist) {
          self.setLevel(self.levels.SILENT, persist);
      };

      self.rebuild = function () {
          if (defaultLogger !== self) {
              inheritedLevel = normalizeLevel(defaultLogger.getLevel());
          }
          replaceLoggingMethods.call(self);

          if (defaultLogger === self) {
              for (var childName in _loggersByName) {
                _loggersByName[childName].rebuild();
              }
          }
      };

      // Initialize all the internal levels.
      inheritedLevel = normalizeLevel(
          defaultLogger ? defaultLogger.getLevel() : "WARN"
      );
      var initialLevel = getPersistedLevel();
      if (initialLevel != null) {
          userLevel = normalizeLevel(initialLevel);
      }
      replaceLoggingMethods.call(self);
    }

    /*
     *
     * Top-level API
     *
     */

    defaultLogger = new Logger();

    defaultLogger.getLogger = function getLogger(name) {
        if ((typeof name !== "symbol" && typeof name !== "string") || name === "") {
            throw new TypeError("You must supply a name when creating a logger.");
        }

        var logger = _loggersByName[name];
        if (!logger) {
            logger = _loggersByName[name] = new Logger(
                name,
                defaultLogger.methodFactory
            );
        }
        return logger;
    };

    // Grab the current global log variable in case of overwrite
    var _log = (typeof window !== undefinedType) ? window.log : undefined;
    defaultLogger.noConflict = function() {
        if (typeof window !== undefinedType &&
               window.log === defaultLogger) {
            window.log = _log;
        }

        return defaultLogger;
    };

    defaultLogger.getLoggers = function getLoggers() {
        return _loggersByName;
    };

    // ES6 default export, for compatibility
    defaultLogger['default'] = defaultLogger;

    return defaultLogger;
}));
}(loglevel));

const log = loglevel.exports;

var OauthStates;
(function (OauthStates) {
    OauthStates["EMPTY"] = "empty";
    OauthStates["LOGINREQUESTED"] = "login-requested";
    /** enforce a re-login, probably a stepup (without logout) */
    OauthStates["RELOGINREQUESTED"] = "relogin-requested";
    /** when a logout with subsequent login has been triggered */
    OauthStates["REAUTHREQUIRED"] = "reauth-required";
    /** directly enforce profile-selection even without having a JWT first */
    OauthStates["PROFILESELECTIONREQUESTED"] = "profile-selection-requested";
    /** is set either in init or after auth code flow, must not be pushed explicitly */
    OauthStates["LOGGEDIN"] = "logged-in";
    OauthStates["LOGOUTREQUESTED"] = "logout-requested";
    OauthStates["LOGOUTREQUESTEDEXTERNAL"] = "logout-requested-external";
    /** transitional state, handled differently from LOGGEDOUT, but at the end we're LOGGEDOUT */
    OauthStates["LOGGEDOUTEXTERNAL"] = "logged-out-external";
    OauthStates["IDLETIMEDOUT"] = "idle-session-timed-out";
    OauthStates["MAXTIMEDOUT"] = "max-session-timed-out";
    OauthStates["LOGOUTAFTERIDLESESSIONTIMEOUT"] = "logged-out-after-idle-session-timeout";
    OauthStates["LOGOUTAFTERMAXSESSIONTIMEOUT"] = "logged-out-after-max-session-timeout";
    /** transitional state, handled differently from LOGOUTREDIRECTED, but at the end we're LOGGEDOUT (or not) */
    OauthStates["LOGOUTREDIRECTEDEXTERNAL"] = "logout-redirected-external";
    OauthStates["RESENDTOKEN"] = "resend-token";
    OauthStates["RENEWTOKEN"] = "renew-token";
    OauthStates["ACCOUNTLINK_REQUESTED"] = "account-link-requested";
})(OauthStates || (OauthStates = {}));
var OiamCookie;
(function (OiamCookie) {
    OiamCookie["SESSIONID"] = "BA-SessionId";
    OiamCookie["SESSIONCOOKIE"] = "oiamsession";
    OiamCookie["BACKENDACCESSCOOKIE"] = "BackendAccessTS";
})(OiamCookie || (OiamCookie = {}));

function isOiamErrorPayload(payload) {
    if (typeof payload === 'object') {
        return 'id' in payload && 'message' in payload;
    }
    return false;
}
const ERR_NOT_CONFIGURED = {
    id: 'OIAM-01',
    message: 'oiam-oauth-wc is not correctly configured',
};
const ERR_INVALID_CONFIG = {
    id: 'OIAM-02',
    message: 'Invalid oiamSetConfigEvent',
};
const ERR_NO_USER = {
    id: 'OIAM-03',
    message: 'No valid user token at token response',
};
const ERR_NO_TOKEN = {
    id: 'OIAM-04',
    message: 'Error Token-Refresh',
};
const ERR_OIAM_GENERAL = {
    id: 'OIAM-06',
    message: 'Error from OIAM',
};
const ERR_LOGIN = {
    id: 'OIAM-08',
    message: 'Error Login',
};
const ERR_RELOGIN = {
    id: 'OIAM-09',
    message: 'Error Relogin',
};
const ERR_UNMET = {
    id: 'OIAM-10',
    message: 'Unmet requirements',
};
const ERR_OIAM_MULTIPLE_INSTANCES = {
    id: 'OIAM-99',
    message: 'More than one oiam-oauth-wc instance loaded in page',
};
class OiamErrorPayloadException extends Error {
    constructor(p) {
        super(p.message);
        this.payload = p;
    }
    static extract(err) {
        if (err && err instanceof OiamErrorPayloadException) {
            return err.payload;
        }
        else {
            return err;
        }
    }
}

const filterForcedActingOnBehalfOf = (forcedAobo, actingOnBehalfOf, requiredActingType) => {
    // no forced-acting-on-behalf-of -> just return the required acting-on-behalf-of
    // it always matches the required acting-type (setConfig check)
    if (!forcedAobo || forcedAobo.length === 0) {
        return actingOnBehalfOf;
    }
    // keep all which startWith at least one of the required actingOnBehalfOfs if any
    // else just take the forcedActingOnBehalfOf
    const filteredAgainstActingOnBehalfOf = actingOnBehalfOf && actingOnBehalfOf.length > 0
        ? forcedAobo.filter(forced => actingOnBehalfOf.filter(aobo => forced.startsWith(aobo)).length > 0)
        : forcedAobo;
    // no required acting-type -> just return the current filter result
    if (!requiredActingType || requiredActingType.length === 0) {
        return filteredAgainstActingOnBehalfOf;
    }
    // filter against required acting-type
    const filteredAgainstActingType = [];
    for (const bafor of filteredAgainstActingOnBehalfOf) {
        const allowedActingType = getAllowedActingTypes(bafor);
        if (requiredActingType.some(a => allowedActingType.includes(a))) {
            filteredAgainstActingType.push(bafor);
        }
    }
    return filteredAgainstActingType;
};
const buildAcrValues = (opts, filterOrType, forcedActingOnBehalfOf) => {
    if (!opts && !forcedActingOnBehalfOf?.length) {
        return '';
    }
    const auth = (opts.authnLevel && `authn-level=${opts.authnLevel}`) || null;
    const filtered = filterForcedActingOnBehalfOf(forcedActingOnBehalfOf, opts.actingOnBehalfOf, opts.actingType);
    const aobo = (filtered && filtered.length && filterOrType === 'acting-type' && `acting-on-behalf-of=${filtered.join(',')}`) ||
        null;
    //no acting-type in combination with forced-acting-on-behalf-of -> prevent invalid combination at profil selection
    const actt = (hasActingType(opts.actingType) &&
        useActingType(filterOrType, forcedActingOnBehalfOf) &&
        `${filterOrType}=${opts.actingType.join(',')}`) ||
        null;
    const authnMethod = (filterOrType === 'login-filter' &&
        opts.authnMethod?.length > 0 &&
        !(opts.authnLevel === 'STORK-QAA-Level-3' || opts.authnLevel === 'STORK-QAA-Level-4') &&
        `authn-method=${opts.authnMethod.join(',')}`) ||
        null;
    const forceps = (filterOrType === 'acting-type' && opts.forceProfileSelection && 'force-profile-selection=true') || null;
    return [auth, authnMethod, actt, aobo, forceps].filter(s => !!s).join('&');
};
const useActingType = (filterOrType, forcedActingOnBehalfOf) => {
    return filterOrType === 'login-filter' || !forcedActingOnBehalfOf || forcedActingOnBehalfOf.length === 0;
};
const hasActingType = (actingType) => {
    return actingType && actingType.length > 0;
};
const checkOwnAndExternalProfile = ({ ownCurrent, ownRequested, externalCurrent, }) => {
    const externalMatchesOwnRequestedActingType = checkActingType(externalCurrent.actingType, ownRequested.actingType);
    const externalMatchesOwnRequestedActingOnBehalfOf = checkActingOnBehalfOf(externalCurrent.actingOnBehalfOf, ownRequested.actingOnBehalfOf);
    if (ownRequested.forcedActingOnBehalfOf?.length) {
        const noProfileSelected = !ownCurrent.actingOnBehalfOf?.length;
        const alreadySelectedAForcedProfile = !!ownCurrent.actingOnBehalfOf?.filter(a => ownRequested.forcedActingOnBehalfOf.includes(a)).length;
        if (noProfileSelected || !alreadySelectedAForcedProfile) {
            // at least not compatible with the force, Luke
            return 'incompatible';
        }
    }
    if (!externalMatchesOwnRequestedActingType || !externalMatchesOwnRequestedActingOnBehalfOf) {
        return 'incompatible';
    }
    const actingOnBehalfOfChanged = actingOnBehalfOfChange(ownCurrent.actingOnBehalfOf, externalCurrent.actingOnBehalfOf);
    const actingTypeChanged = actingTypeChange(ownCurrent.actingType, externalCurrent.actingType);
    if (ownRequested?.actingOnBehalfOf?.length && actingOnBehalfOfChanged) {
        return 'compatible';
    }
    if (ownRequested?.actingType && actingTypeChanged) {
        return 'compatible';
    }
    if (ownRequested.forceProfileSelection) {
        return 'force';
    }
    if (actingOnBehalfOfChanged || actingTypeChanged) {
        return 'irrelevant';
    }
    else {
        return 'none';
    }
};
const actingTypeChange = (a, b) => {
    return a != b;
};
const actingOnBehalfOfChange = (a, b) => {
    if ((!a && b) || (a && !b)) {
        return true;
    }
    if (!a && !b) {
        return false;
    }
    if (a.length != b.length) {
        // case which doesn't occur yet
        return true;
    }
    return !a.every(x => b.includes(x));
};
/**
 * Check, if at least one of the forced-acting-on-behalf-of fulfills the requirements of the eService.
 * It is checked against the required acting-on-behalf-of and the required acting-type.
 */
const fulfillsAnyForceActingOnBehalfOfRequirements = (forcedActingOnBehalfOf, requiredActingOnBehalfOf, requiredActingType) => {
    // if we have no forcedActingOnBehalfOf or no eService requirements we can return true
    if (!forcedActingOnBehalfOf ||
        forcedActingOnBehalfOf.length === 0 ||
        ((!requiredActingType || requiredActingType.length === 0) &&
            (!requiredActingOnBehalfOf || requiredActingOnBehalfOf.length === 0))) {
        return true;
    }
    // filter forced-acting-on-behalf-of against eService requirements
    // if there is any match, at least one forced-acting-on-behalf-of fulfills the eService requirements
    const filtered = filterForcedActingOnBehalfOf(forcedActingOnBehalfOf, requiredActingOnBehalfOf, requiredActingType);
    return filtered.length > 0;
};
/**
 * Check, if a given Combination of acting-type and acting-on-behalf-of is valid.
 * All given acting-on-behalf-ofs must match at least one acting-type.
 * All given acting-types must match at least one acting-on-behalf-of.
 */
const isAllowedCombination = (actingType, actingOnBehalfOf) => {
    // if there is no combination, it is always valid
    if (!actingType?.length || !actingOnBehalfOf?.length) {
        return true;
    }
    // get all allowed acting-types for all acting-on-behalf-of
    let valid = true;
    const allowedActingTypes = actingOnBehalfOf.flatMap(bafor => {
        // check if every acting-on-behalf-of has at least one acting-type match
        const allowed = getAllowedActingTypes(bafor);
        if (actingType.filter(a => allowed.includes(a)).length === 0) {
            valid = false;
        }
        return allowed;
    });
    // check all given acting-types have at least one match
    return valid && actingType.every(at => allowedActingTypes.includes(at)) && allowedActingTypes.length > 0;
};
/**
 * An acting-on-behalf-of allows only certain acting-type(s) as valid combination.
 *
 * Compare acting-type(s) against acting-on-behalf-of(s)
 * - ...:step:... -> either privatperson or unternehmen
 * - ...:step:person:... -> must contain privatperson
 * - ...:step:betrieb:... -> must contain unternehmen
 * - ...:kiwi:... -> must contain privatperson
 * - ...:pmventerprise:... -> must contain partner
 */
const getAllowedActingTypes = (bafor) => {
    if (bafor === 'urn:bafor:step:') {
        return ['privatperson', 'unternehmen'];
    }
    if (bafor.startsWith('urn:bafor:step:betrieb:')) {
        return ['unternehmen'];
    }
    if (bafor.startsWith('urn:bafor:pmventerprise:')) {
        return ['partner'];
    }
    return ['privatperson'];
};

class InvalidTokenError extends Error {
}
InvalidTokenError.prototype.name = "InvalidTokenError";
function b64DecodeUnicode(str) {
    return decodeURIComponent(atob(str).replace(/(.)/g, (m, p) => {
        let code = p.charCodeAt(0).toString(16).toUpperCase();
        if (code.length < 2) {
            code = "0" + code;
        }
        return "%" + code;
    }));
}
function base64UrlDecode(str) {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
        case 0:
            break;
        case 2:
            output += "==";
            break;
        case 3:
            output += "=";
            break;
        default:
            throw new Error("base64 string is not of the correct length");
    }
    try {
        return b64DecodeUnicode(output);
    }
    catch (err) {
        return atob(output);
    }
}
function jwtDecode(token, options) {
    if (typeof token !== "string") {
        throw new InvalidTokenError("Invalid token specified: must be a string");
    }
    options || (options = {});
    const pos = options.header === true ? 0 : 1;
    const part = token.split(".")[pos];
    if (typeof part !== "string") {
        throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
    }
    let decoded;
    try {
        decoded = base64UrlDecode(part);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid base64 for part #${pos + 1} (${e.message})`);
    }
    try {
        return JSON.parse(decoded);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid json for part #${pos + 1} (${e.message})`);
    }
}

const esm = /*#__PURE__*/Object.freeze({
    __proto__: null,
    InvalidTokenError: InvalidTokenError,
    jwtDecode: jwtDecode
});

const require$$0 = /*@__PURE__*/getAugmentedNamespace(esm);

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __toCommonJS = (mod) => __copyProps(__defProp({}, "__esModule", { value: true }), mod);

// src/index.ts
var src_exports = {};
__export(src_exports, {
  AccessTokenEvents: () => AccessTokenEvents,
  CheckSessionIFrame: () => CheckSessionIFrame,
  ErrorResponse: () => ErrorResponse,
  ErrorTimeout: () => ErrorTimeout,
  InMemoryWebStorage: () => InMemoryWebStorage,
  Log: () => Log,
  Logger: () => Logger,
  MetadataService: () => MetadataService,
  OidcClient: () => OidcClient,
  OidcClientSettingsStore: () => OidcClientSettingsStore,
  SessionMonitor: () => SessionMonitor,
  SigninResponse: () => SigninResponse,
  SigninState: () => SigninState,
  SignoutResponse: () => SignoutResponse,
  State: () => State,
  User: () => User,
  UserManager: () => UserManager,
  UserManagerSettingsStore: () => UserManagerSettingsStore,
  Version: () => Version,
  WebStorageStateStore: () => WebStorageStateStore
});
var oidcClientTs = __toCommonJS(src_exports);

// src/utils/Logger.ts
var nopLogger = {
  debug: () => void 0,
  info: () => void 0,
  warn: () => void 0,
  error: () => void 0
};
var level;
var logger;
var Log = /* @__PURE__ */ ((Log2) => {
  Log2[Log2["NONE"] = 0] = "NONE";
  Log2[Log2["ERROR"] = 1] = "ERROR";
  Log2[Log2["WARN"] = 2] = "WARN";
  Log2[Log2["INFO"] = 3] = "INFO";
  Log2[Log2["DEBUG"] = 4] = "DEBUG";
  return Log2;
})(Log || {});
((Log2) => {
  function reset() {
    level = 3 /* INFO */;
    logger = nopLogger;
  }
  Log2.reset = reset;
  function setLevel(value) {
    if (!(0 /* NONE */ <= value && value <= 4 /* DEBUG */)) {
      throw new Error("Invalid log level");
    }
    level = value;
  }
  Log2.setLevel = setLevel;
  function setLogger(value) {
    logger = value;
  }
  Log2.setLogger = setLogger;
})(Log || (Log = {}));
var Logger = class _Logger {
  constructor(_name) {
    this._name = _name;
  }
  /* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
  debug(...args) {
    if (level >= 4 /* DEBUG */) {
      logger.debug(_Logger._format(this._name, this._method), ...args);
    }
  }
  info(...args) {
    if (level >= 3 /* INFO */) {
      logger.info(_Logger._format(this._name, this._method), ...args);
    }
  }
  warn(...args) {
    if (level >= 2 /* WARN */) {
      logger.warn(_Logger._format(this._name, this._method), ...args);
    }
  }
  error(...args) {
    if (level >= 1 /* ERROR */) {
      logger.error(_Logger._format(this._name, this._method), ...args);
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-enum-comparison */
  throw(err) {
    this.error(err);
    throw err;
  }
  create(method) {
    const methodLogger = Object.create(this);
    methodLogger._method = method;
    methodLogger.debug("begin");
    return methodLogger;
  }
  static createStatic(name, staticMethod) {
    const staticLogger = new _Logger(`${name}.${staticMethod}`);
    staticLogger.debug("begin");
    return staticLogger;
  }
  static _format(name, method) {
    const prefix = `[${name}]`;
    return method ? `${prefix} ${method}:` : prefix;
  }
  /* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
  // helpers for static class methods
  static debug(name, ...args) {
    if (level >= 4 /* DEBUG */) {
      logger.debug(_Logger._format(name), ...args);
    }
  }
  static info(name, ...args) {
    if (level >= 3 /* INFO */) {
      logger.info(_Logger._format(name), ...args);
    }
  }
  static warn(name, ...args) {
    if (level >= 2 /* WARN */) {
      logger.warn(_Logger._format(name), ...args);
    }
  }
  static error(name, ...args) {
    if (level >= 1 /* ERROR */) {
      logger.error(_Logger._format(name), ...args);
    }
  }
  /* eslint-enable @typescript-eslint/no-unsafe-enum-comparison */
};
Log.reset();

// src/utils/CryptoUtils.ts
var UUID_V4_TEMPLATE = "10000000-1000-4000-8000-100000000000";
var toBase64 = (val) => btoa([...new Uint8Array(val)].map((chr) => String.fromCharCode(chr)).join(""));
var CryptoUtils = class _CryptoUtils {
  static _randomWord() {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0];
  }
  /**
   * Generates RFC4122 version 4 guid
   */
  static generateUUIDv4() {
    const uuid = UUID_V4_TEMPLATE.replace(
      /[018]/g,
      (c) => (+c ^ _CryptoUtils._randomWord() & 15 >> +c / 4).toString(16)
    );
    return uuid.replace(/-/g, "");
  }
  /**
   * PKCE: Generate a code verifier
   */
  static generateCodeVerifier() {
    return _CryptoUtils.generateUUIDv4() + _CryptoUtils.generateUUIDv4() + _CryptoUtils.generateUUIDv4();
  }
  /**
   * PKCE: Generate a code challenge
   */
  static async generateCodeChallenge(code_verifier) {
    if (!crypto.subtle) {
      throw new Error("Crypto.subtle is available only in secure contexts (HTTPS).");
    }
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(code_verifier);
      const hashed = await crypto.subtle.digest("SHA-256", data);
      return toBase64(hashed).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    } catch (err) {
      Logger.error("CryptoUtils.generateCodeChallenge", err);
      throw err;
    }
  }
  /**
   * Generates a base64-encoded string for a basic auth header
   */
  static generateBasicAuth(client_id, client_secret) {
    const encoder = new TextEncoder();
    const data = encoder.encode([client_id, client_secret].join(":"));
    return toBase64(data);
  }
};

// src/utils/Event.ts
var Event = class {
  constructor(_name) {
    this._name = _name;
    this._logger = new Logger(`Event('${this._name}')`);
    this._callbacks = [];
  }
  addHandler(cb) {
    this._callbacks.push(cb);
    return () => this.removeHandler(cb);
  }
  removeHandler(cb) {
    const idx = this._callbacks.lastIndexOf(cb);
    if (idx >= 0) {
      this._callbacks.splice(idx, 1);
    }
  }
  async raise(...ev) {
    this._logger.debug("raise:", ...ev);
    for (const cb of this._callbacks) {
      await cb(...ev);
    }
  }
};

// src/utils/JwtUtils.ts
var import_jwt_decode = require$$0;
var JwtUtils = class {
  // IMPORTANT: doesn't validate the token
  static decode(token) {
    try {
      return (0, import_jwt_decode.jwtDecode)(token);
    } catch (err) {
      Logger.error("JwtUtils.decode", err);
      throw err;
    }
  }
};

// src/utils/PopupUtils.ts
var PopupUtils = class {
  /**
   * Populates a map of window features with a placement centered in front of
   * the current window. If no explicit width is given, a default value is
   * binned into [800, 720, 600, 480, 360] based on the current window's width.
   */
  static center({ ...features }) {
    var _a, _b, _c;
    if (features.width == null)
      features.width = (_a = [800, 720, 600, 480].find((width) => width <= window.outerWidth / 1.618)) != null ? _a : 360;
    (_b = features.left) != null ? _b : features.left = Math.max(0, Math.round(window.screenX + (window.outerWidth - features.width) / 2));
    if (features.height != null)
      (_c = features.top) != null ? _c : features.top = Math.max(0, Math.round(window.screenY + (window.outerHeight - features.height) / 2));
    return features;
  }
  static serialize(features) {
    return Object.entries(features).filter(([, value]) => value != null).map(([key, value]) => `${key}=${typeof value !== "boolean" ? value : value ? "yes" : "no"}`).join(",");
  }
};

// src/utils/Timer.ts
var Timer = class _Timer extends Event {
  constructor() {
    super(...arguments);
    this._logger = new Logger(`Timer('${this._name}')`);
    this._timerHandle = null;
    this._expiration = 0;
    this._callback = () => {
      const diff = this._expiration - _Timer.getEpochTime();
      this._logger.debug("timer completes in", diff);
      if (this._expiration <= _Timer.getEpochTime()) {
        this.cancel();
        void super.raise();
      }
    };
  }
  // get the time
  static getEpochTime() {
    return Math.floor(Date.now() / 1e3);
  }
  init(durationInSeconds) {
    const logger2 = this._logger.create("init");
    durationInSeconds = Math.max(Math.floor(durationInSeconds), 1);
    const expiration = _Timer.getEpochTime() + durationInSeconds;
    if (this.expiration === expiration && this._timerHandle) {
      logger2.debug("skipping since already initialized for expiration at", this.expiration);
      return;
    }
    this.cancel();
    logger2.debug("using duration", durationInSeconds);
    this._expiration = expiration;
    const timerDurationInSeconds = Math.min(durationInSeconds, 5);
    this._timerHandle = setInterval(this._callback, timerDurationInSeconds * 1e3);
  }
  get expiration() {
    return this._expiration;
  }
  cancel() {
    this._logger.create("cancel");
    if (this._timerHandle) {
      clearInterval(this._timerHandle);
      this._timerHandle = null;
    }
  }
};

// src/utils/UrlUtils.ts
var UrlUtils = class {
  static readParams(url, responseMode = "query") {
    if (!url)
      throw new TypeError("Invalid URL");
    const parsedUrl = new URL(url, "http://127.0.0.1");
    const params = parsedUrl[responseMode === "fragment" ? "hash" : "search"];
    return new URLSearchParams(params.slice(1));
  }
};
var URL_STATE_DELIMITER = ";";

// src/errors/ErrorResponse.ts
var ErrorResponse = class extends Error {
  constructor(args, form) {
    var _a, _b, _c;
    super(args.error_description || args.error || "");
    this.form = form;
    /** Marker to detect class: "ErrorResponse" */
    this.name = "ErrorResponse";
    if (!args.error) {
      Logger.error("ErrorResponse", "No error passed");
      throw new Error("No error passed");
    }
    this.error = args.error;
    this.error_description = (_a = args.error_description) != null ? _a : null;
    this.error_uri = (_b = args.error_uri) != null ? _b : null;
    this.state = args.userState;
    this.session_state = (_c = args.session_state) != null ? _c : null;
    this.url_state = args.url_state;
  }
};

// src/errors/ErrorTimeout.ts
var ErrorTimeout = class extends Error {
  constructor(message) {
    super(message);
    /** Marker to detect class: "ErrorTimeout" */
    this.name = "ErrorTimeout";
  }
};

// src/AccessTokenEvents.ts
var AccessTokenEvents = class {
  constructor(args) {
    this._logger = new Logger("AccessTokenEvents");
    this._expiringTimer = new Timer("Access token expiring");
    this._expiredTimer = new Timer("Access token expired");
    this._expiringNotificationTimeInSeconds = args.expiringNotificationTimeInSeconds;
  }
  load(container) {
    const logger2 = this._logger.create("load");
    if (container.access_token && container.expires_in !== void 0) {
      const duration = container.expires_in;
      logger2.debug("access token present, remaining duration:", duration);
      if (duration > 0) {
        let expiring = duration - this._expiringNotificationTimeInSeconds;
        if (expiring <= 0) {
          expiring = 1;
        }
        logger2.debug("registering expiring timer, raising in", expiring, "seconds");
        this._expiringTimer.init(expiring);
      } else {
        logger2.debug("canceling existing expiring timer because we're past expiration.");
        this._expiringTimer.cancel();
      }
      const expired = duration + 1;
      logger2.debug("registering expired timer, raising in", expired, "seconds");
      this._expiredTimer.init(expired);
    } else {
      this._expiringTimer.cancel();
      this._expiredTimer.cancel();
    }
  }
  unload() {
    this._logger.debug("unload: canceling existing access token timers");
    this._expiringTimer.cancel();
    this._expiredTimer.cancel();
  }
  /**
   * Add callback: Raised prior to the access token expiring.
   */
  addAccessTokenExpiring(cb) {
    return this._expiringTimer.addHandler(cb);
  }
  /**
   * Remove callback: Raised prior to the access token expiring.
   */
  removeAccessTokenExpiring(cb) {
    this._expiringTimer.removeHandler(cb);
  }
  /**
   * Add callback: Raised after the access token has expired.
   */
  addAccessTokenExpired(cb) {
    return this._expiredTimer.addHandler(cb);
  }
  /**
   * Remove callback: Raised after the access token has expired.
   */
  removeAccessTokenExpired(cb) {
    this._expiredTimer.removeHandler(cb);
  }
};

// src/CheckSessionIFrame.ts
var CheckSessionIFrame = class {
  constructor(_callback, _client_id, url, _intervalInSeconds, _stopOnError) {
    this._callback = _callback;
    this._client_id = _client_id;
    this._intervalInSeconds = _intervalInSeconds;
    this._stopOnError = _stopOnError;
    this._logger = new Logger("CheckSessionIFrame");
    this._timer = null;
    this._session_state = null;
    this._message = (e) => {
      if (e.origin === this._frame_origin && e.source === this._frame.contentWindow) {
        if (e.data === "error") {
          this._logger.error("error message from check session op iframe");
          if (this._stopOnError) {
            this.stop();
          }
        } else if (e.data === "changed") {
          this._logger.debug("changed message from check session op iframe");
          this.stop();
          void this._callback();
        } else {
          this._logger.debug(e.data + " message from check session op iframe");
        }
      }
    };
    const parsedUrl = new URL(url);
    this._frame_origin = parsedUrl.origin;
    this._frame = window.document.createElement("iframe");
    this._frame.style.visibility = "hidden";
    this._frame.style.position = "fixed";
    this._frame.style.left = "-1000px";
    this._frame.style.top = "0";
    this._frame.width = "0";
    this._frame.height = "0";
    this._frame.src = parsedUrl.href;
  }
  load() {
    return new Promise((resolve) => {
      this._frame.onload = () => {
        resolve();
      };
      window.document.body.appendChild(this._frame);
      window.addEventListener("message", this._message, false);
    });
  }
  start(session_state) {
    if (this._session_state === session_state) {
      return;
    }
    this._logger.create("start");
    this.stop();
    this._session_state = session_state;
    const send = () => {
      if (!this._frame.contentWindow || !this._session_state) {
        return;
      }
      this._frame.contentWindow.postMessage(this._client_id + " " + this._session_state, this._frame_origin);
    };
    send();
    this._timer = setInterval(send, this._intervalInSeconds * 1e3);
  }
  stop() {
    this._logger.create("stop");
    this._session_state = null;
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }
};

// src/InMemoryWebStorage.ts
var InMemoryWebStorage = class {
  constructor() {
    this._logger = new Logger("InMemoryWebStorage");
    this._data = {};
  }
  clear() {
    this._logger.create("clear");
    this._data = {};
  }
  getItem(key) {
    this._logger.create(`getItem('${key}')`);
    return this._data[key];
  }
  setItem(key, value) {
    this._logger.create(`setItem('${key}')`);
    this._data[key] = value;
  }
  removeItem(key) {
    this._logger.create(`removeItem('${key}')`);
    delete this._data[key];
  }
  get length() {
    return Object.getOwnPropertyNames(this._data).length;
  }
  key(index) {
    return Object.getOwnPropertyNames(this._data)[index];
  }
};

// src/JsonService.ts
var JsonService = class {
  constructor(additionalContentTypes = [], _jwtHandler = null, _extraHeaders = {}) {
    this._jwtHandler = _jwtHandler;
    this._extraHeaders = _extraHeaders;
    this._logger = new Logger("JsonService");
    this._contentTypes = [];
    this._contentTypes.push(...additionalContentTypes, "application/json");
    if (_jwtHandler) {
      this._contentTypes.push("application/jwt");
    }
  }
  async fetchWithTimeout(input, init = {}) {
    const { timeoutInSeconds, ...initFetch } = init;
    if (!timeoutInSeconds) {
      return await fetch(input, initFetch);
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutInSeconds * 1e3);
    try {
      const response = await fetch(input, {
        ...init,
        signal: controller.signal
      });
      return response;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new ErrorTimeout("Network timed out");
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }
  async getJson(url, {
    token,
    credentials
  } = {}) {
    const logger2 = this._logger.create("getJson");
    const headers = {
      "Accept": this._contentTypes.join(", ")
    };
    if (token) {
      logger2.debug("token passed, setting Authorization header");
      headers["Authorization"] = "Bearer " + token;
    }
    this.appendExtraHeaders(headers);
    let response;
    try {
      logger2.debug("url:", url);
      response = await this.fetchWithTimeout(url, { method: "GET", headers, credentials });
    } catch (err) {
      logger2.error("Network Error");
      throw err;
    }
    logger2.debug("HTTP response received, status", response.status);
    const contentType = response.headers.get("Content-Type");
    if (contentType && !this._contentTypes.find((item) => contentType.startsWith(item))) {
      logger2.throw(new Error(`Invalid response Content-Type: ${contentType != null ? contentType : "undefined"}, from URL: ${url}`));
    }
    if (response.ok && this._jwtHandler && (contentType == null ? void 0 : contentType.startsWith("application/jwt"))) {
      return await this._jwtHandler(await response.text());
    }
    let json;
    try {
      json = await response.json();
    } catch (err) {
      logger2.error("Error parsing JSON response", err);
      if (response.ok)
        throw err;
      throw new Error(`${response.statusText} (${response.status})`);
    }
    if (!response.ok) {
      logger2.error("Error from server:", json);
      if (json.error) {
        throw new ErrorResponse(json);
      }
      throw new Error(`${response.statusText} (${response.status}): ${JSON.stringify(json)}`);
    }
    return json;
  }
  async postForm(url, {
    body,
    basicAuth,
    timeoutInSeconds,
    initCredentials
  }) {
    const logger2 = this._logger.create("postForm");
    const headers = {
      "Accept": this._contentTypes.join(", "),
      "Content-Type": "application/x-www-form-urlencoded"
    };
    if (basicAuth !== void 0) {
      headers["Authorization"] = "Basic " + basicAuth;
    }
    this.appendExtraHeaders(headers);
    let response;
    try {
      logger2.debug("url:", url);
      response = await this.fetchWithTimeout(url, { method: "POST", headers, body, timeoutInSeconds, credentials: initCredentials });
    } catch (err) {
      logger2.error("Network error");
      throw err;
    }
    logger2.debug("HTTP response received, status", response.status);
    const contentType = response.headers.get("Content-Type");
    if (contentType && !this._contentTypes.find((item) => contentType.startsWith(item))) {
      throw new Error(`Invalid response Content-Type: ${contentType != null ? contentType : "undefined"}, from URL: ${url}`);
    }
    const responseText = await response.text();
    let json = {};
    if (responseText) {
      try {
        json = JSON.parse(responseText);
      } catch (err) {
        logger2.error("Error parsing JSON response", err);
        if (response.ok)
          throw err;
        throw new Error(`${response.statusText} (${response.status})`);
      }
    }
    if (!response.ok) {
      logger2.error("Error from server:", json);
      if (json.error) {
        throw new ErrorResponse(json, body);
      }
      throw new Error(`${response.statusText} (${response.status}): ${JSON.stringify(json)}`);
    }
    return json;
  }
  appendExtraHeaders(headers) {
    const logger2 = this._logger.create("appendExtraHeaders");
    const customKeys = Object.keys(this._extraHeaders);
    const protectedHeaders = [
      "authorization",
      "accept",
      "content-type"
    ];
    if (customKeys.length === 0) {
      return;
    }
    customKeys.forEach((headerName) => {
      if (protectedHeaders.includes(headerName.toLocaleLowerCase())) {
        logger2.warn("Protected header could not be overridden", headerName, protectedHeaders);
        return;
      }
      const content = typeof this._extraHeaders[headerName] === "function" ? this._extraHeaders[headerName]() : this._extraHeaders[headerName];
      if (content && content !== "") {
        headers[headerName] = content;
      }
    });
  }
};

// src/MetadataService.ts
var MetadataService = class {
  constructor(_settings) {
    this._settings = _settings;
    this._logger = new Logger("MetadataService");
    this._signingKeys = null;
    this._metadata = null;
    this._metadataUrl = this._settings.metadataUrl;
    this._jsonService = new JsonService(
      ["application/jwk-set+json"],
      null,
      this._settings.extraHeaders
    );
    if (this._settings.signingKeys) {
      this._logger.debug("using signingKeys from settings");
      this._signingKeys = this._settings.signingKeys;
    }
    if (this._settings.metadata) {
      this._logger.debug("using metadata from settings");
      this._metadata = this._settings.metadata;
    }
    if (this._settings.fetchRequestCredentials) {
      this._logger.debug("using fetchRequestCredentials from settings");
      this._fetchRequestCredentials = this._settings.fetchRequestCredentials;
    }
  }
  resetSigningKeys() {
    this._signingKeys = null;
  }
  async getMetadata() {
    const logger2 = this._logger.create("getMetadata");
    if (this._metadata) {
      logger2.debug("using cached values");
      return this._metadata;
    }
    if (!this._metadataUrl) {
      logger2.throw(new Error("No authority or metadataUrl configured on settings"));
      throw null;
    }
    logger2.debug("getting metadata from", this._metadataUrl);
    const metadata = await this._jsonService.getJson(this._metadataUrl, { credentials: this._fetchRequestCredentials });
    logger2.debug("merging remote JSON with seed metadata");
    this._metadata = Object.assign({}, this._settings.metadataSeed, metadata);
    return this._metadata;
  }
  getIssuer() {
    return this._getMetadataProperty("issuer");
  }
  getAuthorizationEndpoint() {
    return this._getMetadataProperty("authorization_endpoint");
  }
  getUserInfoEndpoint() {
    return this._getMetadataProperty("userinfo_endpoint");
  }
  getTokenEndpoint(optional = true) {
    return this._getMetadataProperty("token_endpoint", optional);
  }
  getCheckSessionIframe() {
    return this._getMetadataProperty("check_session_iframe", true);
  }
  getEndSessionEndpoint() {
    return this._getMetadataProperty("end_session_endpoint", true);
  }
  getRevocationEndpoint(optional = true) {
    return this._getMetadataProperty("revocation_endpoint", optional);
  }
  getKeysEndpoint(optional = true) {
    return this._getMetadataProperty("jwks_uri", optional);
  }
  async _getMetadataProperty(name, optional = false) {
    const logger2 = this._logger.create(`_getMetadataProperty('${name}')`);
    const metadata = await this.getMetadata();
    logger2.debug("resolved");
    if (metadata[name] === void 0) {
      if (optional === true) {
        logger2.warn("Metadata does not contain optional property");
        return void 0;
      }
      logger2.throw(new Error("Metadata does not contain property " + name));
    }
    return metadata[name];
  }
  async getSigningKeys() {
    const logger2 = this._logger.create("getSigningKeys");
    if (this._signingKeys) {
      logger2.debug("returning signingKeys from cache");
      return this._signingKeys;
    }
    const jwks_uri = await this.getKeysEndpoint(false);
    logger2.debug("got jwks_uri", jwks_uri);
    const keySet = await this._jsonService.getJson(jwks_uri);
    logger2.debug("got key set", keySet);
    if (!Array.isArray(keySet.keys)) {
      logger2.throw(new Error("Missing keys on keyset"));
      throw null;
    }
    this._signingKeys = keySet.keys;
    return this._signingKeys;
  }
};

// src/WebStorageStateStore.ts
var WebStorageStateStore = class {
  constructor({
    prefix = "oidc.",
    store = localStorage
  } = {}) {
    this._logger = new Logger("WebStorageStateStore");
    this._store = store;
    this._prefix = prefix;
  }
  async set(key, value) {
    this._logger.create(`set('${key}')`);
    key = this._prefix + key;
    await this._store.setItem(key, value);
  }
  async get(key) {
    this._logger.create(`get('${key}')`);
    key = this._prefix + key;
    const item = await this._store.getItem(key);
    return item;
  }
  async remove(key) {
    this._logger.create(`remove('${key}')`);
    key = this._prefix + key;
    const item = await this._store.getItem(key);
    await this._store.removeItem(key);
    return item;
  }
  async getAllKeys() {
    this._logger.create("getAllKeys");
    const len = await this._store.length;
    const keys = [];
    for (let index = 0; index < len; index++) {
      const key = await this._store.key(index);
      if (key && key.indexOf(this._prefix) === 0) {
        keys.push(key.substr(this._prefix.length));
      }
    }
    return keys;
  }
};

// src/OidcClientSettings.ts
var DefaultResponseType = "code";
var DefaultScope = "openid";
var DefaultClientAuthentication = "client_secret_post";
var DefaultStaleStateAgeInSeconds = 60 * 15;
var OidcClientSettingsStore = class {
  constructor({
    // metadata related
    authority,
    metadataUrl,
    metadata,
    signingKeys,
    metadataSeed,
    // client related
    client_id,
    client_secret,
    response_type = DefaultResponseType,
    scope = DefaultScope,
    redirect_uri,
    post_logout_redirect_uri,
    client_authentication = DefaultClientAuthentication,
    // optional protocol
    prompt,
    display,
    max_age,
    ui_locales,
    acr_values,
    resource,
    response_mode,
    // behavior flags
    filterProtocolClaims = true,
    loadUserInfo = false,
    staleStateAgeInSeconds = DefaultStaleStateAgeInSeconds,
    mergeClaimsStrategy = { array: "replace" },
    disablePKCE = false,
    // other behavior
    stateStore,
    revokeTokenAdditionalContentTypes,
    fetchRequestCredentials,
    refreshTokenAllowedScope,
    // extra
    extraQueryParams = {},
    extraTokenParams = {},
    extraHeaders = {}
  }) {
    this.authority = authority;
    if (metadataUrl) {
      this.metadataUrl = metadataUrl;
    } else {
      this.metadataUrl = authority;
      if (authority) {
        if (!this.metadataUrl.endsWith("/")) {
          this.metadataUrl += "/";
        }
        this.metadataUrl += ".well-known/openid-configuration";
      }
    }
    this.metadata = metadata;
    this.metadataSeed = metadataSeed;
    this.signingKeys = signingKeys;
    this.client_id = client_id;
    this.client_secret = client_secret;
    this.response_type = response_type;
    this.scope = scope;
    this.redirect_uri = redirect_uri;
    this.post_logout_redirect_uri = post_logout_redirect_uri;
    this.client_authentication = client_authentication;
    this.prompt = prompt;
    this.display = display;
    this.max_age = max_age;
    this.ui_locales = ui_locales;
    this.acr_values = acr_values;
    this.resource = resource;
    this.response_mode = response_mode;
    this.filterProtocolClaims = filterProtocolClaims != null ? filterProtocolClaims : true;
    this.loadUserInfo = !!loadUserInfo;
    this.staleStateAgeInSeconds = staleStateAgeInSeconds;
    this.mergeClaimsStrategy = mergeClaimsStrategy;
    this.disablePKCE = !!disablePKCE;
    this.revokeTokenAdditionalContentTypes = revokeTokenAdditionalContentTypes;
    this.fetchRequestCredentials = fetchRequestCredentials ? fetchRequestCredentials : "same-origin";
    if (stateStore) {
      this.stateStore = stateStore;
    } else {
      const store = typeof window !== "undefined" ? window.localStorage : new InMemoryWebStorage();
      this.stateStore = new WebStorageStateStore({ store });
    }
    this.refreshTokenAllowedScope = refreshTokenAllowedScope;
    this.extraQueryParams = extraQueryParams;
    this.extraTokenParams = extraTokenParams;
    this.extraHeaders = extraHeaders;
  }
};

// src/UserInfoService.ts
var UserInfoService = class {
  constructor(_settings, _metadataService) {
    this._settings = _settings;
    this._metadataService = _metadataService;
    this._logger = new Logger("UserInfoService");
    this._getClaimsFromJwt = async (responseText) => {
      const logger2 = this._logger.create("_getClaimsFromJwt");
      try {
        const payload = JwtUtils.decode(responseText);
        logger2.debug("JWT decoding successful");
        return payload;
      } catch (err) {
        logger2.error("Error parsing JWT response");
        throw err;
      }
    };
    this._jsonService = new JsonService(
      void 0,
      this._getClaimsFromJwt,
      this._settings.extraHeaders
    );
  }
  async getClaims(token) {
    const logger2 = this._logger.create("getClaims");
    if (!token) {
      this._logger.throw(new Error("No token passed"));
    }
    const url = await this._metadataService.getUserInfoEndpoint();
    logger2.debug("got userinfo url", url);
    const claims = await this._jsonService.getJson(url, {
      token,
      credentials: this._settings.fetchRequestCredentials
    });
    logger2.debug("got claims", claims);
    return claims;
  }
};

// src/TokenClient.ts
var TokenClient = class {
  constructor(_settings, _metadataService) {
    this._settings = _settings;
    this._metadataService = _metadataService;
    this._logger = new Logger("TokenClient");
    this._jsonService = new JsonService(
      this._settings.revokeTokenAdditionalContentTypes,
      null,
      this._settings.extraHeaders
    );
  }
  /**
   * Exchange code.
   *
   * @see https://www.rfc-editor.org/rfc/rfc6749#section-4.1.3
   */
  async exchangeCode({
    grant_type = "authorization_code",
    redirect_uri = this._settings.redirect_uri,
    client_id = this._settings.client_id,
    client_secret = this._settings.client_secret,
    ...args
  }) {
    const logger2 = this._logger.create("exchangeCode");
    if (!client_id) {
      logger2.throw(new Error("A client_id is required"));
    }
    if (!redirect_uri) {
      logger2.throw(new Error("A redirect_uri is required"));
    }
    if (!args.code) {
      logger2.throw(new Error("A code is required"));
    }
    const params = new URLSearchParams({ grant_type, redirect_uri });
    for (const [key, value] of Object.entries(args)) {
      if (value != null) {
        params.set(key, value);
      }
    }
    let basicAuth;
    switch (this._settings.client_authentication) {
      case "client_secret_basic":
        if (!client_secret) {
          logger2.throw(new Error("A client_secret is required"));
          throw null;
        }
        basicAuth = CryptoUtils.generateBasicAuth(client_id, client_secret);
        break;
      case "client_secret_post":
        params.append("client_id", client_id);
        if (client_secret) {
          params.append("client_secret", client_secret);
        }
        break;
    }
    const url = await this._metadataService.getTokenEndpoint(false);
    logger2.debug("got token endpoint");
    const response = await this._jsonService.postForm(url, { body: params, basicAuth, initCredentials: this._settings.fetchRequestCredentials });
    logger2.debug("got response");
    return response;
  }
  /**
   * Exchange credentials.
   *
   * @see https://www.rfc-editor.org/rfc/rfc6749#section-4.3.2
   */
  async exchangeCredentials({
    grant_type = "password",
    client_id = this._settings.client_id,
    client_secret = this._settings.client_secret,
    scope = this._settings.scope,
    ...args
  }) {
    const logger2 = this._logger.create("exchangeCredentials");
    if (!client_id) {
      logger2.throw(new Error("A client_id is required"));
    }
    const params = new URLSearchParams({ grant_type, scope });
    for (const [key, value] of Object.entries(args)) {
      if (value != null) {
        params.set(key, value);
      }
    }
    let basicAuth;
    switch (this._settings.client_authentication) {
      case "client_secret_basic":
        if (!client_secret) {
          logger2.throw(new Error("A client_secret is required"));
          throw null;
        }
        basicAuth = CryptoUtils.generateBasicAuth(client_id, client_secret);
        break;
      case "client_secret_post":
        params.append("client_id", client_id);
        if (client_secret) {
          params.append("client_secret", client_secret);
        }
        break;
    }
    const url = await this._metadataService.getTokenEndpoint(false);
    logger2.debug("got token endpoint");
    const response = await this._jsonService.postForm(url, { body: params, basicAuth, initCredentials: this._settings.fetchRequestCredentials });
    logger2.debug("got response");
    return response;
  }
  /**
   * Exchange a refresh token.
   *
   * @see https://www.rfc-editor.org/rfc/rfc6749#section-6
   */
  async exchangeRefreshToken({
    grant_type = "refresh_token",
    client_id = this._settings.client_id,
    client_secret = this._settings.client_secret,
    timeoutInSeconds,
    ...args
  }) {
    const logger2 = this._logger.create("exchangeRefreshToken");
    if (!client_id) {
      logger2.throw(new Error("A client_id is required"));
    }
    if (!args.refresh_token) {
      logger2.throw(new Error("A refresh_token is required"));
    }
    const params = new URLSearchParams({ grant_type });
    for (const [key, value] of Object.entries(args)) {
      if (Array.isArray(value)) {
        value.forEach((param) => params.append(key, param));
      } else if (value != null) {
        params.set(key, value);
      }
    }
    let basicAuth;
    switch (this._settings.client_authentication) {
      case "client_secret_basic":
        if (!client_secret) {
          logger2.throw(new Error("A client_secret is required"));
          throw null;
        }
        basicAuth = CryptoUtils.generateBasicAuth(client_id, client_secret);
        break;
      case "client_secret_post":
        params.append("client_id", client_id);
        if (client_secret) {
          params.append("client_secret", client_secret);
        }
        break;
    }
    const url = await this._metadataService.getTokenEndpoint(false);
    logger2.debug("got token endpoint");
    const response = await this._jsonService.postForm(url, { body: params, basicAuth, timeoutInSeconds, initCredentials: this._settings.fetchRequestCredentials });
    logger2.debug("got response");
    return response;
  }
  /**
   * Revoke an access or refresh token.
   *
   * @see https://datatracker.ietf.org/doc/html/rfc7009#section-2.1
   */
  async revoke(args) {
    var _a;
    const logger2 = this._logger.create("revoke");
    if (!args.token) {
      logger2.throw(new Error("A token is required"));
    }
    const url = await this._metadataService.getRevocationEndpoint(false);
    logger2.debug(`got revocation endpoint, revoking ${(_a = args.token_type_hint) != null ? _a : "default token type"}`);
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(args)) {
      if (value != null) {
        params.set(key, value);
      }
    }
    params.set("client_id", this._settings.client_id);
    if (this._settings.client_secret) {
      params.set("client_secret", this._settings.client_secret);
    }
    await this._jsonService.postForm(url, { body: params });
    logger2.debug("got response");
  }
};

// src/ResponseValidator.ts
var ResponseValidator = class {
  constructor(_settings, _metadataService, _claimsService) {
    this._settings = _settings;
    this._metadataService = _metadataService;
    this._claimsService = _claimsService;
    this._logger = new Logger("ResponseValidator");
    this._userInfoService = new UserInfoService(this._settings, this._metadataService);
    this._tokenClient = new TokenClient(this._settings, this._metadataService);
  }
  async validateSigninResponse(response, state) {
    const logger2 = this._logger.create("validateSigninResponse");
    this._processSigninState(response, state);
    logger2.debug("state processed");
    await this._processCode(response, state);
    logger2.debug("code processed");
    if (response.isOpenId) {
      this._validateIdTokenAttributes(response);
    }
    logger2.debug("tokens validated");
    await this._processClaims(response, state == null ? void 0 : state.skipUserInfo, response.isOpenId);
    logger2.debug("claims processed");
  }
  async validateCredentialsResponse(response, skipUserInfo) {
    const logger2 = this._logger.create("validateCredentialsResponse");
    if (response.isOpenId && !!response.id_token) {
      this._validateIdTokenAttributes(response);
    }
    logger2.debug("tokens validated");
    await this._processClaims(response, skipUserInfo, response.isOpenId);
    logger2.debug("claims processed");
  }
  async validateRefreshResponse(response, state) {
    var _a, _b;
    const logger2 = this._logger.create("validateRefreshResponse");
    response.userState = state.data;
    (_a = response.session_state) != null ? _a : response.session_state = state.session_state;
    (_b = response.scope) != null ? _b : response.scope = state.scope;
    if (response.isOpenId && !!response.id_token) {
      this._validateIdTokenAttributes(response, state.id_token);
      logger2.debug("ID Token validated");
    }
    if (!response.id_token) {
      response.id_token = state.id_token;
      response.profile = state.profile;
    }
    const hasIdToken = response.isOpenId && !!response.id_token;
    await this._processClaims(response, false, hasIdToken);
    logger2.debug("claims processed");
  }
  validateSignoutResponse(response, state) {
    const logger2 = this._logger.create("validateSignoutResponse");
    if (state.id !== response.state) {
      logger2.throw(new Error("State does not match"));
    }
    logger2.debug("state validated");
    response.userState = state.data;
    if (response.error) {
      logger2.warn("Response was error", response.error);
      throw new ErrorResponse(response);
    }
  }
  _processSigninState(response, state) {
    var _a;
    const logger2 = this._logger.create("_processSigninState");
    if (state.id !== response.state) {
      logger2.throw(new Error("State does not match"));
    }
    if (!state.client_id) {
      logger2.throw(new Error("No client_id on state"));
    }
    if (!state.authority) {
      logger2.throw(new Error("No authority on state"));
    }
    if (this._settings.authority !== state.authority) {
      logger2.throw(new Error("authority mismatch on settings vs. signin state"));
    }
    if (this._settings.client_id && this._settings.client_id !== state.client_id) {
      logger2.throw(new Error("client_id mismatch on settings vs. signin state"));
    }
    logger2.debug("state validated");
    response.userState = state.data;
    response.url_state = state.url_state;
    (_a = response.scope) != null ? _a : response.scope = state.scope;
    if (response.error) {
      logger2.warn("Response was error", response.error);
      throw new ErrorResponse(response);
    }
    if (state.code_verifier && !response.code) {
      logger2.throw(new Error("Expected code in response"));
    }
  }
  async _processClaims(response, skipUserInfo = false, validateSub = true) {
    const logger2 = this._logger.create("_processClaims");
    response.profile = this._claimsService.filterProtocolClaims(response.profile);
    if (skipUserInfo || !this._settings.loadUserInfo || !response.access_token) {
      logger2.debug("not loading user info");
      return;
    }
    logger2.debug("loading user info");
    const claims = await this._userInfoService.getClaims(response.access_token);
    logger2.debug("user info claims received from user info endpoint");
    if (validateSub && claims.sub !== response.profile.sub) {
      logger2.throw(new Error("subject from UserInfo response does not match subject in ID Token"));
    }
    response.profile = this._claimsService.mergeClaims(response.profile, this._claimsService.filterProtocolClaims(claims));
    logger2.debug("user info claims received, updated profile:", response.profile);
  }
  async _processCode(response, state) {
    const logger2 = this._logger.create("_processCode");
    if (response.code) {
      logger2.debug("Validating code");
      const tokenResponse = await this._tokenClient.exchangeCode({
        client_id: state.client_id,
        client_secret: state.client_secret,
        code: response.code,
        redirect_uri: state.redirect_uri,
        code_verifier: state.code_verifier,
        ...state.extraTokenParams
      });
      Object.assign(response, tokenResponse);
    } else {
      logger2.debug("No code to process");
    }
  }
  _validateIdTokenAttributes(response, existingToken) {
    var _a;
    const logger2 = this._logger.create("_validateIdTokenAttributes");
    logger2.debug("decoding ID Token JWT");
    const incoming = JwtUtils.decode((_a = response.id_token) != null ? _a : "");
    if (!incoming.sub) {
      logger2.throw(new Error("ID Token is missing a subject claim"));
    }
    if (existingToken) {
      const existing = JwtUtils.decode(existingToken);
      if (incoming.sub !== existing.sub) {
        logger2.throw(new Error("sub in id_token does not match current sub"));
      }
      if (incoming.auth_time && incoming.auth_time !== existing.auth_time) {
        logger2.throw(new Error("auth_time in id_token does not match original auth_time"));
      }
      if (incoming.azp && incoming.azp !== existing.azp) {
        logger2.throw(new Error("azp in id_token does not match original azp"));
      }
      if (!incoming.azp && existing.azp) {
        logger2.throw(new Error("azp not in id_token, but present in original id_token"));
      }
    }
    response.profile = incoming;
  }
};

// src/State.ts
var State = class _State {
  constructor(args) {
    this.id = args.id || CryptoUtils.generateUUIDv4();
    this.data = args.data;
    if (args.created && args.created > 0) {
      this.created = args.created;
    } else {
      this.created = Timer.getEpochTime();
    }
    this.request_type = args.request_type;
    this.url_state = args.url_state;
  }
  toStorageString() {
    new Logger("State").create("toStorageString");
    return JSON.stringify({
      id: this.id,
      data: this.data,
      created: this.created,
      request_type: this.request_type,
      url_state: this.url_state
    });
  }
  static fromStorageString(storageString) {
    Logger.createStatic("State", "fromStorageString");
    return Promise.resolve(new _State(JSON.parse(storageString)));
  }
  static async clearStaleState(storage, age) {
    const logger2 = Logger.createStatic("State", "clearStaleState");
    const cutoff = Timer.getEpochTime() - age;
    const keys = await storage.getAllKeys();
    logger2.debug("got keys", keys);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const item = await storage.get(key);
      let remove = false;
      if (item) {
        try {
          const state = await _State.fromStorageString(item);
          logger2.debug("got item from key:", key, state.created);
          if (state.created <= cutoff) {
            remove = true;
          }
        } catch (err) {
          logger2.error("Error parsing state for key:", key, err);
          remove = true;
        }
      } else {
        logger2.debug("no item in storage for key:", key);
        remove = true;
      }
      if (remove) {
        logger2.debug("removed item for key:", key);
        void storage.remove(key);
      }
    }
  }
};

// src/SigninState.ts
var SigninState = class _SigninState extends State {
  constructor(args) {
    super(args);
    this.code_verifier = args.code_verifier;
    this.code_challenge = args.code_challenge;
    this.authority = args.authority;
    this.client_id = args.client_id;
    this.redirect_uri = args.redirect_uri;
    this.scope = args.scope;
    this.client_secret = args.client_secret;
    this.extraTokenParams = args.extraTokenParams;
    this.response_mode = args.response_mode;
    this.skipUserInfo = args.skipUserInfo;
  }
  static async create(args) {
    const code_verifier = args.code_verifier === true ? CryptoUtils.generateCodeVerifier() : args.code_verifier || void 0;
    const code_challenge = code_verifier ? await CryptoUtils.generateCodeChallenge(code_verifier) : void 0;
    return new _SigninState({
      ...args,
      code_verifier,
      code_challenge
    });
  }
  toStorageString() {
    new Logger("SigninState").create("toStorageString");
    return JSON.stringify({
      id: this.id,
      data: this.data,
      created: this.created,
      request_type: this.request_type,
      url_state: this.url_state,
      code_verifier: this.code_verifier,
      authority: this.authority,
      client_id: this.client_id,
      redirect_uri: this.redirect_uri,
      scope: this.scope,
      client_secret: this.client_secret,
      extraTokenParams: this.extraTokenParams,
      response_mode: this.response_mode,
      skipUserInfo: this.skipUserInfo
    });
  }
  static fromStorageString(storageString) {
    Logger.createStatic("SigninState", "fromStorageString");
    const data = JSON.parse(storageString);
    return _SigninState.create(data);
  }
};

// src/SigninRequest.ts
var _SigninRequest = class _SigninRequest {
  constructor(args) {
    this.url = args.url;
    this.state = args.state;
  }
  static async create({
    // mandatory
    url,
    authority,
    client_id,
    redirect_uri,
    response_type,
    scope,
    // optional
    state_data,
    response_mode,
    request_type,
    client_secret,
    nonce,
    url_state,
    resource,
    skipUserInfo,
    extraQueryParams,
    extraTokenParams,
    disablePKCE,
    ...optionalParams
  }) {
    if (!url) {
      this._logger.error("create: No url passed");
      throw new Error("url");
    }
    if (!client_id) {
      this._logger.error("create: No client_id passed");
      throw new Error("client_id");
    }
    if (!redirect_uri) {
      this._logger.error("create: No redirect_uri passed");
      throw new Error("redirect_uri");
    }
    if (!response_type) {
      this._logger.error("create: No response_type passed");
      throw new Error("response_type");
    }
    if (!scope) {
      this._logger.error("create: No scope passed");
      throw new Error("scope");
    }
    if (!authority) {
      this._logger.error("create: No authority passed");
      throw new Error("authority");
    }
    const state = await SigninState.create({
      data: state_data,
      request_type,
      url_state,
      code_verifier: !disablePKCE,
      client_id,
      authority,
      redirect_uri,
      response_mode,
      client_secret,
      scope,
      extraTokenParams,
      skipUserInfo
    });
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.append("client_id", client_id);
    parsedUrl.searchParams.append("redirect_uri", redirect_uri);
    parsedUrl.searchParams.append("response_type", response_type);
    parsedUrl.searchParams.append("scope", scope);
    if (nonce) {
      parsedUrl.searchParams.append("nonce", nonce);
    }
    let stateParam = state.id;
    if (url_state) {
      stateParam = `${stateParam}${URL_STATE_DELIMITER}${url_state}`;
    }
    parsedUrl.searchParams.append("state", stateParam);
    if (state.code_challenge) {
      parsedUrl.searchParams.append("code_challenge", state.code_challenge);
      parsedUrl.searchParams.append("code_challenge_method", "S256");
    }
    if (resource) {
      const resources = Array.isArray(resource) ? resource : [resource];
      resources.forEach((r) => parsedUrl.searchParams.append("resource", r));
    }
    for (const [key, value] of Object.entries({ response_mode, ...optionalParams, ...extraQueryParams })) {
      if (value != null) {
        parsedUrl.searchParams.append(key, value.toString());
      }
    }
    return new _SigninRequest({
      url: parsedUrl.href,
      state
    });
  }
};
_SigninRequest._logger = new Logger("SigninRequest");
var SigninRequest = _SigninRequest;

// src/SigninResponse.ts
var OidcScope = "openid";
var SigninResponse = class {
  constructor(params) {
    /** @see {@link User.access_token} */
    this.access_token = "";
    /** @see {@link User.token_type} */
    this.token_type = "";
    /** @see {@link User.profile} */
    this.profile = {};
    this.state = params.get("state");
    this.session_state = params.get("session_state");
    if (this.state) {
      const splitState = decodeURIComponent(this.state).split(URL_STATE_DELIMITER);
      this.state = splitState[0];
      if (splitState.length > 1) {
        this.url_state = splitState.slice(1).join(URL_STATE_DELIMITER);
      }
    }
    this.error = params.get("error");
    this.error_description = params.get("error_description");
    this.error_uri = params.get("error_uri");
    this.code = params.get("code");
  }
  get expires_in() {
    if (this.expires_at === void 0) {
      return void 0;
    }
    return this.expires_at - Timer.getEpochTime();
  }
  set expires_in(value) {
    if (typeof value === "string")
      value = Number(value);
    if (value !== void 0 && value >= 0) {
      this.expires_at = Math.floor(value) + Timer.getEpochTime();
    }
  }
  get isOpenId() {
    var _a;
    return ((_a = this.scope) == null ? void 0 : _a.split(" ").includes(OidcScope)) || !!this.id_token;
  }
};

// src/SignoutRequest.ts
var SignoutRequest = class {
  constructor({
    url,
    state_data,
    id_token_hint,
    post_logout_redirect_uri,
    extraQueryParams,
    request_type,
    client_id
  }) {
    this._logger = new Logger("SignoutRequest");
    if (!url) {
      this._logger.error("ctor: No url passed");
      throw new Error("url");
    }
    const parsedUrl = new URL(url);
    if (id_token_hint) {
      parsedUrl.searchParams.append("id_token_hint", id_token_hint);
    }
    if (client_id) {
      parsedUrl.searchParams.append("client_id", client_id);
    }
    if (post_logout_redirect_uri) {
      parsedUrl.searchParams.append("post_logout_redirect_uri", post_logout_redirect_uri);
      if (state_data) {
        this.state = new State({ data: state_data, request_type });
        parsedUrl.searchParams.append("state", this.state.id);
      }
    }
    for (const [key, value] of Object.entries({ ...extraQueryParams })) {
      if (value != null) {
        parsedUrl.searchParams.append(key, value.toString());
      }
    }
    this.url = parsedUrl.href;
  }
};

// src/SignoutResponse.ts
var SignoutResponse = class {
  constructor(params) {
    this.state = params.get("state");
    this.error = params.get("error");
    this.error_description = params.get("error_description");
    this.error_uri = params.get("error_uri");
  }
};

// src/ClaimsService.ts
var DefaultProtocolClaims = [
  "nbf",
  "jti",
  "auth_time",
  "nonce",
  "acr",
  "amr",
  "azp",
  "at_hash"
  // https://openid.net/specs/openid-connect-core-1_0.html#CodeIDToken
];
var InternalRequiredProtocolClaims = ["sub", "iss", "aud", "exp", "iat"];
var ClaimsService = class {
  constructor(_settings) {
    this._settings = _settings;
    this._logger = new Logger("ClaimsService");
  }
  filterProtocolClaims(claims) {
    const result = { ...claims };
    if (this._settings.filterProtocolClaims) {
      let protocolClaims;
      if (Array.isArray(this._settings.filterProtocolClaims)) {
        protocolClaims = this._settings.filterProtocolClaims;
      } else {
        protocolClaims = DefaultProtocolClaims;
      }
      for (const claim of protocolClaims) {
        if (!InternalRequiredProtocolClaims.includes(claim)) {
          delete result[claim];
        }
      }
    }
    return result;
  }
  mergeClaims(claims1, claims2) {
    const result = { ...claims1 };
    for (const [claim, values] of Object.entries(claims2)) {
      if (result[claim] !== values) {
        if (Array.isArray(result[claim]) || Array.isArray(values)) {
          if (this._settings.mergeClaimsStrategy.array == "replace") {
            result[claim] = values;
          } else {
            const mergedValues = Array.isArray(result[claim]) ? result[claim] : [result[claim]];
            for (const value of Array.isArray(values) ? values : [values]) {
              if (!mergedValues.includes(value)) {
                mergedValues.push(value);
              }
            }
            result[claim] = mergedValues;
          }
        } else if (typeof result[claim] === "object" && typeof values === "object") {
          result[claim] = this.mergeClaims(result[claim], values);
        } else {
          result[claim] = values;
        }
      }
    }
    return result;
  }
};

// src/OidcClient.ts
var OidcClient = class {
  constructor(settings, metadataService) {
    this._logger = new Logger("OidcClient");
    this.settings = settings instanceof OidcClientSettingsStore ? settings : new OidcClientSettingsStore(settings);
    this.metadataService = metadataService != null ? metadataService : new MetadataService(this.settings);
    this._claimsService = new ClaimsService(this.settings);
    this._validator = new ResponseValidator(this.settings, this.metadataService, this._claimsService);
    this._tokenClient = new TokenClient(this.settings, this.metadataService);
  }
  async createSigninRequest({
    state,
    request,
    request_uri,
    request_type,
    id_token_hint,
    login_hint,
    skipUserInfo,
    nonce,
    url_state,
    response_type = this.settings.response_type,
    scope = this.settings.scope,
    redirect_uri = this.settings.redirect_uri,
    prompt = this.settings.prompt,
    display = this.settings.display,
    max_age = this.settings.max_age,
    ui_locales = this.settings.ui_locales,
    acr_values = this.settings.acr_values,
    resource = this.settings.resource,
    response_mode = this.settings.response_mode,
    extraQueryParams = this.settings.extraQueryParams,
    extraTokenParams = this.settings.extraTokenParams
  }) {
    const logger2 = this._logger.create("createSigninRequest");
    if (response_type !== "code") {
      throw new Error("Only the Authorization Code flow (with PKCE) is supported");
    }
    const url = await this.metadataService.getAuthorizationEndpoint();
    logger2.debug("Received authorization endpoint", url);
    const signinRequest = await SigninRequest.create({
      url,
      authority: this.settings.authority,
      client_id: this.settings.client_id,
      redirect_uri,
      response_type,
      scope,
      state_data: state,
      url_state,
      prompt,
      display,
      max_age,
      ui_locales,
      id_token_hint,
      login_hint,
      acr_values,
      resource,
      request,
      request_uri,
      extraQueryParams,
      extraTokenParams,
      request_type,
      response_mode,
      client_secret: this.settings.client_secret,
      skipUserInfo,
      nonce,
      disablePKCE: this.settings.disablePKCE
    });
    await this.clearStaleState();
    const signinState = signinRequest.state;
    await this.settings.stateStore.set(signinState.id, signinState.toStorageString());
    return signinRequest;
  }
  async readSigninResponseState(url, removeState = false) {
    const logger2 = this._logger.create("readSigninResponseState");
    const response = new SigninResponse(UrlUtils.readParams(url, this.settings.response_mode));
    if (!response.state) {
      logger2.throw(new Error("No state in response"));
      throw null;
    }
    const storedStateString = await this.settings.stateStore[removeState ? "remove" : "get"](response.state);
    if (!storedStateString) {
      logger2.throw(new Error("No matching state found in storage"));
      throw null;
    }
    const state = await SigninState.fromStorageString(storedStateString);
    return { state, response };
  }
  async processSigninResponse(url) {
    const logger2 = this._logger.create("processSigninResponse");
    const { state, response } = await this.readSigninResponseState(url, true);
    logger2.debug("received state from storage; validating response");
    await this._validator.validateSigninResponse(response, state);
    return response;
  }
  async processResourceOwnerPasswordCredentials({
    username,
    password,
    skipUserInfo = false,
    extraTokenParams = {}
  }) {
    const tokenResponse = await this._tokenClient.exchangeCredentials({ username, password, ...extraTokenParams });
    const signinResponse = new SigninResponse(new URLSearchParams());
    Object.assign(signinResponse, tokenResponse);
    await this._validator.validateCredentialsResponse(signinResponse, skipUserInfo);
    return signinResponse;
  }
  async useRefreshToken({
    state,
    redirect_uri,
    resource,
    timeoutInSeconds,
    extraTokenParams
  }) {
    var _a;
    const logger2 = this._logger.create("useRefreshToken");
    let scope;
    if (this.settings.refreshTokenAllowedScope === void 0) {
      scope = state.scope;
    } else {
      const allowableScopes = this.settings.refreshTokenAllowedScope.split(" ");
      const providedScopes = ((_a = state.scope) == null ? void 0 : _a.split(" ")) || [];
      scope = providedScopes.filter((s) => allowableScopes.includes(s)).join(" ");
    }
    const result = await this._tokenClient.exchangeRefreshToken({
      refresh_token: state.refresh_token,
      // provide the (possible filtered) scope list
      scope,
      redirect_uri,
      resource,
      timeoutInSeconds,
      ...extraTokenParams
    });
    const response = new SigninResponse(new URLSearchParams());
    Object.assign(response, result);
    logger2.debug("validating response", response);
    await this._validator.validateRefreshResponse(response, {
      ...state,
      // override the scope in the state handed over to the validator
      // so it can set the granted scope to the requested scope in case none is included in the response
      scope
    });
    return response;
  }
  async createSignoutRequest({
    state,
    id_token_hint,
    client_id,
    request_type,
    post_logout_redirect_uri = this.settings.post_logout_redirect_uri,
    extraQueryParams = this.settings.extraQueryParams
  } = {}) {
    const logger2 = this._logger.create("createSignoutRequest");
    const url = await this.metadataService.getEndSessionEndpoint();
    if (!url) {
      logger2.throw(new Error("No end session endpoint"));
      throw null;
    }
    logger2.debug("Received end session endpoint", url);
    if (!client_id && post_logout_redirect_uri && !id_token_hint) {
      client_id = this.settings.client_id;
    }
    const request = new SignoutRequest({
      url,
      id_token_hint,
      client_id,
      post_logout_redirect_uri,
      state_data: state,
      extraQueryParams,
      request_type
    });
    await this.clearStaleState();
    const signoutState = request.state;
    if (signoutState) {
      logger2.debug("Signout request has state to persist");
      await this.settings.stateStore.set(signoutState.id, signoutState.toStorageString());
    }
    return request;
  }
  async readSignoutResponseState(url, removeState = false) {
    const logger2 = this._logger.create("readSignoutResponseState");
    const response = new SignoutResponse(UrlUtils.readParams(url, this.settings.response_mode));
    if (!response.state) {
      logger2.debug("No state in response");
      if (response.error) {
        logger2.warn("Response was error:", response.error);
        throw new ErrorResponse(response);
      }
      return { state: void 0, response };
    }
    const storedStateString = await this.settings.stateStore[removeState ? "remove" : "get"](response.state);
    if (!storedStateString) {
      logger2.throw(new Error("No matching state found in storage"));
      throw null;
    }
    const state = await State.fromStorageString(storedStateString);
    return { state, response };
  }
  async processSignoutResponse(url) {
    const logger2 = this._logger.create("processSignoutResponse");
    const { state, response } = await this.readSignoutResponseState(url, true);
    if (state) {
      logger2.debug("Received state from storage; validating response");
      this._validator.validateSignoutResponse(response, state);
    } else {
      logger2.debug("No state from storage; skipping response validation");
    }
    return response;
  }
  clearStaleState() {
    this._logger.create("clearStaleState");
    return State.clearStaleState(this.settings.stateStore, this.settings.staleStateAgeInSeconds);
  }
  async revokeToken(token, type) {
    this._logger.create("revokeToken");
    return await this._tokenClient.revoke({
      token,
      token_type_hint: type
    });
  }
};

// src/SessionMonitor.ts
var SessionMonitor = class {
  constructor(_userManager) {
    this._userManager = _userManager;
    this._logger = new Logger("SessionMonitor");
    this._start = async (user) => {
      const session_state = user.session_state;
      if (!session_state) {
        return;
      }
      const logger2 = this._logger.create("_start");
      if (user.profile) {
        this._sub = user.profile.sub;
        logger2.debug("session_state", session_state, ", sub", this._sub);
      } else {
        this._sub = void 0;
        logger2.debug("session_state", session_state, ", anonymous user");
      }
      if (this._checkSessionIFrame) {
        this._checkSessionIFrame.start(session_state);
        return;
      }
      try {
        const url = await this._userManager.metadataService.getCheckSessionIframe();
        if (url) {
          logger2.debug("initializing check session iframe");
          const client_id = this._userManager.settings.client_id;
          const intervalInSeconds = this._userManager.settings.checkSessionIntervalInSeconds;
          const stopOnError = this._userManager.settings.stopCheckSessionOnError;
          const checkSessionIFrame = new CheckSessionIFrame(this._callback, client_id, url, intervalInSeconds, stopOnError);
          await checkSessionIFrame.load();
          this._checkSessionIFrame = checkSessionIFrame;
          checkSessionIFrame.start(session_state);
        } else {
          logger2.warn("no check session iframe found in the metadata");
        }
      } catch (err) {
        logger2.error("Error from getCheckSessionIframe:", err instanceof Error ? err.message : err);
      }
    };
    this._stop = () => {
      const logger2 = this._logger.create("_stop");
      this._sub = void 0;
      if (this._checkSessionIFrame) {
        this._checkSessionIFrame.stop();
      }
      if (this._userManager.settings.monitorAnonymousSession) {
        const timerHandle = setInterval(async () => {
          clearInterval(timerHandle);
          try {
            const session = await this._userManager.querySessionStatus();
            if (session) {
              const tmpUser = {
                session_state: session.session_state,
                profile: session.sub ? {
                  sub: session.sub
                } : null
              };
              void this._start(tmpUser);
            }
          } catch (err) {
            logger2.error("error from querySessionStatus", err instanceof Error ? err.message : err);
          }
        }, 1e3);
      }
    };
    this._callback = async () => {
      const logger2 = this._logger.create("_callback");
      try {
        const session = await this._userManager.querySessionStatus();
        let raiseEvent = true;
        if (session && this._checkSessionIFrame) {
          if (session.sub === this._sub) {
            raiseEvent = false;
            this._checkSessionIFrame.start(session.session_state);
            logger2.debug("same sub still logged in at OP, session state has changed, restarting check session iframe; session_state", session.session_state);
            await this._userManager.events._raiseUserSessionChanged();
          } else {
            logger2.debug("different subject signed into OP", session.sub);
          }
        } else {
          logger2.debug("subject no longer signed into OP");
        }
        if (raiseEvent) {
          if (this._sub) {
            await this._userManager.events._raiseUserSignedOut();
          } else {
            await this._userManager.events._raiseUserSignedIn();
          }
        } else {
          logger2.debug("no change in session detected, no event to raise");
        }
      } catch (err) {
        if (this._sub) {
          logger2.debug("Error calling queryCurrentSigninSession; raising signed out event", err);
          await this._userManager.events._raiseUserSignedOut();
        }
      }
    };
    if (!_userManager) {
      this._logger.throw(new Error("No user manager passed"));
    }
    this._userManager.events.addUserLoaded(this._start);
    this._userManager.events.addUserUnloaded(this._stop);
    this._init().catch((err) => {
      this._logger.error(err);
    });
  }
  async _init() {
    this._logger.create("_init");
    const user = await this._userManager.getUser();
    if (user) {
      void this._start(user);
    } else if (this._userManager.settings.monitorAnonymousSession) {
      const session = await this._userManager.querySessionStatus();
      if (session) {
        const tmpUser = {
          session_state: session.session_state,
          profile: session.sub ? {
            sub: session.sub
          } : null
        };
        void this._start(tmpUser);
      }
    }
  }
};

// src/User.ts
var User = class _User {
  constructor(args) {
    var _a;
    this.id_token = args.id_token;
    this.session_state = (_a = args.session_state) != null ? _a : null;
    this.access_token = args.access_token;
    this.refresh_token = args.refresh_token;
    this.token_type = args.token_type;
    this.scope = args.scope;
    this.profile = args.profile;
    this.expires_at = args.expires_at;
    this.state = args.userState;
    this.url_state = args.url_state;
  }
  /** Computed number of seconds the access token has remaining. */
  get expires_in() {
    if (this.expires_at === void 0) {
      return void 0;
    }
    return this.expires_at - Timer.getEpochTime();
  }
  set expires_in(value) {
    if (value !== void 0) {
      this.expires_at = Math.floor(value) + Timer.getEpochTime();
    }
  }
  /** Computed value indicating if the access token is expired. */
  get expired() {
    const expires_in = this.expires_in;
    if (expires_in === void 0) {
      return void 0;
    }
    return expires_in <= 0;
  }
  /** Array representing the parsed values from the `scope`. */
  get scopes() {
    var _a, _b;
    return (_b = (_a = this.scope) == null ? void 0 : _a.split(" ")) != null ? _b : [];
  }
  toStorageString() {
    new Logger("User").create("toStorageString");
    return JSON.stringify({
      id_token: this.id_token,
      session_state: this.session_state,
      access_token: this.access_token,
      refresh_token: this.refresh_token,
      token_type: this.token_type,
      scope: this.scope,
      profile: this.profile,
      expires_at: this.expires_at
    });
  }
  static fromStorageString(storageString) {
    Logger.createStatic("User", "fromStorageString");
    return new _User(JSON.parse(storageString));
  }
};

// src/navigators/AbstractChildWindow.ts
var messageSource = "oidc-client";
var AbstractChildWindow = class {
  constructor() {
    this._abort = new Event("Window navigation aborted");
    this._disposeHandlers = /* @__PURE__ */ new Set();
    this._window = null;
  }
  async navigate(params) {
    const logger2 = this._logger.create("navigate");
    if (!this._window) {
      throw new Error("Attempted to navigate on a disposed window");
    }
    logger2.debug("setting URL in window");
    this._window.location.replace(params.url);
    const { url, keepOpen } = await new Promise((resolve, reject) => {
      const listener = (e) => {
        var _a;
        const data = e.data;
        const origin = (_a = params.scriptOrigin) != null ? _a : window.location.origin;
        if (e.origin !== origin || (data == null ? void 0 : data.source) !== messageSource) {
          return;
        }
        try {
          const state = UrlUtils.readParams(data.url, params.response_mode).get("state");
          if (!state) {
            logger2.warn("no state found in response url");
          }
          if (e.source !== this._window && state !== params.state) {
            return;
          }
        } catch (err) {
          this._dispose();
          reject(new Error("Invalid response from window"));
        }
        resolve(data);
      };
      window.addEventListener("message", listener, false);
      this._disposeHandlers.add(() => window.removeEventListener("message", listener, false));
      this._disposeHandlers.add(this._abort.addHandler((reason) => {
        this._dispose();
        reject(reason);
      }));
    });
    logger2.debug("got response from window");
    this._dispose();
    if (!keepOpen) {
      this.close();
    }
    return { url };
  }
  _dispose() {
    this._logger.create("_dispose");
    for (const dispose of this._disposeHandlers) {
      dispose();
    }
    this._disposeHandlers.clear();
  }
  static _notifyParent(parent, url, keepOpen = false, targetOrigin = window.location.origin) {
    parent.postMessage({
      source: messageSource,
      url,
      keepOpen
    }, targetOrigin);
  }
};

// src/UserManagerSettings.ts
var DefaultPopupWindowFeatures = {
  location: false,
  toolbar: false,
  height: 640,
  closePopupWindowAfterInSeconds: -1
};
var DefaultPopupTarget = "_blank";
var DefaultAccessTokenExpiringNotificationTimeInSeconds = 60;
var DefaultCheckSessionIntervalInSeconds = 2;
var DefaultSilentRequestTimeoutInSeconds = 10;
var UserManagerSettingsStore = class extends OidcClientSettingsStore {
  constructor(args) {
    const {
      popup_redirect_uri = args.redirect_uri,
      popup_post_logout_redirect_uri = args.post_logout_redirect_uri,
      popupWindowFeatures = DefaultPopupWindowFeatures,
      popupWindowTarget = DefaultPopupTarget,
      redirectMethod = "assign",
      redirectTarget = "self",
      iframeNotifyParentOrigin = args.iframeNotifyParentOrigin,
      iframeScriptOrigin = args.iframeScriptOrigin,
      silent_redirect_uri = args.redirect_uri,
      silentRequestTimeoutInSeconds = DefaultSilentRequestTimeoutInSeconds,
      automaticSilentRenew = true,
      validateSubOnSilentRenew = true,
      includeIdTokenInSilentRenew = false,
      monitorSession = false,
      monitorAnonymousSession = false,
      checkSessionIntervalInSeconds = DefaultCheckSessionIntervalInSeconds,
      query_status_response_type = "code",
      stopCheckSessionOnError = true,
      revokeTokenTypes = ["access_token", "refresh_token"],
      revokeTokensOnSignout = false,
      includeIdTokenInSilentSignout = false,
      accessTokenExpiringNotificationTimeInSeconds = DefaultAccessTokenExpiringNotificationTimeInSeconds,
      userStore
    } = args;
    super(args);
    this.popup_redirect_uri = popup_redirect_uri;
    this.popup_post_logout_redirect_uri = popup_post_logout_redirect_uri;
    this.popupWindowFeatures = popupWindowFeatures;
    this.popupWindowTarget = popupWindowTarget;
    this.redirectMethod = redirectMethod;
    this.redirectTarget = redirectTarget;
    this.iframeNotifyParentOrigin = iframeNotifyParentOrigin;
    this.iframeScriptOrigin = iframeScriptOrigin;
    this.silent_redirect_uri = silent_redirect_uri;
    this.silentRequestTimeoutInSeconds = silentRequestTimeoutInSeconds;
    this.automaticSilentRenew = automaticSilentRenew;
    this.validateSubOnSilentRenew = validateSubOnSilentRenew;
    this.includeIdTokenInSilentRenew = includeIdTokenInSilentRenew;
    this.monitorSession = monitorSession;
    this.monitorAnonymousSession = monitorAnonymousSession;
    this.checkSessionIntervalInSeconds = checkSessionIntervalInSeconds;
    this.stopCheckSessionOnError = stopCheckSessionOnError;
    this.query_status_response_type = query_status_response_type;
    this.revokeTokenTypes = revokeTokenTypes;
    this.revokeTokensOnSignout = revokeTokensOnSignout;
    this.includeIdTokenInSilentSignout = includeIdTokenInSilentSignout;
    this.accessTokenExpiringNotificationTimeInSeconds = accessTokenExpiringNotificationTimeInSeconds;
    if (userStore) {
      this.userStore = userStore;
    } else {
      const store = typeof window !== "undefined" ? window.sessionStorage : new InMemoryWebStorage();
      this.userStore = new WebStorageStateStore({ store });
    }
  }
};

// src/navigators/IFrameWindow.ts
var IFrameWindow = class _IFrameWindow extends AbstractChildWindow {
  constructor({
    silentRequestTimeoutInSeconds = DefaultSilentRequestTimeoutInSeconds
  }) {
    super();
    this._logger = new Logger("IFrameWindow");
    this._timeoutInSeconds = silentRequestTimeoutInSeconds;
    this._frame = _IFrameWindow.createHiddenIframe();
    this._window = this._frame.contentWindow;
  }
  static createHiddenIframe() {
    const iframe = window.document.createElement("iframe");
    iframe.style.visibility = "hidden";
    iframe.style.position = "fixed";
    iframe.style.left = "-1000px";
    iframe.style.top = "0";
    iframe.width = "0";
    iframe.height = "0";
    window.document.body.appendChild(iframe);
    return iframe;
  }
  async navigate(params) {
    this._logger.debug("navigate: Using timeout of:", this._timeoutInSeconds);
    const timer = setTimeout(() => void this._abort.raise(new ErrorTimeout("IFrame timed out without a response")), this._timeoutInSeconds * 1e3);
    this._disposeHandlers.add(() => clearTimeout(timer));
    return await super.navigate(params);
  }
  close() {
    var _a;
    if (this._frame) {
      if (this._frame.parentNode) {
        this._frame.addEventListener("load", (ev) => {
          var _a2;
          const frame = ev.target;
          (_a2 = frame.parentNode) == null ? void 0 : _a2.removeChild(frame);
          void this._abort.raise(new Error("IFrame removed from DOM"));
        }, true);
        (_a = this._frame.contentWindow) == null ? void 0 : _a.location.replace("about:blank");
      }
      this._frame = null;
    }
    this._window = null;
  }
  static notifyParent(url, targetOrigin) {
    return super._notifyParent(window.parent, url, false, targetOrigin);
  }
};

// src/navigators/IFrameNavigator.ts
var IFrameNavigator = class {
  constructor(_settings) {
    this._settings = _settings;
    this._logger = new Logger("IFrameNavigator");
  }
  async prepare({
    silentRequestTimeoutInSeconds = this._settings.silentRequestTimeoutInSeconds
  }) {
    return new IFrameWindow({ silentRequestTimeoutInSeconds });
  }
  async callback(url) {
    this._logger.create("callback");
    IFrameWindow.notifyParent(url, this._settings.iframeNotifyParentOrigin);
  }
};

// src/navigators/PopupWindow.ts
var checkForPopupClosedInterval = 500;
var second = 1e3;
var PopupWindow = class extends AbstractChildWindow {
  constructor({
    popupWindowTarget = DefaultPopupTarget,
    popupWindowFeatures = {}
  }) {
    super();
    this._logger = new Logger("PopupWindow");
    const centeredPopup = PopupUtils.center({ ...DefaultPopupWindowFeatures, ...popupWindowFeatures });
    this._window = window.open(void 0, popupWindowTarget, PopupUtils.serialize(centeredPopup));
    if (popupWindowFeatures.closePopupWindowAfterInSeconds && popupWindowFeatures.closePopupWindowAfterInSeconds > 0) {
      setTimeout(() => {
        if (!this._window || typeof this._window.closed !== "boolean" || this._window.closed) {
          void this._abort.raise(new Error("Popup blocked by user"));
          return;
        }
        this.close();
      }, popupWindowFeatures.closePopupWindowAfterInSeconds * second);
    }
  }
  async navigate(params) {
    var _a;
    (_a = this._window) == null ? void 0 : _a.focus();
    const popupClosedInterval = setInterval(() => {
      if (!this._window || this._window.closed) {
        void this._abort.raise(new Error("Popup closed by user"));
      }
    }, checkForPopupClosedInterval);
    this._disposeHandlers.add(() => clearInterval(popupClosedInterval));
    return await super.navigate(params);
  }
  close() {
    if (this._window) {
      if (!this._window.closed) {
        this._window.close();
        void this._abort.raise(new Error("Popup closed"));
      }
    }
    this._window = null;
  }
  static notifyOpener(url, keepOpen) {
    if (!window.opener) {
      throw new Error("No window.opener. Can't complete notification.");
    }
    return super._notifyParent(window.opener, url, keepOpen);
  }
};

// src/navigators/PopupNavigator.ts
var PopupNavigator = class {
  constructor(_settings) {
    this._settings = _settings;
    this._logger = new Logger("PopupNavigator");
  }
  async prepare({
    popupWindowFeatures = this._settings.popupWindowFeatures,
    popupWindowTarget = this._settings.popupWindowTarget
  }) {
    return new PopupWindow({ popupWindowFeatures, popupWindowTarget });
  }
  async callback(url, { keepOpen = false }) {
    this._logger.create("callback");
    PopupWindow.notifyOpener(url, keepOpen);
  }
};

// src/navigators/RedirectNavigator.ts
var RedirectNavigator = class {
  constructor(_settings) {
    this._settings = _settings;
    this._logger = new Logger("RedirectNavigator");
  }
  async prepare({
    redirectMethod = this._settings.redirectMethod,
    redirectTarget = this._settings.redirectTarget
  }) {
    var _a;
    this._logger.create("prepare");
    let targetWindow = window.self;
    if (redirectTarget === "top") {
      targetWindow = (_a = window.top) != null ? _a : window.self;
    }
    const redirect = targetWindow.location[redirectMethod].bind(targetWindow.location);
    let abort;
    return {
      navigate: async (params) => {
        this._logger.create("navigate");
        const promise = new Promise((resolve, reject) => {
          abort = reject;
        });
        redirect(params.url);
        return await promise;
      },
      close: () => {
        this._logger.create("close");
        abort == null ? void 0 : abort(new Error("Redirect aborted"));
        targetWindow.stop();
      }
    };
  }
  async callback() {
    return;
  }
};

// src/UserManagerEvents.ts
var UserManagerEvents = class extends AccessTokenEvents {
  constructor(settings) {
    super({ expiringNotificationTimeInSeconds: settings.accessTokenExpiringNotificationTimeInSeconds });
    this._logger = new Logger("UserManagerEvents");
    this._userLoaded = new Event("User loaded");
    this._userUnloaded = new Event("User unloaded");
    this._silentRenewError = new Event("Silent renew error");
    this._userSignedIn = new Event("User signed in");
    this._userSignedOut = new Event("User signed out");
    this._userSessionChanged = new Event("User session changed");
  }
  async load(user, raiseEvent = true) {
    super.load(user);
    if (raiseEvent) {
      await this._userLoaded.raise(user);
    }
  }
  async unload() {
    super.unload();
    await this._userUnloaded.raise();
  }
  /**
   * Add callback: Raised when a user session has been established (or re-established).
   */
  addUserLoaded(cb) {
    return this._userLoaded.addHandler(cb);
  }
  /**
   * Remove callback: Raised when a user session has been established (or re-established).
   */
  removeUserLoaded(cb) {
    return this._userLoaded.removeHandler(cb);
  }
  /**
   * Add callback: Raised when a user session has been terminated.
   */
  addUserUnloaded(cb) {
    return this._userUnloaded.addHandler(cb);
  }
  /**
   * Remove callback: Raised when a user session has been terminated.
   */
  removeUserUnloaded(cb) {
    return this._userUnloaded.removeHandler(cb);
  }
  /**
   * Add callback: Raised when the automatic silent renew has failed.
   */
  addSilentRenewError(cb) {
    return this._silentRenewError.addHandler(cb);
  }
  /**
   * Remove callback: Raised when the automatic silent renew has failed.
   */
  removeSilentRenewError(cb) {
    return this._silentRenewError.removeHandler(cb);
  }
  /**
   * @internal
   */
  async _raiseSilentRenewError(e) {
    await this._silentRenewError.raise(e);
  }
  /**
   * Add callback: Raised when the user is signed in (when `monitorSession` is set).
   * @see {@link UserManagerSettings.monitorSession}
   */
  addUserSignedIn(cb) {
    return this._userSignedIn.addHandler(cb);
  }
  /**
   * Remove callback: Raised when the user is signed in (when `monitorSession` is set).
   */
  removeUserSignedIn(cb) {
    this._userSignedIn.removeHandler(cb);
  }
  /**
   * @internal
   */
  async _raiseUserSignedIn() {
    await this._userSignedIn.raise();
  }
  /**
   * Add callback: Raised when the user's sign-in status at the OP has changed (when `monitorSession` is set).
   * @see {@link UserManagerSettings.monitorSession}
   */
  addUserSignedOut(cb) {
    return this._userSignedOut.addHandler(cb);
  }
  /**
   * Remove callback: Raised when the user's sign-in status at the OP has changed (when `monitorSession` is set).
   */
  removeUserSignedOut(cb) {
    this._userSignedOut.removeHandler(cb);
  }
  /**
   * @internal
   */
  async _raiseUserSignedOut() {
    await this._userSignedOut.raise();
  }
  /**
   * Add callback: Raised when the user session changed (when `monitorSession` is set).
   * @see {@link UserManagerSettings.monitorSession}
   */
  addUserSessionChanged(cb) {
    return this._userSessionChanged.addHandler(cb);
  }
  /**
   * Remove callback: Raised when the user session changed (when `monitorSession` is set).
   */
  removeUserSessionChanged(cb) {
    this._userSessionChanged.removeHandler(cb);
  }
  /**
   * @internal
   */
  async _raiseUserSessionChanged() {
    await this._userSessionChanged.raise();
  }
};

// src/SilentRenewService.ts
var SilentRenewService = class {
  constructor(_userManager) {
    this._userManager = _userManager;
    this._logger = new Logger("SilentRenewService");
    this._isStarted = false;
    this._retryTimer = new Timer("Retry Silent Renew");
    this._tokenExpiring = async () => {
      const logger2 = this._logger.create("_tokenExpiring");
      try {
        await this._userManager.signinSilent();
        logger2.debug("silent token renewal successful");
      } catch (err) {
        if (err instanceof ErrorTimeout) {
          logger2.warn("ErrorTimeout from signinSilent:", err, "retry in 5s");
          this._retryTimer.init(5);
          return;
        }
        logger2.error("Error from signinSilent:", err);
        await this._userManager.events._raiseSilentRenewError(err);
      }
    };
  }
  async start() {
    const logger2 = this._logger.create("start");
    if (!this._isStarted) {
      this._isStarted = true;
      this._userManager.events.addAccessTokenExpiring(this._tokenExpiring);
      this._retryTimer.addHandler(this._tokenExpiring);
      try {
        await this._userManager.getUser();
      } catch (err) {
        logger2.error("getUser error", err);
      }
    }
  }
  stop() {
    if (this._isStarted) {
      this._retryTimer.cancel();
      this._retryTimer.removeHandler(this._tokenExpiring);
      this._userManager.events.removeAccessTokenExpiring(this._tokenExpiring);
      this._isStarted = false;
    }
  }
};

// src/RefreshState.ts
var RefreshState = class {
  constructor(args) {
    this.refresh_token = args.refresh_token;
    this.id_token = args.id_token;
    this.session_state = args.session_state;
    this.scope = args.scope;
    this.profile = args.profile;
    this.data = args.state;
  }
};

// src/UserManager.ts
var UserManager = class {
  constructor(settings, redirectNavigator, popupNavigator, iframeNavigator) {
    this._logger = new Logger("UserManager");
    this.settings = new UserManagerSettingsStore(settings);
    this._client = new OidcClient(settings);
    this._redirectNavigator = redirectNavigator != null ? redirectNavigator : new RedirectNavigator(this.settings);
    this._popupNavigator = popupNavigator != null ? popupNavigator : new PopupNavigator(this.settings);
    this._iframeNavigator = iframeNavigator != null ? iframeNavigator : new IFrameNavigator(this.settings);
    this._events = new UserManagerEvents(this.settings);
    this._silentRenewService = new SilentRenewService(this);
    if (this.settings.automaticSilentRenew) {
      this.startSilentRenew();
    }
    this._sessionMonitor = null;
    if (this.settings.monitorSession) {
      this._sessionMonitor = new SessionMonitor(this);
    }
  }
  /**
   * Get object used to register for events raised by the `UserManager`.
   */
  get events() {
    return this._events;
  }
  /**
   * Get object used to access the metadata configuration of the identity provider.
   */
  get metadataService() {
    return this._client.metadataService;
  }
  /**
   * Load the `User` object for the currently authenticated user.
   *
   * @returns A promise
   */
  async getUser() {
    const logger2 = this._logger.create("getUser");
    const user = await this._loadUser();
    if (user) {
      logger2.info("user loaded");
      await this._events.load(user, false);
      return user;
    }
    logger2.info("user not found in storage");
    return null;
  }
  /**
   * Remove from any storage the currently authenticated user.
   *
   * @returns A promise
   */
  async removeUser() {
    const logger2 = this._logger.create("removeUser");
    await this.storeUser(null);
    logger2.info("user removed from storage");
    await this._events.unload();
  }
  /**
   * Trigger a redirect of the current window to the authorization endpoint.
   *
   * @returns A promise
   *
   * @throws `Error` In cases of wrong authentication.
   */
  async signinRedirect(args = {}) {
    this._logger.create("signinRedirect");
    const {
      redirectMethod,
      ...requestArgs
    } = args;
    const handle = await this._redirectNavigator.prepare({ redirectMethod });
    await this._signinStart({
      request_type: "si:r",
      ...requestArgs
    }, handle);
  }
  /**
   * Process the response (callback) from the authorization endpoint.
   * It is recommend to use {@link UserManager.signinCallback} instead.
   *
   * @returns A promise containing the authenticated `User`.
   *
   * @see {@link UserManager.signinCallback}
   */
  async signinRedirectCallback(url = window.location.href) {
    const logger2 = this._logger.create("signinRedirectCallback");
    const user = await this._signinEnd(url);
    if (user.profile && user.profile.sub) {
      logger2.info("success, signed in subject", user.profile.sub);
    } else {
      logger2.info("no subject");
    }
    return user;
  }
  /**
   * Trigger the signin with user/password.
   *
   * @returns A promise containing the authenticated `User`.
   * @throws {@link ErrorResponse} In cases of wrong authentication.
   */
  async signinResourceOwnerCredentials({
    username,
    password,
    skipUserInfo = false
  }) {
    const logger2 = this._logger.create("signinResourceOwnerCredential");
    const signinResponse = await this._client.processResourceOwnerPasswordCredentials({ username, password, skipUserInfo, extraTokenParams: this.settings.extraTokenParams });
    logger2.debug("got signin response");
    const user = await this._buildUser(signinResponse);
    if (user.profile && user.profile.sub) {
      logger2.info("success, signed in subject", user.profile.sub);
    } else {
      logger2.info("no subject");
    }
    return user;
  }
  /**
   * Trigger a request (via a popup window) to the authorization endpoint.
   *
   * @returns A promise containing the authenticated `User`.
   * @throws `Error` In cases of wrong authentication.
   */
  async signinPopup(args = {}) {
    const logger2 = this._logger.create("signinPopup");
    const {
      popupWindowFeatures,
      popupWindowTarget,
      ...requestArgs
    } = args;
    const url = this.settings.popup_redirect_uri;
    if (!url) {
      logger2.throw(new Error("No popup_redirect_uri configured"));
    }
    const handle = await this._popupNavigator.prepare({ popupWindowFeatures, popupWindowTarget });
    const user = await this._signin({
      request_type: "si:p",
      redirect_uri: url,
      display: "popup",
      ...requestArgs
    }, handle);
    if (user) {
      if (user.profile && user.profile.sub) {
        logger2.info("success, signed in subject", user.profile.sub);
      } else {
        logger2.info("no subject");
      }
    }
    return user;
  }
  /**
   * Notify the opening window of response (callback) from the authorization endpoint.
   * It is recommend to use {@link UserManager.signinCallback} instead.
   *
   * @returns A promise
   *
   * @see {@link UserManager.signinCallback}
   */
  async signinPopupCallback(url = window.location.href, keepOpen = false) {
    const logger2 = this._logger.create("signinPopupCallback");
    await this._popupNavigator.callback(url, { keepOpen });
    logger2.info("success");
  }
  /**
   * Trigger a silent request (via refresh token or an iframe) to the authorization endpoint.
   *
   * @returns A promise that contains the authenticated `User`.
   */
  async signinSilent(args = {}) {
    var _a;
    const logger2 = this._logger.create("signinSilent");
    const {
      silentRequestTimeoutInSeconds,
      ...requestArgs
    } = args;
    let user = await this._loadUser();
    if (user == null ? void 0 : user.refresh_token) {
      logger2.debug("using refresh token");
      const state = new RefreshState(user);
      return await this._useRefreshToken({
        state,
        redirect_uri: requestArgs.redirect_uri,
        resource: requestArgs.resource,
        extraTokenParams: requestArgs.extraTokenParams,
        timeoutInSeconds: silentRequestTimeoutInSeconds
      });
    }
    const url = this.settings.silent_redirect_uri;
    if (!url) {
      logger2.throw(new Error("No silent_redirect_uri configured"));
    }
    let verifySub;
    if (user && this.settings.validateSubOnSilentRenew) {
      logger2.debug("subject prior to silent renew:", user.profile.sub);
      verifySub = user.profile.sub;
    }
    const handle = await this._iframeNavigator.prepare({ silentRequestTimeoutInSeconds });
    user = await this._signin({
      request_type: "si:s",
      redirect_uri: url,
      prompt: "none",
      id_token_hint: this.settings.includeIdTokenInSilentRenew ? user == null ? void 0 : user.id_token : void 0,
      ...requestArgs
    }, handle, verifySub);
    if (user) {
      if ((_a = user.profile) == null ? void 0 : _a.sub) {
        logger2.info("success, signed in subject", user.profile.sub);
      } else {
        logger2.info("no subject");
      }
    }
    return user;
  }
  async _useRefreshToken(args) {
    const response = await this._client.useRefreshToken({
      ...args,
      timeoutInSeconds: this.settings.silentRequestTimeoutInSeconds
    });
    const user = new User({ ...args.state, ...response });
    await this.storeUser(user);
    await this._events.load(user);
    return user;
  }
  /**
   *
   * Notify the parent window of response (callback) from the authorization endpoint.
   * It is recommend to use {@link UserManager.signinCallback} instead.
   *
   * @returns A promise
   *
   * @see {@link UserManager.signinCallback}
   */
  async signinSilentCallback(url = window.location.href) {
    const logger2 = this._logger.create("signinSilentCallback");
    await this._iframeNavigator.callback(url);
    logger2.info("success");
  }
  /**
   * Process any response (callback) from the authorization endpoint, by dispatching the request_type
   * and executing one of the following functions:
   * - {@link UserManager.signinRedirectCallback}
   * - {@link UserManager.signinPopupCallback}
   * - {@link UserManager.signinSilentCallback}
   *
   * @throws `Error` If request_type is unknown or signout can not processed.
   */
  async signinCallback(url = window.location.href) {
    const { state } = await this._client.readSigninResponseState(url);
    switch (state.request_type) {
      case "si:r":
        return await this.signinRedirectCallback(url);
      case "si:p":
        return await this.signinPopupCallback(url);
      case "si:s":
        return await this.signinSilentCallback(url);
      default:
        throw new Error("invalid response_type in state");
    }
  }
  /**
   * Process any response (callback) from the end session endpoint, by dispatching the request_type
   * and executing one of the following functions:
   * - {@link UserManager.signoutRedirectCallback}
   * - {@link UserManager.signoutPopupCallback}
   * - {@link UserManager.signoutSilentCallback}
   *
   * @throws `Error` If request_type is unknown or signout can not processed.
   */
  async signoutCallback(url = window.location.href, keepOpen = false) {
    const { state } = await this._client.readSignoutResponseState(url);
    if (!state) {
      return;
    }
    switch (state.request_type) {
      case "so:r":
        await this.signoutRedirectCallback(url);
        break;
      case "so:p":
        await this.signoutPopupCallback(url, keepOpen);
        break;
      case "so:s":
        await this.signoutSilentCallback(url);
        break;
      default:
        throw new Error("invalid response_type in state");
    }
  }
  /**
   * Query OP for user's current signin status.
   *
   * @returns A promise object with session_state and subject identifier.
   */
  async querySessionStatus(args = {}) {
    const logger2 = this._logger.create("querySessionStatus");
    const {
      silentRequestTimeoutInSeconds,
      ...requestArgs
    } = args;
    const url = this.settings.silent_redirect_uri;
    if (!url) {
      logger2.throw(new Error("No silent_redirect_uri configured"));
    }
    const user = await this._loadUser();
    const handle = await this._iframeNavigator.prepare({ silentRequestTimeoutInSeconds });
    const navResponse = await this._signinStart({
      request_type: "si:s",
      // this acts like a signin silent
      redirect_uri: url,
      prompt: "none",
      id_token_hint: this.settings.includeIdTokenInSilentRenew ? user == null ? void 0 : user.id_token : void 0,
      response_type: this.settings.query_status_response_type,
      scope: "openid",
      skipUserInfo: true,
      ...requestArgs
    }, handle);
    try {
      const signinResponse = await this._client.processSigninResponse(navResponse.url);
      logger2.debug("got signin response");
      if (signinResponse.session_state && signinResponse.profile.sub) {
        logger2.info("success for subject", signinResponse.profile.sub);
        return {
          session_state: signinResponse.session_state,
          sub: signinResponse.profile.sub
        };
      }
      logger2.info("success, user not authenticated");
      return null;
    } catch (err) {
      if (this.settings.monitorAnonymousSession && err instanceof ErrorResponse) {
        switch (err.error) {
          case "login_required":
          case "consent_required":
          case "interaction_required":
          case "account_selection_required":
            logger2.info("success for anonymous user");
            return {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              session_state: err.session_state
            };
        }
      }
      throw err;
    }
  }
  async _signin(args, handle, verifySub) {
    const navResponse = await this._signinStart(args, handle);
    return await this._signinEnd(navResponse.url, verifySub);
  }
  async _signinStart(args, handle) {
    const logger2 = this._logger.create("_signinStart");
    try {
      const signinRequest = await this._client.createSigninRequest(args);
      logger2.debug("got signin request");
      return await handle.navigate({
        url: signinRequest.url,
        state: signinRequest.state.id,
        response_mode: signinRequest.state.response_mode,
        scriptOrigin: this.settings.iframeScriptOrigin
      });
    } catch (err) {
      logger2.debug("error after preparing navigator, closing navigator window");
      handle.close();
      throw err;
    }
  }
  async _signinEnd(url, verifySub) {
    const logger2 = this._logger.create("_signinEnd");
    const signinResponse = await this._client.processSigninResponse(url);
    logger2.debug("got signin response");
    const user = await this._buildUser(signinResponse, verifySub);
    return user;
  }
  async _buildUser(signinResponse, verifySub) {
    const logger2 = this._logger.create("_buildUser");
    const user = new User(signinResponse);
    if (verifySub) {
      if (verifySub !== user.profile.sub) {
        logger2.debug("current user does not match user returned from signin. sub from signin:", user.profile.sub);
        throw new ErrorResponse({ ...signinResponse, error: "login_required" });
      }
      logger2.debug("current user matches user returned from signin");
    }
    await this.storeUser(user);
    logger2.debug("user stored");
    await this._events.load(user);
    return user;
  }
  /**
   * Trigger a redirect of the current window to the end session endpoint.
   *
   * @returns A promise
   */
  async signoutRedirect(args = {}) {
    const logger2 = this._logger.create("signoutRedirect");
    const {
      redirectMethod,
      ...requestArgs
    } = args;
    const handle = await this._redirectNavigator.prepare({ redirectMethod });
    await this._signoutStart({
      request_type: "so:r",
      post_logout_redirect_uri: this.settings.post_logout_redirect_uri,
      ...requestArgs
    }, handle);
    logger2.info("success");
  }
  /**
   * Process response (callback) from the end session endpoint.
   * It is recommend to use {@link UserManager.signoutCallback} instead.
   *
   * @returns A promise containing signout response
   *
   * @see {@link UserManager.signoutCallback}
   */
  async signoutRedirectCallback(url = window.location.href) {
    const logger2 = this._logger.create("signoutRedirectCallback");
    const response = await this._signoutEnd(url);
    logger2.info("success");
    return response;
  }
  /**
   * Trigger a redirect of a popup window window to the end session endpoint.
   *
   * @returns A promise
   */
  async signoutPopup(args = {}) {
    const logger2 = this._logger.create("signoutPopup");
    const {
      popupWindowFeatures,
      popupWindowTarget,
      ...requestArgs
    } = args;
    const url = this.settings.popup_post_logout_redirect_uri;
    const handle = await this._popupNavigator.prepare({ popupWindowFeatures, popupWindowTarget });
    await this._signout({
      request_type: "so:p",
      post_logout_redirect_uri: url,
      // we're putting a dummy entry in here because we
      // need a unique id from the state for notification
      // to the parent window, which is necessary if we
      // plan to return back to the client after signout
      // and so we can close the popup after signout
      state: url == null ? void 0 : {},
      ...requestArgs
    }, handle);
    logger2.info("success");
  }
  /**
   * Process response (callback) from the end session endpoint from a popup window.
   * It is recommend to use {@link UserManager.signoutCallback} instead.
   *
   * @returns A promise
   *
   * @see {@link UserManager.signoutCallback}
   */
  async signoutPopupCallback(url = window.location.href, keepOpen = false) {
    const logger2 = this._logger.create("signoutPopupCallback");
    await this._popupNavigator.callback(url, { keepOpen });
    logger2.info("success");
  }
  async _signout(args, handle) {
    const navResponse = await this._signoutStart(args, handle);
    return await this._signoutEnd(navResponse.url);
  }
  async _signoutStart(args = {}, handle) {
    var _a;
    const logger2 = this._logger.create("_signoutStart");
    try {
      const user = await this._loadUser();
      logger2.debug("loaded current user from storage");
      if (this.settings.revokeTokensOnSignout) {
        await this._revokeInternal(user);
      }
      const id_token = args.id_token_hint || user && user.id_token;
      if (id_token) {
        logger2.debug("setting id_token_hint in signout request");
        args.id_token_hint = id_token;
      }
      await this.removeUser();
      logger2.debug("user removed, creating signout request");
      const signoutRequest = await this._client.createSignoutRequest(args);
      logger2.debug("got signout request");
      return await handle.navigate({
        url: signoutRequest.url,
        state: (_a = signoutRequest.state) == null ? void 0 : _a.id,
        scriptOrigin: this.settings.iframeScriptOrigin
      });
    } catch (err) {
      logger2.debug("error after preparing navigator, closing navigator window");
      handle.close();
      throw err;
    }
  }
  async _signoutEnd(url) {
    const logger2 = this._logger.create("_signoutEnd");
    const signoutResponse = await this._client.processSignoutResponse(url);
    logger2.debug("got signout response");
    return signoutResponse;
  }
  /**
   * Trigger a silent request (via an iframe) to the end session endpoint.
   *
   * @returns A promise
   */
  async signoutSilent(args = {}) {
    var _a;
    const logger2 = this._logger.create("signoutSilent");
    const {
      silentRequestTimeoutInSeconds,
      ...requestArgs
    } = args;
    const id_token_hint = this.settings.includeIdTokenInSilentSignout ? (_a = await this._loadUser()) == null ? void 0 : _a.id_token : void 0;
    const url = this.settings.popup_post_logout_redirect_uri;
    const handle = await this._iframeNavigator.prepare({ silentRequestTimeoutInSeconds });
    await this._signout({
      request_type: "so:s",
      post_logout_redirect_uri: url,
      id_token_hint,
      ...requestArgs
    }, handle);
    logger2.info("success");
  }
  /**
   * Notify the parent window of response (callback) from the end session endpoint.
   * It is recommend to use {@link UserManager.signoutCallback} instead.
   *
   * @returns A promise
   *
   * @see {@link UserManager.signoutCallback}
   */
  async signoutSilentCallback(url = window.location.href) {
    const logger2 = this._logger.create("signoutSilentCallback");
    await this._iframeNavigator.callback(url);
    logger2.info("success");
  }
  async revokeTokens(types) {
    const user = await this._loadUser();
    await this._revokeInternal(user, types);
  }
  async _revokeInternal(user, types = this.settings.revokeTokenTypes) {
    const logger2 = this._logger.create("_revokeInternal");
    if (!user)
      return;
    const typesPresent = types.filter((type) => typeof user[type] === "string");
    if (!typesPresent.length) {
      logger2.debug("no need to revoke due to no token(s)");
      return;
    }
    for (const type of typesPresent) {
      await this._client.revokeToken(
        user[type],
        // eslint-disable-line @typescript-eslint/no-non-null-assertion
        type
      );
      logger2.info(`${type} revoked successfully`);
      if (type !== "access_token") {
        user[type] = null;
      }
    }
    await this.storeUser(user);
    logger2.debug("user stored");
    await this._events.load(user);
  }
  /**
   * Enables silent renew for the `UserManager`.
   */
  startSilentRenew() {
    this._logger.create("startSilentRenew");
    void this._silentRenewService.start();
  }
  /**
   * Disables silent renew for the `UserManager`.
   */
  stopSilentRenew() {
    this._silentRenewService.stop();
  }
  get _userStoreKey() {
    return `user:${this.settings.authority}:${this.settings.client_id}`;
  }
  async _loadUser() {
    const logger2 = this._logger.create("_loadUser");
    const storageString = await this.settings.userStore.get(this._userStoreKey);
    if (storageString) {
      logger2.debug("user storageString loaded");
      return User.fromStorageString(storageString);
    }
    logger2.debug("no user storageString");
    return null;
  }
  async storeUser(user) {
    const logger2 = this._logger.create("storeUser");
    if (user) {
      logger2.debug("storing user");
      const storageString = user.toStorageString();
      await this.settings.userStore.set(this._userStoreKey, storageString);
    } else {
      this._logger.debug("removing user");
      await this.settings.userStore.remove(this._userStoreKey);
    }
  }
  /**
   * Removes stale state entries in storage for incomplete authorize requests.
   */
  async clearStaleState() {
    await this._client.clearStaleState();
  }
};

// package.json
var version = "3.0.1";

// src/Version.ts
var Version = version;

class TokenStorageFactory {
    static getTokenStorage() {
        return this.instance;
    }
}
TokenStorageFactory.instance = sessionStorage;

class FeatureToggle {
    static init(config) {
        if ('deprecatedExpireEvents' in config) {
            FeatureToggle.deprecatedExpireEvents = config.deprecatedExpireEvents;
        }
    }
    /**
     * Support deprecated OIAM 'expire' Events?
     *
     * To configure the feature-toggle a config is needed.
     * The OauthService.config seems to be the (temporarily) most apporopriate way
     * in concern of delivery.
     */
    static supportDeprecatedExpireEvents() {
        return FeatureToggle.deprecatedExpireEvents;
    }
}
FeatureToggle.deprecatedExpireEvents = false;

// This constructor is used to store event handlers. Instantiating this is
// faster than explicitly calling `Object.create(null)` to get a "clean" empty
// object (tested with v8 v4.9).
function EventHandlers() {}
EventHandlers.prototype = Object.create(null);

function EventEmitter() {
  EventEmitter.init.call(this);
}

// nodejs oddity
// require('events') === require('events').EventEmitter
EventEmitter.EventEmitter = EventEmitter;

EventEmitter.usingDomains = false;

EventEmitter.prototype.domain = undefined;
EventEmitter.prototype._events = undefined;
EventEmitter.prototype._maxListeners = undefined;

// By default EventEmitters will print a warning if more than 10 listeners are
// added to it. This is a useful default which helps finding memory leaks.
EventEmitter.defaultMaxListeners = 10;

EventEmitter.init = function() {
  this.domain = null;

  if (!this._events || this._events === Object.getPrototypeOf(this)._events) {
    this._events = new EventHandlers();
    this._eventsCount = 0;
  }

  this._maxListeners = this._maxListeners || undefined;
};

// Obviously not all Emitters should be limited to 10. This function allows
// that to be increased. Set to zero for unlimited.
EventEmitter.prototype.setMaxListeners = function setMaxListeners(n) {
  if (typeof n !== 'number' || n < 0 || isNaN(n))
    throw new TypeError('"n" argument must be a positive number');
  this._maxListeners = n;
  return this;
};

function $getMaxListeners(that) {
  if (that._maxListeners === undefined)
    return EventEmitter.defaultMaxListeners;
  return that._maxListeners;
}

EventEmitter.prototype.getMaxListeners = function getMaxListeners() {
  return $getMaxListeners(this);
};

// These standalone emit* functions are used to optimize calling of event
// handlers for fast cases because emit() itself often has a variable number of
// arguments and can be deoptimized because of that. These functions always have
// the same number of arguments and thus do not get deoptimized, so the code
// inside them can execute faster.
function emitNone(handler, isFn, self) {
  if (isFn)
    handler.call(self);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self);
  }
}
function emitOne(handler, isFn, self, arg1) {
  if (isFn)
    handler.call(self, arg1);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1);
  }
}
function emitTwo(handler, isFn, self, arg1, arg2) {
  if (isFn)
    handler.call(self, arg1, arg2);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1, arg2);
  }
}
function emitThree(handler, isFn, self, arg1, arg2, arg3) {
  if (isFn)
    handler.call(self, arg1, arg2, arg3);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].call(self, arg1, arg2, arg3);
  }
}

function emitMany(handler, isFn, self, args) {
  if (isFn)
    handler.apply(self, args);
  else {
    var len = handler.length;
    var listeners = arrayClone(handler, len);
    for (var i = 0; i < len; ++i)
      listeners[i].apply(self, args);
  }
}

EventEmitter.prototype.emit = function emit(type) {
  var er, handler, len, args, i, events, domain;
  var doError = (type === 'error');

  events = this._events;
  if (events)
    doError = (doError && events.error == null);
  else if (!doError)
    return false;

  domain = this.domain;

  // If there is no 'error' event listener then throw.
  if (doError) {
    er = arguments[1];
    if (domain) {
      if (!er)
        er = new Error('Uncaught, unspecified "error" event');
      er.domainEmitter = this;
      er.domain = domain;
      er.domainThrown = false;
      domain.emit('error', er);
    } else if (er instanceof Error) {
      throw er; // Unhandled 'error' event
    } else {
      // At least give some kind of context to the user
      var err = new Error('Uncaught, unspecified "error" event. (' + er + ')');
      err.context = er;
      throw err;
    }
    return false;
  }

  handler = events[type];

  if (!handler)
    return false;

  var isFn = typeof handler === 'function';
  len = arguments.length;
  switch (len) {
    // fast cases
    case 1:
      emitNone(handler, isFn, this);
      break;
    case 2:
      emitOne(handler, isFn, this, arguments[1]);
      break;
    case 3:
      emitTwo(handler, isFn, this, arguments[1], arguments[2]);
      break;
    case 4:
      emitThree(handler, isFn, this, arguments[1], arguments[2], arguments[3]);
      break;
    // slower
    default:
      args = new Array(len - 1);
      for (i = 1; i < len; i++)
        args[i - 1] = arguments[i];
      emitMany(handler, isFn, this, args);
  }

  return true;
};

function _addListener(target, type, listener, prepend) {
  var m;
  var events;
  var existing;

  if (typeof listener !== 'function')
    throw new TypeError('"listener" argument must be a function');

  events = target._events;
  if (!events) {
    events = target._events = new EventHandlers();
    target._eventsCount = 0;
  } else {
    // To avoid recursion in the case that type === "newListener"! Before
    // adding it to the listeners, first emit "newListener".
    if (events.newListener) {
      target.emit('newListener', type,
                  listener.listener ? listener.listener : listener);

      // Re-assign `events` because a newListener handler could have caused the
      // this._events to be assigned to a new object
      events = target._events;
    }
    existing = events[type];
  }

  if (!existing) {
    // Optimize the case of one listener. Don't need the extra array object.
    existing = events[type] = listener;
    ++target._eventsCount;
  } else {
    if (typeof existing === 'function') {
      // Adding the second element, need to change to array.
      existing = events[type] = prepend ? [listener, existing] :
                                          [existing, listener];
    } else {
      // If we've already got an array, just append.
      if (prepend) {
        existing.unshift(listener);
      } else {
        existing.push(listener);
      }
    }

    // Check for listener leak
    if (!existing.warned) {
      m = $getMaxListeners(target);
      if (m && m > 0 && existing.length > m) {
        existing.warned = true;
        var w = new Error('Possible EventEmitter memory leak detected. ' +
                            existing.length + ' ' + type + ' listeners added. ' +
                            'Use emitter.setMaxListeners() to increase limit');
        w.name = 'MaxListenersExceededWarning';
        w.emitter = target;
        w.type = type;
        w.count = existing.length;
        emitWarning(w);
      }
    }
  }

  return target;
}
function emitWarning(e) {
  typeof console.warn === 'function' ? console.warn(e) : console.log(e);
}
EventEmitter.prototype.addListener = function addListener(type, listener) {
  return _addListener(this, type, listener, false);
};

EventEmitter.prototype.on = EventEmitter.prototype.addListener;

EventEmitter.prototype.prependListener =
    function prependListener(type, listener) {
      return _addListener(this, type, listener, true);
    };

function _onceWrap(target, type, listener) {
  var fired = false;
  function g() {
    target.removeListener(type, g);
    if (!fired) {
      fired = true;
      listener.apply(target, arguments);
    }
  }
  g.listener = listener;
  return g;
}

EventEmitter.prototype.once = function once(type, listener) {
  if (typeof listener !== 'function')
    throw new TypeError('"listener" argument must be a function');
  this.on(type, _onceWrap(this, type, listener));
  return this;
};

EventEmitter.prototype.prependOnceListener =
    function prependOnceListener(type, listener) {
      if (typeof listener !== 'function')
        throw new TypeError('"listener" argument must be a function');
      this.prependListener(type, _onceWrap(this, type, listener));
      return this;
    };

// emits a 'removeListener' event iff the listener was removed
EventEmitter.prototype.removeListener =
    function removeListener(type, listener) {
      var list, events, position, i, originalListener;

      if (typeof listener !== 'function')
        throw new TypeError('"listener" argument must be a function');

      events = this._events;
      if (!events)
        return this;

      list = events[type];
      if (!list)
        return this;

      if (list === listener || (list.listener && list.listener === listener)) {
        if (--this._eventsCount === 0)
          this._events = new EventHandlers();
        else {
          delete events[type];
          if (events.removeListener)
            this.emit('removeListener', type, list.listener || listener);
        }
      } else if (typeof list !== 'function') {
        position = -1;

        for (i = list.length; i-- > 0;) {
          if (list[i] === listener ||
              (list[i].listener && list[i].listener === listener)) {
            originalListener = list[i].listener;
            position = i;
            break;
          }
        }

        if (position < 0)
          return this;

        if (list.length === 1) {
          list[0] = undefined;
          if (--this._eventsCount === 0) {
            this._events = new EventHandlers();
            return this;
          } else {
            delete events[type];
          }
        } else {
          spliceOne(list, position);
        }

        if (events.removeListener)
          this.emit('removeListener', type, originalListener || listener);
      }

      return this;
    };

EventEmitter.prototype.removeAllListeners =
    function removeAllListeners(type) {
      var listeners, events;

      events = this._events;
      if (!events)
        return this;

      // not listening for removeListener, no need to emit
      if (!events.removeListener) {
        if (arguments.length === 0) {
          this._events = new EventHandlers();
          this._eventsCount = 0;
        } else if (events[type]) {
          if (--this._eventsCount === 0)
            this._events = new EventHandlers();
          else
            delete events[type];
        }
        return this;
      }

      // emit removeListener for all listeners on all events
      if (arguments.length === 0) {
        var keys = Object.keys(events);
        for (var i = 0, key; i < keys.length; ++i) {
          key = keys[i];
          if (key === 'removeListener') continue;
          this.removeAllListeners(key);
        }
        this.removeAllListeners('removeListener');
        this._events = new EventHandlers();
        this._eventsCount = 0;
        return this;
      }

      listeners = events[type];

      if (typeof listeners === 'function') {
        this.removeListener(type, listeners);
      } else if (listeners) {
        // LIFO order
        do {
          this.removeListener(type, listeners[listeners.length - 1]);
        } while (listeners[0]);
      }

      return this;
    };

EventEmitter.prototype.listeners = function listeners(type) {
  var evlistener;
  var ret;
  var events = this._events;

  if (!events)
    ret = [];
  else {
    evlistener = events[type];
    if (!evlistener)
      ret = [];
    else if (typeof evlistener === 'function')
      ret = [evlistener.listener || evlistener];
    else
      ret = unwrapListeners(evlistener);
  }

  return ret;
};

EventEmitter.listenerCount = function(emitter, type) {
  if (typeof emitter.listenerCount === 'function') {
    return emitter.listenerCount(type);
  } else {
    return listenerCount.call(emitter, type);
  }
};

EventEmitter.prototype.listenerCount = listenerCount;
function listenerCount(type) {
  var events = this._events;

  if (events) {
    var evlistener = events[type];

    if (typeof evlistener === 'function') {
      return 1;
    } else if (evlistener) {
      return evlistener.length;
    }
  }

  return 0;
}

EventEmitter.prototype.eventNames = function eventNames() {
  return this._eventsCount > 0 ? Reflect.ownKeys(this._events) : [];
};

// About 1.5x faster than the two-arg version of Array#splice().
function spliceOne(list, index) {
  for (var i = index, k = i + 1, n = list.length; k < n; i += 1, k += 1)
    list[i] = list[k];
  list.pop();
}

function arrayClone(arr, i) {
  var copy = new Array(i);
  while (i--)
    copy[i] = arr[i];
  return copy;
}

function unwrapListeners(arr) {
  var ret = new Array(arr.length);
  for (var i = 0; i < ret.length; ++i) {
    ret[i] = arr[i].listener || arr[i];
  }
  return ret;
}

class OauthEventService {
    static doDispatchEvent(event, data) {
        log.debug('oiam-oauth-wc: dispatch event', event);
        // loaded event as interval to handle async loading of components
        if (event === OiamEvents.LOADED) {
            this.startSendLoadedEventWithExponentialBackoff();
        }
        else {
            this.sendEvent(event, data);
        }
    }
    static startSendLoadedEventWithExponentialBackoff() {
        // due to JavaScript's nature, event listeners for loaded may execute
        // synchronously and send oiamSetConfigEvent synchronously which triggers
        // CONFIGURED_EVENT synchronously so that we have to add the clearTimeout-listener
        // before we send LOADED
        OauthEventService.emitter.once(OauthEventService.CONFIGURED_EVENT, () => {
            log.debug(`stop emitting ${OiamEvents.LOADED}, we've been configured`);
            clearTimeout(this.eventTimer);
            this.configured = true;
        });
        this.sendEvent(OiamEvents.LOADED, true);
        this.sendLoadedPeriodicallyUntilConfigured(1);
    }
    static async sendBeforeNavigationEventWithWait(waittime = 20) {
        this.doDispatchEvent(OiamEvents.BEFORENAVIGATION);
        return new Promise(resolve => setTimeout(resolve, waittime));
    }
    static sendLoadedPeriodicallyUntilConfigured(currentAttempt) {
        if (this.configured) {
            return;
        }
        // base value 1.095 results in min 50 events in the first minute
        const currentDelay = 20 * Math.pow(1.095, currentAttempt - 1);
        this.eventTimer = setTimeout(() => {
            if (currentAttempt > this.MAX_ATTEMPTS_SEND_LOADED) {
                log.warn('Missing oiamSetConfigEvent, oiam-oauth-wc cannot work without it.');
            }
            else {
                this.sendEvent(OiamEvents.LOADED, true);
                this.sendLoadedPeriodicallyUntilConfigured(++currentAttempt);
            }
        }, currentDelay);
    }
    static sendEvent(event, data) {
        window.self.dispatchEvent(new CustomEvent(event, {
            bubbles: true,
            composed: true,
            detail: data,
        }));
    }
}
OauthEventService.emitter = new EventEmitter();
OauthEventService.STATE_QUEUE_EVENT = 'oiamStateChangeEvent';
OauthEventService.CONFIGURED_EVENT = 'oiamConfiguredEvent';
OauthEventService.configured = false;
// spaming events should terminate after 30min
OauthEventService.MAX_ATTEMPTS_SEND_LOADED = 95;
OauthEventService.eventTimer = undefined;

const cookieConfig = {
    samesiteLax: 'samesite=lax',
};

const generateRandomString = length => {
    const characters = 'abcdef0123456789';
    const charactersLength = characters.length;
    let result = '';
    // Create an array of 32-bit unsigned integers
    const randomValues = new Uint32Array(length);
    // Generate random values
    window.crypto.getRandomValues(randomValues);
    randomValues.forEach(value => {
        result += characters.charAt(value % charactersLength);
    });
    return result;
};
const generateUUID = () => {
    return (generateRandomString(8) +
        '-' +
        generateRandomString(4) +
        '-' +
        generateRandomString(4) +
        '-' +
        generateRandomString(4) +
        '-' +
        generateRandomString(12));
};
const getDomainPart = (partcount = 2, host) => {
    let base = window.location.hostname;
    if (host) {
        // handle full urls -> remove protocol, port, query parameter
        host = host.replaceAll(/^(\w+:\/\/)/g, '');
        host = host.replaceAll(/([/:?#].*)/g, '');
        base = host;
    }
    // localhost
    if (base === 'localhost') {
        return 'localhost';
    }
    // get domain part
    let retval = '';
    const parts = base.split('.');
    if (parts.length < partcount) {
        partcount = parts.length;
    }
    for (let n = parts.length - partcount; n < parts.length; n++) {
        retval += '.' + parts[n];
    }
    return retval;
};
const stripSpecialQueryparams = (originuri, extraqueryparams) => {
    const u = new URL(originuri);
    // query params reserved for our OIDC implementation
    for (const qp of [
        'code',
        'state',
        'acr_values',
        'session_state',
        'customstate',
        'iss',
        'acting-on-behalf-of',
        'identity',
        'error',
        'error_description',
        'motp',
        'kc_action',
        'kc_action_status',
    ]) {
        u.searchParams.delete(qp);
    }
    for (const qp in extraqueryparams) {
        u.searchParams.delete(qp);
    }
    return u.toString();
};
function compareArrayAnyOrder(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    return a.every(value => b.includes(value));
}
/**
 * Somehow simplified recursive deep object comparison ignoring differences in order.
 * Use case is SetConfigEventPayload comparison, objects within arrays are not really supported
 * @returns true for equal objects, false otherwise
 */
const compareObjectAnyOrder = (a, b) => {
    if ((a === undefined && b === undefined) || (a === null && b === null)) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    if (Object.keys(a).length != Object.keys(b).length) {
        return false;
    }
    return Object.keys(a).every(key => {
        // arrayA === arrayB is always false -> we have to check each element
        if (a[key] instanceof Array && b[key] instanceof Array) {
            return compareArrayAnyOrder(a[key], b[key]);
        }
        else if (['number', 'string', 'boolean', 'bigint', 'symbol', 'function', 'undefined'].includes(typeof a[key])) {
            return a[key] === b[key];
        }
        else {
            return compareObjectAnyOrder(a[key], b[key]);
        }
    });
};
const isValidUri = (uri) => {
    try {
        return Boolean(new URL(uri));
    }
    catch (e) {
        log.error('oiam-oauth-wc: wrong url format given!', e);
        return false;
    }
};

class StoreService {
    /** a specific client has been previously invoked, is not transferrable to other clients */
    static get keyPrevInvocation() {
        return this.KEY_OAUTH_PREV_INVOCATION + ':' + this.clientid;
    }
    static get keyRetryInvocation() {
        return this.KEY_OAUTH_RETRY_INVOCATION + ':' + this.clientid;
    }
    static createInstance() {
        const inst = this.loadInstance();
        if (inst.instance) {
            this.instance = inst.instance;
        }
        else {
            this.instance = generateUUID();
            this.setInstance(this.instance);
        }
        return this.isRunningInstance();
    }
    static createFallbackInstance() {
        const inst = this.getFallbackInstance();
        if (inst.instance) {
            this.instance = inst.instance;
        }
        else {
            this.instance = generateUUID();
            this.setFallbackInstance(this.instance);
        }
        return this.isRunningFallbackInstance();
    }
    static isSameInstance(instance) {
        return this.instance === instance;
    }
    static readState() {
        const sta = this.getFromSessionStorage(this.KEY_OAUTHSTATE);
        if (sta) {
            const astates = JSON.parse(sta);
            const astate = astates[this.clientid];
            if (astate) {
                return astate;
            }
        }
        return OauthStates.EMPTY;
    }
    static deleteState() {
        this.removeFromSessionStorage(this.KEY_OAUTHSTATE);
        return this.removeFromSessionStorage(this.KEY_OAUTHSTATE);
    }
    /**
     * Save state per clientid
     * Zentrales Item im sessionStorage damit es einfach gelöscht werden kann
     *
     * @param state
     */
    static saveState(state) {
        // we need the client id
        if (!this.clientid) {
            return;
        }
        let storedstates = {};
        const st = this.getFromSessionStorage(this.KEY_OAUTHSTATE);
        if (st) {
            const states = this.getStatesObjectHandleOldFormats(st);
            storedstates = states ? states : storedstates;
        }
        storedstates[this.clientid] = state;
        this.setToSessionStorage(this.KEY_OAUTHSTATE, JSON.stringify(storedstates));
    }
    static setSettings(clientid, authority) {
        this.clientid = clientid;
        this.authority = authority;
    }
    static getAuthority() {
        return this.authority;
    }
    static getClientId() {
        return this.clientid;
    }
    static setInstance(instance) {
        const obj = {
            instance,
        };
        this.setToSessionStorage(this.KEY_OAUTHINSTANCE, JSON.stringify(obj));
    }
    static setFallbackInstance(instance) {
        const obj = {
            instance,
        };
        this.setToSessionStorage(this.KEY_OAUTHFALLBACKINSTANCE, JSON.stringify(obj));
    }
    static getInstance() {
        if (!this.instance) {
            this.createInstance();
        }
        return { instance: this.instance };
    }
    static getFallbackInstance() {
        const inst = this.getFromSessionStorage(this.KEY_OAUTHFALLBACKINSTANCE);
        if (inst) {
            return JSON.parse(inst);
        }
        return {
            instance: null,
        };
    }
    static getOiamSessionCookie() {
        const cookie = this.getOiamCookie(OiamCookie.SESSIONCOOKIE);
        return cookie ? JSON.parse(cookie) : undefined;
    }
    static storeOiamSessionCookieKeepAdditionalInfo(parts) {
        const parsedSessionCookie = this.getOiamSessionCookie();
        this.storeOiamSessionCookie(parsedSessionCookie?.additionalinfo, parts);
    }
    static storeOiamSessionCookie(additionalInfo, parts) {
        const domain = getDomainPart();
        const body = {
            instance: this.getInstance().instance,
            clientid: this.clientid,
            additionalinfo: additionalInfo,
            ...parts,
        };
        document.cookie =
            OiamCookie.SESSIONCOOKIE +
                '=' +
                btoa(JSON.stringify(body)) +
                '; path=/; domain=' +
                domain +
                ';' +
                cookieConfig.samesiteLax +
                ';';
        log.debug('oiam-oauth-wc: oiamsession cookie stored', JSON.stringify(body));
    }
    static updateOiamSessionCookie(updatedParts) {
        const parsedSessionCookie = this.getOiamSessionCookie();
        const staticParts = {
            ...(parsedSessionCookie?.actingOnBehalfOf && { actingOnBehalfOf: parsedSessionCookie.actingOnBehalfOf }),
            ...(parsedSessionCookie?.actingType && { actingType: parsedSessionCookie.actingType }),
            ...(parsedSessionCookie?.maxstart && { maxstart: parsedSessionCookie.maxstart }),
            ...(parsedSessionCookie?.authnlevel && { authnlevel: parsedSessionCookie.authnlevel }),
            ...updatedParts,
        };
        this.storeOiamSessionCookie(parsedSessionCookie?.additionalinfo, staticParts);
    }
    static setAdditionalInfoInOiamSessionCookie(additionalInfo) {
        const parsedSessionCookie = this.getOiamSessionCookie();
        const staticParts = {
            ...(parsedSessionCookie?.actingOnBehalfOf && { actingOnBehalfOf: parsedSessionCookie.actingOnBehalfOf }),
            ...(parsedSessionCookie?.actingType && { actingType: parsedSessionCookie.actingType }),
            ...(parsedSessionCookie?.maxstart && { maxstart: parsedSessionCookie.maxstart }),
            ...(parsedSessionCookie?.idlestart && { idlestart: parsedSessionCookie.idlestart }),
            ...(parsedSessionCookie?.authnlevel && { authnlevel: parsedSessionCookie.authnlevel }),
        };
        this.storeOiamSessionCookie(additionalInfo, staticParts);
    }
    static checkForOiamSessionIdCookie() {
        return this.getPlainCookie(OiamCookie.SESSIONID);
    }
    /**
     * There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
     * To prevent errors in that case, store the cookie at client side (login)
     */
    static storeOiamSessionIdCookieOnTestDevOrLocalhost(accessToken) {
        if (this.hostsWithoutOAG.indexOf(window.location.hostname) >= 0) {
            const jwt = new BaJwt(accessToken);
            const domain = getDomainPart();
            document.cookie =
                OiamCookie.SESSIONID +
                    '=' +
                    jwt.payload.sid +
                    '; path=/; domain=' +
                    domain +
                    ';' +
                    cookieConfig.samesiteLax +
                    ';';
        }
    }
    /**
     * There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
     * To prevent errors in that case, remove the cookie at client side (logout)
     */
    static removeOiamSessionIdCookieOnTestDevOrLocalhost() {
        if (this.hostsWithoutOAG.indexOf(window.location.hostname) >= 0) {
            log.debug('removeOiamSessionIdCookieOnTestDevOrLocalhost', this.hostsWithoutOAG, window.location.hostname);
            this.deleteOiamCookie(OiamCookie.SESSIONID);
        }
    }
    static getOiamCookie(cookiename) {
        const plain = this.getPlainCookie(cookiename);
        return atob(plain);
    }
    static getPlainCookie(cookiename) {
        let name = cookiename + '=';
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let cpart of ca) {
            let c = cpart.trimStart();
            if (c.indexOf(name) == 0) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    }
    static deleteOiamCookie(cookiename) {
        const domain = getDomainPart();
        document.cookie = cookiename + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;' + ' domain=' + domain + ';';
    }
    static getPreviousInvocation() {
        const prev = this.getFromSessionStorage(this.keyPrevInvocation);
        return JSON.parse(prev);
    }
    static getRetryInvocation() {
        const retry = this.getFromSessionStorage(this.keyRetryInvocation);
        return JSON.parse(retry);
    }
    static setPreviousInvocation(c, fieldsToExclude = ['authnLevel', 'authnMethod', 'accountLink']) {
        const pruned = { ...c };
        // these settings should only be active for the next Keycloak round
        for (const f of fieldsToExclude) {
            delete pruned[f];
        }
        this.setToSessionStorage(this.keyPrevInvocation, JSON.stringify(pruned));
        return pruned;
    }
    static copyRetryToPreviousInvocation() {
        const retryInv = this.getRetryInvocation();
        // full retry with all options set
        const prevInv = this.setPreviousInvocation(retryInv, []);
        log.debug('oiam-oauth-wc: copy retry invocation as previous', retryInv, prevInv);
    }
    static setRetryInvocation(c) {
        this.setToSessionStorage(this.keyRetryInvocation, JSON.stringify(c));
    }
    static deletePreviousInvocation() {
        this.removeFromSessionStorage(this.keyPrevInvocation);
    }
    static deleteRetryInvocation() {
        this.removeFromSessionStorage(this.keyRetryInvocation);
    }
    /**
     * We have to remove any user data from the session storage at logout
     * Currently we do a session storage clear, but this might change in future
     *
     * To perform a logout with the oidc-client-ts, we must remember and restore necessary data (state, id-token ...)
     */
    static deleteUserData(keepRetryInvocation) {
        // delete state
        this.removeFromSessionStorage(this.KEY_OAUTHSTATE);
        // first get the data, the oidc-client-ts will need for the logout
        // key format "oidc.user:[authority]:[client-id]"
        const key = 'oidc.user:' + this.authority + ':' + this.clientid;
        const data = this.getFromSessionStorage(key);
        const retryInv = keepRetryInvocation ? this.getRetryInvocation() : undefined;
        // now clear storage
        this.clearStorage();
        // restore the oidc-client-ts data
        if (data) {
            this.setToSessionStorage(key, data);
        }
        if (retryInv) {
            this.setRetryInvocation(retryInv);
        }
    }
    /**
     * @returns the access Unix timestamp in seconds from the last OAG backend call
     */
    static getBackendAccessTS() {
        const ts = StoreService.getPlainCookie(OiamCookie.BACKENDACCESSCOOKIE);
        const result = +ts;
        if (isNaN(result)) {
            return 0;
        }
        return result;
    }
    /**
     * Check for multiple instances of oiam-oauth-component
     * Store cache value at window object to prevent multiple intances to render
     *
     * @private
     */
    static isRunningInstance() {
        const elements = document.getElementsByTagName('oiam-oauth-component');
        if (elements.length > 1) {
            if (window['oiam_running']) {
                // keep console log for eService team to see
                log.error('oiam-oauth-wc: Multiple Instances available -> only one instance will render', 'main');
                NginxErrorLogger.logError({ ...ERR_OIAM_MULTIPLE_INSTANCES, details: 'oiam-oauth-component' });
                return false;
            }
            else {
                window['oiam_running'] = this.instance;
            }
        }
        return true;
    }
    static isRunningFallbackInstance() {
        const elements = document.getElementsByTagName('oiam-oauth-fallback');
        if (elements.length > 1) {
            if (window['oiam_fallback_running']) {
                // keep console log for eService team to see
                log.error('oiam-oauth-wc: Multiple Instances available -> only one instance will render', 'fallback');
                NginxErrorLogger.logError({ ...ERR_OIAM_MULTIPLE_INSTANCES, details: 'oiam-oauth-fallback' });
                return false;
            }
            else {
                window['oiam_fallback_running'] = this.instance;
            }
        }
        return true;
    }
    static loadInstance() {
        const inst = this.getFromSessionStorage(this.KEY_OAUTHINSTANCE);
        if (inst) {
            return JSON.parse(inst);
        }
        return {
            instance: null,
        };
    }
    /**
     * There are sometimes exceptions at Storage get especially on firefox (NS_ERROR_FILE_NOT_FOUND, NS_ERROR_FILE_CORRUPTED)
     * use try catch to get rid of the nginx error logs in such case
     * we cannot do anything to fix it
     */
    static getFromSessionStorage(key) {
        try {
            return sessionStorage.getItem(key);
        }
        catch (ex) {
            // it is a problem of the users system
            // we cannot do anything about it
            // just log local
            log.error('Exception at read sessionStorage', key, ex);
        }
        return null;
    }
    static setToSessionStorage(key, value) {
        try {
            sessionStorage.setItem(key, value);
        }
        catch (ex) {
            log.error('Exception at write sessionStorage', key, ex);
        }
    }
    static removeFromSessionStorage(key) {
        try {
            sessionStorage.removeItem(key);
        }
        catch (ex) {
            log.error('Exception at remove from sessionStorage', key, ex);
        }
    }
    static clearStorage() {
        try {
            sessionStorage.clear();
        }
        catch (ex) {
            log.error('Exception at clear Storage', ex);
        }
    }
    static getStatesObjectHandleOldFormats(statesstr) {
        const states = JSON.parse(statesstr);
        if (Object.getOwnPropertyNames(states).includes('internalstate')) {
            console.debug('Deprecated state format found: will be deleted');
            this.deleteState();
            return;
        }
        else {
            return states;
        }
    }
}
StoreService.KEY_OAUTHSTATE = 'oiam-oauth-wc-state';
StoreService.KEY_OAUTHINSTANCE = 'oiam-oauth-wc-instance';
StoreService.KEY_OAUTHFALLBACKINSTANCE = 'oiam-oauth-fallback-instance';
/**
 * This is the part that should be continued after returning vom Keycloak.
 * One-time actions like forced profile-selection normally are not part of this.
 * Same for accountLink, which only gets copied over from PREV in case of retries.
 */
StoreService.KEY_OAUTH_PREV_INVOCATION = 'oiam-oauth-wc-prev-invocation';
/**
 * "retry invocation" is the full configuration of the last login/stepup/kc_action attempt.
 * Should there be the need for a repeated try (e.g. because the previous login was to old),
 * this attempt can be recreated by copying it from "retry invocation" to "previous invocation".
 */
StoreService.KEY_OAUTH_RETRY_INVOCATION = 'oiam-oauth-wc-retry-invocation';
StoreService.clientid = '';
StoreService.authority = '';
StoreService.hostsWithoutOAG = [
    'secure.dev.ocp.webapp.idst.ibaintern.de',
    'test.dev.ocp.webapp.idst.ibaintern.de',
    'localhost',
];

const createUserManager = (settings, _) => {
    return new oidcClientTs.UserManager(settings);
};

class OauthService {
    /**
     * Laden der Konfigurationsdatei
     * Zurücksetzen der Objekte
     * Auslesen von Query Parametern
     */
    static async init(config) {
        log.setLevel('info');
        this.defaultConfig = config;
        this.defaultConfig.logLevel && log.setLevel(this.defaultConfig.logLevel);
    }
    static async setConfig(config) {
        config.logLevel && log.setLevel(config.logLevel);
        FeatureToggle.init(config);
        // Set motp, correlation-id ...
        let extraqueryparams = this.computeExtraQueryParams();
        // save settings for usage after redirects
        this.redirecturi = stripSpecialQueryparams(config.redirecturi, extraqueryparams);
        const settings = this.fillSettings(config, extraqueryparams);
        // factory method overwritten in e2e tests
        this.userManager = createUserManager(settings);
        StoreService.setSettings(settings.client_id, settings.authority);
    }
    static checkConfigured() {
        return !!this.userManager;
    }
    static async doLogin(loginStep, eServiceConfig) {
        await OauthEventService.sendBeforeNavigationEventWithWait();
        if (this.checkConfigured()) {
            const prevInv = StoreService.getPreviousInvocation();
            if (!prevInv) {
                const newPrev = OauthService.toPreviousInvocation(eServiceConfig);
                if (loginStep === 'profile-selection') {
                    // a forced profile selection should be remembered after a login,
                    // but is done when sending to profile selection.
                    delete newPrev.forceProfileSelection;
                }
                StoreService.setPreviousInvocation(newPrev);
                StoreService.setRetryInvocation(newPrev);
            }
            else {
                if (loginStep === '(re-)login') {
                    // Normally, in authCodeRequested the prevInv has been restored.
                    // But in case of reauth_required, we restart after a logout
                    // where prevInv in all other cases has been cleared.
                    eServiceConfig.mergeLoginAcrValues(prevInv);
                }
                this.forgetOnetimePartsOfPreviousInvocation(loginStep, prevInv);
            }
            // init authorization code flow with arguments if any
            const args = this.fillLoginArgs(loginStep, eServiceConfig);
            if (args) {
                log.debug('oiam-oauth-wc: Start login with arguments', args);
                await this.userManager.signinRedirect(args);
            }
            else {
                await this.userManager.signinRedirect();
            }
        }
        else {
            // page not loaded completely
            // hard reload to prevent empty page
            window.location.reload();
        }
    }
    static async doLogout() {
        if (!this.checkConfigured()) {
            throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call doLogout' });
        }
        await OauthEventService.sendBeforeNavigationEventWithWait();
        await this.doSignout();
    }
    static setLoginMotp(motp) {
        this.motp = motp;
    }
    static async askForToken(forcerenew = false) {
        if (!this.checkConfigured()) {
            return '';
        }
        const user = await this.getUser();
        if (!user) {
            return '';
        }
        const baJwt = new BaJwt(user.access_token);
        if (!forcerenew && TokenEnsurer.validLongEnough(baJwt)) {
            return user.access_token;
        }
        await this.userManager.signinSilent({
            extraTokenParams: { 'correlation-id': StoreService.getInstance().instance },
        });
        return (await this.getUser()).access_token; // has to be fresh
    }
    static async getToken() {
        if (this.checkConfigured()) {
            // get the access token
            let error = '';
            const user = await this.userManager.signinRedirectCallback().catch(err => {
                error = err;
                return undefined;
            });
            if (user) {
                // BundID on mobile requires that the user switches to AusweisApp and on return,
                // will end up in a fresh tab on redirecturi, previousInvocation lost.
                // Therefore restore it from the user's data/state attribute.
                // Only overwrite it in this case and return as if it always had been there.
                const storedPreviousInvocation = StoreService.getPreviousInvocation();
                if (!storedPreviousInvocation) {
                    const previousInvocation = user.state;
                    StoreService.setPreviousInvocation(previousInvocation);
                }
                // There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
                // To prevent errors in that case, store the cookie at client side (login)
                StoreService.storeOiamSessionIdCookieOnTestDevOrLocalhost(user.access_token);
                return user;
            }
            else {
                throw new OiamErrorPayloadException({ ...ERR_NO_USER, details: error });
            }
        }
        else {
            throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call getToken' });
        }
    }
    static async getTokenEndpoint() {
        if (this.checkConfigured()) {
            return await this.userManager.metadataService.getTokenEndpoint();
        }
        return '';
    }
    static async getUser() {
        if (this.checkConfigured()) {
            return this.userManager.getUser();
        }
        return null;
    }
    static async removeUser() {
        if (this.checkConfigured()) {
            return this.userManager.removeUser();
        }
        StoreService.deletePreviousInvocation();
        StoreService.deleteRetryInvocation();
    }
    static getUserManager() {
        return this.userManager;
    }
    static getProfileSelectionRedirectUrls() {
        if (this.checkConfigured()) {
            return this.defaultConfig.profileSelectionRedirectUrls;
        }
        else {
            throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call getProfileSelectionRedirectUrls' });
        }
    }
    static getRedirecturi() {
        if (this.checkConfigured()) {
            return this.redirecturi;
        }
        else {
            throw new OiamErrorPayloadException({ ...ERR_NOT_CONFIGURED, details: 'call getRedirecturi' });
        }
    }
    static async isPendingCodeFlow(state) {
        const storedstate = await this.userManager.settings.stateStore.get(state);
        log.debug('oiam-oauth-wc: storedState', storedstate);
        return !!storedstate;
    }
    static async removePendingCodeFlow(state) {
        const storedstate = await this.userManager.settings.stateStore.get(state);
        if (storedstate) {
            await this.userManager.settings.stateStore.remove(state);
            log.debug('oiam-oauth-wc: removed pending code flow', state);
        }
        else {
            log.debug('oiam-oauth-wc: no pending code flow which could be removed', state);
        }
    }
    // ### private methods ########################################################
    static fillLoginArgs(loginStep, eServiceConfig) {
        // set login-filter if not logged in (normally state empty)
        // but if there is a forcedActingOnBehalfOf we came from a logged in context already
        // we can set the acting-type / acting-on-behalf-of on the first roundtrip to keycloak and save a roundtrip
        const acrString = buildAcrValues(eServiceConfig.acrValues, loginStep !== 'profile-selection' && !eServiceConfig.forcedActingOnBehalfOf ? 'login-filter' : 'acting-type', eServiceConfig.forcedActingOnBehalfOf);
        const args = {
            state: eServiceConfig.asPreviousInvocation(),
            ...(acrString ? { acr_values: acrString } : undefined),
        };
        if (loginStep === 'account-link') {
            switch (eServiceConfig.accountLink) {
                case 'passkey':
                    this.userManager.settings.extraQueryParams.kc_action = 'ba-setup-passkey';
                    break;
                case 'totp':
                    this.userManager.settings.extraQueryParams.kc_action = 'configure-otp';
                    break;
                case 'bundid':
                    args.prompt = 'bundid_link';
                    break;
                case 'muk':
                    args.prompt = 'muk_link';
                    break;
            }
        }
        // return args if not empty
        return Object.keys(args).length > 0 ? args : null;
    }
    static handleUrlAndState(state) {
        let strippedUrl = stripSpecialQueryparams(window.location.href, {});
        log.debug('Stripped query parameter: ' + strippedUrl);
        const u = new URL(strippedUrl);
        if (state) {
            u.searchParams.set('customstate', state);
        }
        // update query parameters in url (minus stripped ones, plus customstate)
        window.history.replaceState(null, '', u);
        // a previous customstate is not needed after dispatching a fresh customStateEvent
        StoreService.deletePreviousInvocation();
        StoreService.deleteRetryInvocation();
        if (state) {
            // custom state as oiamCustomStateEvent
            OauthEventService.doDispatchEvent(OiamEvents.CUSTOMSTATE, {
                customstate: state,
            });
        }
    }
    static fillSettings(config, queryparams) {
        const scope = ['openid', ...(config.scopes ?? [])].join(' ');
        return {
            authority: config.authority ? config.authority : this.defaultConfig.authority,
            client_id: config.clientid,
            client_secret: config.clientid,
            redirect_uri: this.redirecturi,
            response_type: 'code',
            scope,
            automaticSilentRenew: false,
            fetchRequestCredentials: 'include',
            post_logout_redirect_uri: config.logouturi
                ? this.stripLogoutUri(config.logouturi, true)
                : 'https://www.arbeitsagentur.de',
            metadataUrl: config.metadataUrl ? config.metadataUrl : '',
            extraQueryParams: queryparams,
            extraTokenParams: { 'correlation-id': StoreService.getInstance().instance },
            userStore: new oidcClientTs.WebStorageStateStore({ store: TokenStorageFactory.getTokenStorage() }),
        };
    }
    static stripLogoutUri(logouturi, warn) {
        const result = logouturi.split(/[?#]/)[0];
        if (warn && result !== logouturi) {
            log.info(`logouturi must not contain query params, these will be removed (was '${logouturi.substring(result.length)}')`);
        }
        return result;
    }
    static computeExtraQueryParams() {
        let queryparams = this.defaultConfig && this.defaultConfig.extraQueryParams ? this.defaultConfig.extraQueryParams : {};
        // set correlation id
        queryparams = { ...queryparams, 'correlation-id': StoreService.getInstance().instance };
        if (this.motp) {
            queryparams.motp = this.motp;
        }
        return queryparams;
    }
    static toPreviousInvocation(eServiceConfig) {
        const newPrev = {
            ...eServiceConfig.acrValues,
            customstate: eServiceConfig.customstate,
            ...(isSupportedAccountType(eServiceConfig.accountLink) ? { accountLink: eServiceConfig.accountLink } : undefined),
        };
        log.debug('current invocation will be the new "previous"', newPrev);
        return newPrev;
    }
    static forgetOnetimePartsOfPreviousInvocation(loginStep, p) {
        const parts = this.partsOfPreviousInvocationToForget(loginStep);
        const atLeastOneFieldToDelete = parts.some(f => f in p);
        if (!atLeastOneFieldToDelete) {
            return;
        }
        StoreService.setPreviousInvocation(p, parts);
    }
    static partsOfPreviousInvocationToForget(loginStep) {
        switch (loginStep) {
            case '(re-)login':
                return ['authnLevel', 'authnMethod'];
            case 'account-link':
                return ['authnLevel', 'authnMethod', 'accountLink'];
            case 'profile-selection':
                return ['authnLevel', 'authnMethod', 'accountLink', 'forceProfileSelection'];
        }
    }
    static async doSignout() {
        /**
         * There is no OAG on test.dev.ocp.webapp.idst.ibaintern.de and therefor no BA-SessionId cookie
         * To prevent errors in that case, remove the cookie at client side (logout)
         */
        StoreService.removeOiamSessionIdCookieOnTestDevOrLocalhost();
        return this.userManager.signoutRedirect();
    }
}

var NginxErrorLogger;
(function (NginxErrorLogger) {
    let _logurl;
    function setLogUrl(path) {
        // Special case fallback component
        // it is loaded from npm, getAssetPath does not work correctly in that case
        // just set it hard coded
        if (path) {
            _logurl = new URL(window.location.origin + path);
            return;
        }
        // Log URL is at a path relative to oiam-oauth-wc, but it has to be on the same host as the eService to avoid CORS errors.
        // getAssetPath has the path to our WC, but we have to ensure that the host of the eService' is used.
        const logpath = getAssetPath('../ct/mt/clienterror/');
        // normally getAssetPath returns a relative path
        // only if origin of current page and the origin of the oiam-oauth-wc differs, a full url is returned
        // change the host in such a cases
        if (logpath.startsWith('http://') || logpath.startsWith('https://')) {
            try {
                const scripturl = new URL(logpath);
                // set host to current page host
                scripturl.host = window.location.host;
                _logurl = scripturl;
            }
            catch (e) {
                log.warn('oiam-oauth-wc: failed to generate nginx error url', logpath, e);
            }
        }
        else {
            _logurl = new URL(window.location.origin + logpath);
        }
    }
    NginxErrorLogger.setLogUrl = setLogUrl;
    /**
     * Log error at nginx asynchronously
     * Use browser event queue, so no side effects to other functionality
     * @param error
     */
    function logError(error) {
        setTimeout(async () => {
            await doLog(error);
        }, 0);
    }
    NginxErrorLogger.logError = logError;
    async function doLog(error) {
        // no error in error log
        try {
            // error type is not always predictable -> convert it
            let err;
            if (isOiamErrorPayload(error)) {
                err = error;
            }
            else {
                err = {
                    id: 'OIAM-UNKNOWN',
                    message: 'Unknown error',
                    details: error,
                };
            }
            // get current session id if any
            let sid = '';
            const user = await OauthService.getUser();
            if (user) {
                const baJwt = new BaJwt(user.access_token);
                sid = baJwt.payload.sid;
            }
            _logurl.searchParams.set('sid', sid);
            _logurl.searchParams.set('errorid', err.id);
            _logurl.searchParams.set('errortext', err.message);
            // log error detail if any
            _logurl.searchParams.set('errordetail', formatDetail(err.details));
            _logurl.searchParams.set('owcstate', await StoreService.readState());
            _logurl.searchParams.set('authority', StoreService.getAuthority());
            _logurl.searchParams.set('clientid', StoreService.getClientId());
            // do fetch at nginx to log
            await fetch(_logurl, { headers: { 'correlation-id': StoreService.getInstance().instance } });
        }
        catch (e) {
            log.warn('Error writing server-side error-log', _logurl, e);
            return;
        }
    }
    NginxErrorLogger.doLog = doLog;
    function formatDetail(detail) {
        let formatted = detail ? detail : '';
        switch (typeof formatted) {
            case 'number':
            case 'boolean':
            case 'object': {
                // JSON.stringify does not work correctly on Error classes (one gets just empty {})
                // format errors separately
                if (formatted instanceof Error) {
                    formatted = formatted.name + ': ' + formatted.message;
                }
                else {
                    try {
                        formatted = JSON.stringify(formatted);
                    }
                    catch (e) {
                        formatted = 'format error';
                    }
                }
                break;
            }
            case 'bigint': {
                formatted = String(formatted);
                break;
            }
            case 'string': {
                // nothing
                break;
            }
            default: {
                formatted = 'unknown format';
            }
        }
        return formatted;
    }
    NginxErrorLogger.formatDetail = formatDetail;
})(NginxErrorLogger || (NginxErrorLogger = {}));

class EServiceConfig {
    constructor() {
        this._acrValues = {};
    }
    set setConfigPayload(payload) {
        // validate config payload
        if (!this.checkConfig(payload)) {
            throw new Error('config error');
        }
        this._setConfigPayload = payload;
        // generate acr values from given config
        this.setLoginAcrValues(payload);
    }
    get setConfigPayload() {
        return this._setConfigPayload;
    }
    set forcedActingOnBehalfOf(bafor) {
        this._forcedActingOnBehalfOf = bafor;
    }
    get forcedActingOnBehalfOf() {
        return this._forcedActingOnBehalfOf;
    }
    set acrValues(acr) {
        this._acrValues = acr;
    }
    get acrValues() {
        return this._acrValues;
    }
    get customstate() {
        return this._customstate;
    }
    get tdaUrl() {
        return this._setConfigPayload?.tdaUrl;
    }
    get accountLink() {
        return this._accountLink;
    }
    setLoginAcrValues(payload) {
        if (!payload) {
            return;
        }
        this.setOrDeleteAcrValue('authnLevel', payload);
        this.setOrDeleteAcrValue('authnMethod', payload);
        this.setOrDeleteAcrValue('actingType', payload);
        this.setOrDeleteAcrValue('actingOnBehalfOf', payload);
        this.setOrDeleteAcrValue('forceProfileSelection', payload);
        this._customstate = payload.customstate ? payload.customstate : '';
        this._accountLink = payload.accountLink ? payload.accountLink : undefined;
    }
    mergeLoginAcrValues(payload) {
        if (!payload) {
            return;
        }
        this.setAcrValue('authnLevel', payload);
        this.setAcrValue('authnMethod', payload);
        this.setAcrValue('actingType', payload);
        this.setAcrValue('actingOnBehalfOf', payload);
        this.setAcrValue('forceProfileSelection', payload);
        this._customstate = payload.customstate ? payload.customstate : this._customstate;
        this._accountLink = payload.accountLink ? payload.accountLink : this._accountLink;
    }
    /**
     * @returns the current requirements of the eService towards the JWT so that they can be
     * remembered as the previousInvocation.
     */
    asPreviousInvocation() {
        return {
            ...this.acrValues,
            ...(this.customstate ? { customstate: this.customstate } : undefined),
        };
    }
    setOrDeleteAcrValue(v, e) {
        if (v in e) {
            if (e[v]) {
                this.acrValues[v] = e[v];
            }
            else {
                delete this.acrValues[v];
            }
        }
    }
    setAcrValue(v, e) {
        if (v in e) {
            if (e[v]) {
                this.acrValues[v] = e[v];
            }
        }
    }
    checkConfig(config) {
        if (!config) {
            this.logErrorAndDispatchEvent('missing SetConfigEventPayload');
            return false;
        }
        if (!config?.clientid) {
            this.logErrorAndDispatchEvent('missing client id');
            return false;
        }
        if (!config?.redirecturi) {
            this.logErrorAndDispatchEvent('missing redirect uri');
            return false;
        }
        if (!isValidUri(config?.redirecturi)) {
            this.logErrorAndDispatchEvent('redirect uri is not a valid url');
            return false;
        }
        if (!isAllowedCombination(config.actingType, config.actingOnBehalfOf)) {
            this.logErrorAndDispatchEvent('invalid combination of acting-type and acting-on-behalf-of');
            return false;
        }
        return true;
    }
    logErrorAndDispatchEvent(errordetails) {
        const error = { ...ERR_INVALID_CONFIG, details: errordetails };
        NginxErrorLogger.logError(error);
        OauthEventService.doDispatchEvent(OiamEvents.ERROR, error);
    }
}

const parse = (desc) => {
    const usp = new URLSearchParams(desc);
    const type = usp.get('type');
    switch (type) {
        case 'profilanlage': {
            const baforprefix = usp.get('baforprefix');
            if (baforprefix) {
                return { type, baforprefix };
            }
            break;
        }
        case 'fehler': {
            const quelle = usp.get('quelle');
            const meldung = usp.get('meldung');
            return { type, quelle: quelle || 'unknown', meldung: meldung || desc };
        }
        case 'baidmatch': {
            const identity = usp.get('identity');
            const actingOnBehalfOf = usp.get('acting-on-behalf-of');
            if (identity && actingOnBehalfOf) {
                return { type, identity, actingOnBehalfOf: actingOnBehalfOf };
            }
            break;
        }
        case 'freischaltung':
        case 'abbruch':
        case 'reauth_required':
        case 'invalid_combination':
        case 'missing_session': {
            return { type };
        }
    }
    return genericError(desc);
};
const genericError = (meldung) => {
    return { type: 'fehler', quelle: 'unknown', meldung };
};

/**
 * Parses the query parameters for BA OIDC error messages and returns a suitable reaction.
 * This class has to be instantiated anew for each URL.
 */
class OidcErrorService {
    constructor(authority, profileSelectionRedirectUrls, redirecturi, tdaUrl, url) {
        this.authority = authority;
        this.profileSelectionRedirectUrls = profileSelectionRedirectUrls;
        this.redirecturi = redirecturi;
        this.tdaUrl = tdaUrl;
        this.url = new URL(url);
    }
    /**
     * Check our URL for (custom) OIDC errors and handle them.
     * We have implemented our own protocol using unmet_authentication_requirements which can trigger follow-up
     * actions like redirecting to profile creation.
     * Purges the error message from the browser history before returning to avoid repeated handling
     * in cases of browser back or alike.
     * @returns false is returned when everything is fine and the regular flow can should continue. The other values have to be treated specifically.
     * When a URL is returned, the caller has to redirect there.
     */
    checkForOidcErrors() {
        const qp = this.url.searchParams;
        if (qp.has('error')) {
            const error = qp.get('error');
            const description = qp.get('error_description');
            // there might be unexpected behavior at browser back
            // to prevent it, remove the error from browser history
            this.removeErrorFromBrowserHistory();
            if (error === 'unmet_authentication_requirements' && description) {
                const ped = parse(description);
                // log at nginx for alerting
                NginxErrorLogger.logError({
                    ...ERR_UNMET,
                    details: ped.type,
                });
                switch (ped.type) {
                    case 'freischaltung': {
                        return { url: this.calcFreischaltungUrl() };
                    }
                    case 'baidmatch': {
                        return {
                            url: this.calcBaidmatchUrl(ped.actingOnBehalfOf, ped.identity, ped.actingOnBehalfOf),
                        };
                    }
                    case 'profilanlage': {
                        return { url: this.calcProfilanlageUrl(ped.baforprefix) };
                    }
                    case 'abbruch': {
                        return { targetState: OauthStates.LOGGEDIN };
                    }
                    case 'reauth_required': {
                        return { targetState: OauthStates.REAUTHREQUIRED };
                    }
                    case 'invalid_combination':
                    case 'missing_session':
                    case 'fehler': {
                        return {
                            ...ERR_OIAM_GENERAL,
                            details: ped,
                        };
                    }
                    default: {
                        const exhaustiveCheck = ped;
                        throw new Error(`Unexpected value: ${exhaustiveCheck}`);
                    }
                }
            }
            else if (error === 'logout_requested') {
                return { targetState: OauthStates.LOGOUTREQUESTED };
            }
            else {
                const err = {
                    ...ERR_OIAM_GENERAL,
                    details: { error, description },
                };
                // log at nginx
                NginxErrorLogger.logError(err);
                return err;
            }
        }
        return false;
    }
    calcFreischaltungUrl() {
        const urlPattern = this.profileSelectionRedirectUrls[this.authority]?.freischaltung ??
            this.profileSelectionRedirectUrls.default.freischaltung;
        const url = urlPattern.replace('${CALLBACKURL}', this.callbackUrl());
        return new URL(url);
    }
    calcProfilanlageUrl(baforprefix) {
        const [_, urlPattern] = [
            ...Object.entries(this.profileSelectionRedirectUrls[this.authority]?.profilanlage ?? {}),
            ...Object.entries(this.profileSelectionRedirectUrls.default.profilanlage),
        ].find(([bp, _]) => baforprefix.startsWith(bp));
        const urlString = urlPattern.replace('${CALLBACKURL}', this.callbackUrl());
        const url = new URL(urlString);
        if (this.tdaUrl) {
            url.searchParams.set('tdaurl', this.tdaUrl);
        }
        return url;
    }
    calcBaidmatchUrl(bafor, identity, actingOnBehalfOf) {
        const [_, urlPattern] = [
            ...Object.entries(this.profileSelectionRedirectUrls[this.authority]?.baidmatch ?? {}),
            ...Object.entries(this.profileSelectionRedirectUrls.default.baidmatch),
        ].find(([bp, _]) => bafor.startsWith(bp));
        const url = urlPattern
            .replace('${CALLBACKURL}', this.callbackUrl())
            .replace('${IDENTITY}', identity)
            .replace('${ACTING_ON_BEHALF_OF}', actingOnBehalfOf);
        return new URL(url);
    }
    static redirectTo(url) {
        // purge our OIDC protocol query parameters from the URL, so that a browser back won't see them again
        const u = stripSpecialQueryparams(window.location.href, {});
        window.history.replaceState(null, '', u);
        // now navigate away
        window.location.href = url.toString();
    }
    callbackUrl() {
        // calling back redirecturi is fine because the original customstate
        // has to contain any special sub-route / params.
        const u = new URL(this.redirecturi);
        return encodeURIComponent(u.toString());
    }
    removeErrorFromBrowserHistory() {
        this.url.searchParams.delete('error');
        this.url.searchParams.delete('error_description');
        window.history.replaceState(null, '', this.url);
    }
}
/**
 * true for error types supported by checkForOidcErrors.
 * Must be implemented consistently with this method!
 */
OidcErrorService.isHandleableError = (e) => ['unmet_authentication_requirements', 'logout_requested'].includes(e);

class AuthnLevelTimes {
    constructor(authnLevel) {
        this.MAXSESSIONHOCH = 1800000; // 30min
        this.IDLEHOCH = 300000; // 5min
        this.IDLEWARNHOCH = 60000; // 1min
        this.MAXSESSIONSUBSTANTIELL = 7200000; // 120min
        this.IDLESUBSTANTIELL = 1800000; // 30min
        this.IDLEWARNSUBSTANTIELL = 300000; // 5min
        this.MAXSESSIONNORMAL = 43200000; // 720min
        this.IDLENORMAL = 3600000; // 60min
        this.IDLEWARNNORMAL = 300000; // 5min
        this.sessionTimeMap = new Map([
            [
                'STORK-QAA-Level-1',
                {
                    maxsessiontime: this.MAXSESSIONNORMAL,
                    idletime: this.IDLENORMAL,
                    idlewarntime: this.IDLEWARNNORMAL,
                    authnLevel: 'STORK-QAA-Level-1',
                },
            ],
            [
                'STORK-QAA-Level-2',
                {
                    maxsessiontime: this.MAXSESSIONNORMAL,
                    idletime: this.IDLENORMAL,
                    idlewarntime: this.IDLEWARNNORMAL,
                    authnLevel: 'STORK-QAA-Level-2',
                },
            ],
            [
                'STORK-QAA-Level-3',
                {
                    maxsessiontime: this.MAXSESSIONSUBSTANTIELL,
                    idletime: this.IDLESUBSTANTIELL,
                    idlewarntime: this.IDLEWARNSUBSTANTIELL,
                    authnLevel: 'STORK-QAA-Level-3',
                },
            ],
            [
                'STORK-QAA-Level-4',
                {
                    maxsessiontime: this.MAXSESSIONHOCH,
                    idletime: this.IDLEHOCH,
                    idlewarntime: this.IDLEWARNHOCH,
                    authnLevel: 'STORK-QAA-Level-4',
                },
            ],
        ]);
        this._authnLevel = authnLevel;
        this._sessionTime = this.sessionTimeMap.get(this._authnLevel);
    }
    get authnLevel() {
        return this._authnLevel;
    }
    get sessionTime() {
        return this._sessionTime;
    }
}

class SessionService {
    constructor() {
        this._timer = null;
        this.ACCESS_TOKEN_EXPIRING_TRESHOLD = 30000;
        this.idleWarningDispatched = false;
        // bind function for event listener add/remove
        this.handleUserAction = this.handleUserAction.bind(this);
    }
    /**
     * Needed in future with duplicated tabs handling -> always false at the moment
     *
     * With the token store per client on duplicated tabs, the token of the tab at visibility change might not be valid
     * any more. This happens, if there was a token refresh in another tab with the same clientid. We have to resend
     * the updated token to the eService in such a case.
     * Compare the token of the session service (possible old one) with the one got from token storage (current one).
     * Return true if jwts are different (we have to resend the current token).
     */
    haveToResendTokenOnActivating(baJwt) {
        return !!(this.bajwt?.payload.iat !== baJwt?.payload.iat);
    }
    /**
     * Check if there is an active and valid user session
     * or if there are actions necessary to get into a valid session state.
     */
    checkForValidSession(baJwt, parsedSessionCookie) {
        const sessionid = StoreService.checkForOiamSessionIdCookie();
        let action;
        // prevent double token renew in timer (see initTimer)
        this.bajwt = undefined;
        // oiamsession cookie is older than the idle time (invalid stale cookie)
        // this can happen if the browser is long open on non BA eService or the browser restore function is used
        action = this.checkForStaleData(parsedSessionCookie);
        log.debug('oiam-oauth-wc: checkForStaleData', action);
        // oiamsession cookie prechecks: logged out after idle/max session timeout
        action = this.checkLoggedoutAfterIdleMaxTimeout(action, parsedSessionCookie);
        log.debug('oiam-oauth-wc: checkLoggedoutAfterIdleMaxTimeout', action);
        action = this.checkLoggedOut(action, sessionid, parsedSessionCookie);
        log.debug('oiam-oauth-wc: checkLoggedOut', action);
        // check if there is no session
        action = this.checkExternalLoggedOut(action, sessionid, baJwt?.raw);
        log.debug('oiam-oauth-wc: checkExternalLoggedOut', action);
        // check if login/relogin is necessary
        action = this.checkValidLogin(action, sessionid, baJwt);
        log.debug('oiam-oauth-wc: checkValidLogin', action);
        // check for stepup
        action = this.checkForStepup(action, parsedSessionCookie, baJwt);
        log.debug('oiam-oauth-wc: checkForStepup', action);
        // restart session timer and user action handler if correctly logged in
        // else return the necessary action
        return this.restartSessionOrReturnAction(action, baJwt, parsedSessionCookie?.authnlevel);
    }
    /**
     * Start the local user session after receiving the tokens from IAM
     */
    startSession(jwt) {
        this.setJwt(jwt);
        const level = this.bajwt.payload['authn-level'];
        const idlestart = Date.now();
        // store profile if exists
        const profile = {
            ...(this.bajwt &&
                this.bajwt.payload['acting-on-behalf-of'] &&
                this.bajwt.payload['acting-on-behalf-of'].length > 0 && {
                actingOnBehalfOf: this.bajwt.payload['acting-on-behalf-of'],
            }),
            ...(this.bajwt && this.bajwt.payload['acting-type'] && { actingType: this.bajwt.payload['acting-type'] }),
            maxstart: this.bajwt.payload.auth_time,
            idlestart,
            authnlevel: level,
        };
        StoreService.storeOiamSessionCookieKeepAdditionalInfo(profile);
        this.startSessionTimer(level);
        this.startUserActionHandler();
    }
    /**
     * Stop the local user session at logout
     */
    stopSession(keepRetryInvocation) {
        this.bajwt = undefined;
        this.stopTimer();
        StoreService.deletePreviousInvocation();
        if (!keepRetryInvocation) {
            StoreService.deleteRetryInvocation();
        }
        document.removeEventListener('keypress', this.handleUserAction);
        document.removeEventListener('mouseup', this.handleUserAction);
    }
    /**
     * Set new jwt and authn-level always if successful received the token from IAM
     */
    setJwt(jwt) {
        this.bajwt = jwt;
        const level = jwt.payload['authn-level'];
        if (!this.authnLevelTimes || level !== this.authnLevelTimes.authnLevel) {
            this.authnLevelTimes = new AuthnLevelTimes(level);
        }
        this.ACCESS_TOKEN_EXPIRING_TIME = this.bajwt.expirationDate * 1000 - this.ACCESS_TOKEN_EXPIRING_TRESHOLD;
    }
    /**
     * Check if idle session time is expired
     */
    isIdleSessionExpired(parsedSessionCookie) {
        const sessionid = StoreService.checkForOiamSessionIdCookie();
        if (sessionid) {
            const cookiesession = parsedSessionCookie
                ? parsedSessionCookie
                : StoreService.getOiamSessionCookie();
            // no oiam session cookie? Technically, we can't determine that the session has exired.
            // In practice, other parts of the code detect this and trigger a relogin.
            if (!cookiesession) {
                return false;
            }
            // no authnLevelTimes set? Set it from cookie
            if (!this.authnLevelTimes) {
                this.authnLevelTimes = new AuthnLevelTimes(cookiesession.authnlevel);
            }
            // get idlestart from oiamsession or BackendAccessTS cookie
            const idlestart = this.getEffectiveIdleStart(cookiesession);
            const time = Date.now();
            const idletimeout = idlestart + this.authnLevelTimes.sessionTime.idletime;
            log.debug('oiam-oauth-wc: isIdleSessionExpired', 'idletimeout', new Date(idletimeout).toLocaleTimeString(), 'now', new Date(time).toLocaleTimeString(), this.authnLevelTimes.authnLevel);
            return time >= idletimeout;
        }
        return false;
    }
    /**
     * Check if max session time is expired
     */
    isMaxSessionExpired() {
        const maxtimeout = this.getSessionTimeout();
        if (!maxtimeout) {
            return false;
        }
        const time = Date.now();
        log.debug('oiam-oauth-wc: isMaxSessionExpired', 'maxtimeout', new Date(maxtimeout).toLocaleTimeString(), 'now', new Date(time).toLocaleTimeString(), this.authnLevelTimes.authnLevel);
        return time >= maxtimeout;
    }
    /**
     * Returns the session-timeout (in milliseconds) in regards of the authnLevel of the user
     * (if there is a JWT present)
     */
    getSessionTimeout() {
        if (this.bajwt?.payload) {
            return this.bajwt.payload.auth_time * 1_000 + this.authnLevelTimes.sessionTime.maxsessiontime;
        }
        return undefined;
    }
    /**
     * Sends an 'oiamSessionEvent' including the session-end-timestamp and a given AuthnLevel
     */
    async sendSessionEvent() {
        const sessionEnd = this.getSessionTimeout();
        if (sessionEnd !== undefined) {
            const detail = {
                sessionEnd,
                authnLevel: this.authnLevelTimes.authnLevel,
            };
            OauthEventService.doDispatchEvent(OiamEvents.SESSION, detail);
        }
    }
    /**
     * Creates an MaxSessionExpirationWarnEvent. Event Signature is depending on the FeatureToggle.
     *
     * @param msUntilSessionEnd
     * Time until the session will expire.
     * E.g. 30 minutes (accompanied with nextWarningMsBeforeSessionEnd=600_000) or 5 minutes
     * @param nextWarningMsBeforeSessionEnd
     * Informational value when the next session end warning will be dispatched.
     * E.g. 5 minutes for the 30 minutes event.
     *
     */
    sendMaxSessionExpireWarnEvent(msUntilSessionEnd, nextWarningMsBeforeSessionEnd) {
        const event = {
            msUntilSessionEnd,
            nextWarningMsBeforeSessionEnd,
        };
        OauthEventService.doDispatchEvent(OiamEvents.MAXSESSIONEXPIRATIONWARNING, event);
    }
    /**
     * Creates an IdleSessionExpireWarnEvent. Event Signature is depending on the FeatureToggle.
     *
     * @param msUntilIdleTimeout
     * ms until forced logoff if no further interaction occurs
     */
    sendIdleSessionExpireWarnEvent(msUntilIdleTimeout) {
        if (FeatureToggle.supportDeprecatedExpireEvents()) {
            OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIREWARNING, msUntilIdleTimeout);
        }
        else {
            const event = {
                msUntilIdleTimeout,
            };
            OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIRATIONWARNING, event);
        }
    }
    hasSessionBeenTerminated() {
        const sessionid = StoreService.checkForOiamSessionIdCookie();
        // OAG deletes this cookie when detecting a successful logout.
        // Its absence is a valid trigger to consider us as logged-out.
        return !sessionid;
    }
    // ### private methods ###############################################################
    /**
     * Check if there was a logout after idle or max session timeout to send the according expired event
     *
     * @param cookiesession
     * @private
     */
    checkLoggedoutAfterIdleMaxTimeout(oldaction, cookiesession) {
        if (oldaction !== 'next' || !cookiesession) {
            return oldaction;
        }
        if (cookiesession.additionalinfo && cookiesession.additionalinfo === 'loggedout-idle-timeout') {
            StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
            return 'idletimeout';
        }
        if (cookiesession.additionalinfo && cookiesession.additionalinfo === 'loggedout-max-timeout') {
            StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
            return 'maxtimeout';
        }
        if (this.isMaxSessionExpired()) {
            return 'maxtimeout';
        }
        return 'next';
    }
    /**
     * When logging out, we clear sessionStorage, but not oiamsessioncookie. OAG deletes BA-SessionId cookie.
     * When seeing its absence, we have to complete the cleanup by deleting oiamsessioncookie and sessionStorage
     * in case logoutredirecturi is on another origin.
     */
    checkLoggedOut(oldaction, sessionid, cookiesession) {
        if (oldaction !== 'next' || sessionid || !cookiesession) {
            return oldaction;
        }
        return 'cleanup-after-logout';
    }
    /**
     * Check for no session or external logged out
     */
    checkExternalLoggedOut(oldaction, sessionid, jwt) {
        if (oldaction !== 'next' || sessionid) {
            return oldaction;
        }
        // external logged out
        if (jwt) {
            return 'cleanup';
        }
        return 'no-session';
    }
    /**
     * Check if the idle session timed out at a non BA tab or the browser was restored with an old logged-in state
     * Delete the stale cookies in that case
     * Only execute the check if there is no previous action
     */
    checkForStaleData(cookiesession) {
        if (this.isStaleOiamCookie(cookiesession)) {
            return this.cleanupStaleData();
        }
        return 'next';
    }
    isStaleOiamCookie(cookiesession) {
        if (cookiesession) {
            if (!cookiesession.authnlevel) {
                log.info('oiam-oauth-wc: old oiamsession cookie without authnLevel, clean up and start anew');
                return true;
            }
            // return after idle/max-session-timeout
            if (cookiesession.additionalinfo) {
                return false;
            }
            const authnLevelTimes = new AuthnLevelTimes(cookiesession.authnlevel);
            const earliestPossibleIdlestart = Date.now() - authnLevelTimes.sessionTime.idletime;
            // get idlestart from oiamsession or BackendAccessTS cookie
            const idlestart = this.getEffectiveIdleStart(cookiesession);
            if (idlestart > 0 && idlestart < earliestPossibleIdlestart) {
                log.debug('oiam-oauth-wc: old oiamsession cookie with idle time already timed out!', idlestart, '<', earliestPossibleIdlestart);
                return true;
            }
        }
        return false;
    }
    /**
     * cleanup stale BA-SessionId and oiamsession cookie
     */
    cleanupStaleData() {
        StoreService.deleteOiamCookie(OiamCookie.SESSIONID);
        StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
        // cleanup stale sessionStorage
        return 'cleanup';
    }
    /**
     * Check for external logged in, different client id or different sessionid
     * Only execute the check if there is no previous action
     */
    checkValidLogin(oldaction, sessionid, baJwt) {
        if (oldaction === 'next') {
            // not logged in => no login
            if (!sessionid) {
                return 'next';
            }
            // sessionid, but no user
            if (!baJwt?.payload) {
                return 'login';
            }
            // different sessionid -> logout + login other tab
            if (baJwt.payload.sid !== sessionid) {
                return 'relogin';
            }
        }
        return oldaction;
    }
    /**
     * Check existing local user session against the values in the oiamsession cookie and decides
     * necessary actions to perform to get a valid session state.
     * Only execute the check if there is no previous action.
     */
    checkForStepup(oldaction, cookiesession, baJwt) {
        if (oldaction !== 'next') {
            return oldaction;
        }
        if (cookiesession) {
            if (!this.canIgnoreOwnOiamsessionCookie(cookiesession)) {
                // If the user has authenticated himself in the meantime, our refresh token can no longer be used.
                // We have to relogin to get a fresh one.
                const stepupDetected = baJwt.payload.auth_time < cookiesession.maxstart;
                if (stepupDetected) {
                    // If this is the same as before, nothing bad happens.
                    this.authnLevelTimes = new AuthnLevelTimes(cookiesession.authnlevel);
                    return 'relogin';
                }
            }
            return 'next';
        }
        // error case: no oiamsession cookie -> there must be something terrible wrong -> renew session at IAM
        return 'relogin';
    }
    /**
     * Restart session timer and user action handler
     * Only execute if there is no previous action
     */
    restartSessionOrReturnAction(oldaction, baJwt, authnlevel) {
        if (oldaction === 'next') {
            // we are logged in: restart session timer and user action handler etc.
            this.setJwt(baJwt);
            this.startSessionTimer(authnlevel);
            this.startUserActionHandler();
            return 'session-valid';
        }
        return oldaction;
    }
    setIdleStart() {
        const sessionid = StoreService.checkForOiamSessionIdCookie();
        if (sessionid) {
            this.idleWarningDispatched = false;
            // update session cookie for other tabs
            // store with current profile from cookie (else it would be deleted)
            StoreService.updateOiamSessionCookie({ idlestart: Date.now() });
        }
    }
    /**
     * Rekursiver Timer, der jede Sekunde einen Tick hat in dem die einzelnen Timeout entprechend Vertrauensniveau
     * geprüft werden.
     */
    initTimer() {
        this.stopTimer(); // allow only one instance
        this._timer = setTimeout(() => {
            const now = Date.now();
            this.check(now);
            this.initTimer();
        }, 1000);
    }
    check(datetime) {
        const parsedSessionCookie = StoreService.getOiamSessionCookie();
        // keine valide session mehr
        if (!parsedSessionCookie) {
            return;
        }
        // get idlestart from oiamsession or BackendAccessTS cookie
        let idlestart = this.getEffectiveIdleStart(parsedSessionCookie);
        // check external logout or idle session renew
        // only for inactive tabs
        if (document.visibilityState !== 'visible') {
            // check sessionid cookie for external logout
            // ToDo: Überdenken, ist doppelt mit updateAfterVisibilityChange und kann eigentlich weg
            if (this.hasSessionBeenTerminated()) {
                window.self.dispatchEvent(new CustomEvent(OiamEvents.DOLOGOUT, { detail: { reason: OauthStates.LOGGEDOUTEXTERNAL } }));
            }
        }
        const idletimeout = idlestart + this.authnLevelTimes.sessionTime.idletime;
        const idlewarntimeout = idletimeout - this.authnLevelTimes.sessionTime.idlewarntime;
        const maxtimeout = this.getSessionTimeout();
        // idle warn
        // only for active tab
        if (datetime >= idlewarntimeout && document.visibilityState === 'visible') {
            this.idleWarning();
        }
        // idle session timeout
        // check independent of visibility state of the tab
        if (datetime >= idletimeout) {
            log.debug('Idle Timer Tick', 'now=', new Date(datetime).toLocaleString(), 'idletimeout=', new Date(idletimeout).toLocaleString());
            // handled by state-service
            window.self.dispatchEvent(new CustomEvent(OiamEvents.DOLOGOUT, { detail: { reason: OauthStates.IDLETIMEDOUT } }));
            return;
        }
        // check max session timeout or necessary token refresh
        // only for active tab
        if (document.visibilityState === 'visible') {
            this.checkMaxSessionTimeoutWarn(maxtimeout, datetime);
            // max session timeout ?
            if (datetime >= maxtimeout) {
                log.debug('Max Session Timer Tick', 'now=', new Date(datetime).toLocaleString(), 'maxtimeout=', new Date(maxtimeout).toLocaleString());
                // handled by state-service
                window.self.dispatchEvent(new CustomEvent(OiamEvents.DOLOGOUT, { detail: { reason: OauthStates.MAXTIMEDOUT } }));
                return;
            }
            // check token and init token refresh if necessary
            if (this.bajwt) {
                if (this.shouldRefreshToken(datetime, idlestart)) {
                    log.debug('oiam-oauth-wc: token expired -> send renew token event. Now:', new Date(datetime).toLocaleTimeString(), 'idlestart:', new Date(idlestart).toLocaleTimeString(), 'iat:', new Date(this.bajwt.payload.iat * 1000).toLocaleTimeString());
                    window.self.dispatchEvent(new CustomEvent(OiamEvents.SENDTOKEN, { detail: { forcerenew: true } }));
                }
            }
        }
    }
    startSessionTimer(authnlevel) {
        this.authnLevelTimes = new AuthnLevelTimes(authnlevel);
        this.initTimer();
    }
    startUserActionHandler() {
        // remove existing handlers to prevent douple handling
        document.removeEventListener('keypress', this.handleUserAction);
        document.removeEventListener('mouseup', this.handleUserAction);
        // handle keyboard and click events as user actions
        document.addEventListener('keypress', this.handleUserAction);
        document.addEventListener('mouseup', this.handleUserAction);
    }
    checkMaxSessionTimeoutWarn(maxtimeout, datetime) {
        const remainingUntilTimeout = maxtimeout - datetime;
        // check 5min timeout
        if (remainingUntilTimeout <= 300000) {
            this.maxSessionExpireWarn5(remainingUntilTimeout);
            // do not send first warn message if already under 5min
            return;
        }
        // check 30min timeout -> first warn message
        if (this.authnLevelTimes.authnLevel !== 'STORK-QAA-Level-4' && remainingUntilTimeout <= 1800000) {
            this.maxSessionExpireWarn30(remainingUntilTimeout);
        }
    }
    shouldRefreshToken(datetime, idlestart) {
        if (datetime <= this.ACCESS_TOKEN_EXPIRING_TIME) {
            return false;
        }
        if (idlestart > this.bajwt.payload.iat * 1000) {
            return true;
        }
        return false;
    }
    idleWarning() {
        // idle warning only once
        if (!this.idleWarningDispatched) {
            this.sendIdleSessionExpireWarnEvent(this.authnLevelTimes.sessionTime.idlewarntime);
            this.idleWarningDispatched = true;
            log.debug('Idle Warn Timer Tick');
        }
    }
    maxSessionExpireWarn30(remainingTime) {
        // session expire warning only once
        const oiamSessionCookie = StoreService.getOiamSessionCookie();
        if (!oiamSessionCookie ||
            !oiamSessionCookie.additionalinfo ||
            oiamSessionCookie.additionalinfo !== 'max-warn-30-sent') {
            this.sendMaxSessionExpireWarnEvent(remainingTime, 5 * 60 * 1000);
            StoreService.setAdditionalInfoInOiamSessionCookie('max-warn-30-sent');
            log.debug('Session Expire Warn Timer Tick (30)');
        }
    }
    maxSessionExpireWarn5(remainingTime) {
        // session expire warning only once
        const oiamSessionCookie = StoreService.getOiamSessionCookie();
        if (!oiamSessionCookie ||
            !oiamSessionCookie.additionalinfo ||
            oiamSessionCookie.additionalinfo !== 'max-warn-5-sent') {
            this.sendMaxSessionExpireWarnEvent(remainingTime);
            StoreService.setAdditionalInfoInOiamSessionCookie('max-warn-5-sent');
            log.debug('Session Expire Warn Timer Tick (5)');
        }
    }
    /**
     * Compares session cookies' idlestart time with the last backend access at OAG.
     *
     * @returns: newest idle start time
     */
    getEffectiveIdleStart(cookiesession) {
        const oagAccess = StoreService.getBackendAccessTS() * 1000;
        let otherOidcWcActivity = 0;
        if (cookiesession) {
            otherOidcWcActivity = cookiesession.idlestart;
        }
        return Math.max(otherOidcWcActivity, oagAccess);
    }
    stopTimer() {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }
    handleUserAction() {
        this.setIdleStart();
    }
    canIgnoreOwnOiamsessionCookie(oiamsessioncookie) {
        return (StoreService.isSameInstance(oiamsessioncookie.instance) &&
            oiamsessioncookie.clientid === StoreService.getClientId());
    }
}

class StateService {
    /**
     * Init all services and deside actions according to state
     */
    constructor() {
        this.useMotp = false;
        this.forceProfileSelectionOnStartup = false;
        this.renewTokenErrorTimeout = 0;
        this.renewTokenErrorCount = 0;
        this.eServiceConfig = new EServiceConfig();
        this.sessionService = new SessionService();
        // check for a special query params in the URL
        this.rememberStartupParameters();
    }
    async handleBackForwardCache() {
        let oauthState = StoreService.readState();
        if (await this.isSessionTerminated(oauthState)) {
            return;
        }
        // don't retry auth flows
        if (this.isUnfinishedSessionStart()) {
            await this.clearUnfinishedSessionStart();
        }
        // only if configured
        if (OauthService.checkConfigured()) {
            // check BA-SessionId, oiamsession and old BaLoginSession cookie
            // some conditions lead to authorization code request and redirect here (login, relogin)
            await this.checkCookies(oauthState, { forceProfileSelectionOnCompatibleProfileChange: true });
        }
        else {
            // page not loaded completely from cache
            // hard reload to prevent empty page
            await OauthEventService.sendBeforeNavigationEventWithWait();
            window.location.reload();
        }
        // always send oiamTokenEvent at browser back
        await this.handleResendTokenState();
    }
    async updateStateAfterVisibleChange() {
        const oauthState = StoreService.readState();
        log.debug('oiam-oauth-wc: updateStateAfterVisibleChange', oauthState);
        if (await this.isSessionTerminated(oauthState)) {
            log.debug('oiam-oauth-wc: session terminated');
            return;
        }
        // check BA-SessionId, oiamsession and old BaLoginSession cookie
        // some conditions lead to authorization code request and redirect here (login, relogin)
        const resendtoken = await this.checkCookies(oauthState, { forceProfileSelectionOnCompatibleProfileChange: true });
        if (resendtoken) {
            // need to resend token
            await this.handleResendTokenState();
        }
    }
    async handleSendSessionEvent() {
        if (this.sessionService) {
            return this.sessionService.sendSessionEvent();
        }
    }
    async handleSendTokenEvent(p) {
        if (p?.forcerenew) {
            // get new token from iam
            await this.handleRenewtokenState();
            // get token failed -> do not send invalid token to eService
            if (this.renewTokenErrorCount > 0) {
                return;
            }
        }
        await this.handleResendTokenState();
    }
    async handleLoginEvent(loginArgs) {
        if (loginArgs) {
            this.eServiceConfig.setLoginAcrValues(loginArgs);
        }
        await this.handleLoginrequestedState(StoreService.readState());
    }
    async handleLogoutEvent(payload) {
        if (payload) {
            await this.changeState(payload.reason);
        }
        else {
            await this.handleLogoutRequestedState();
        }
    }
    async handleSetConfigEvent(setConfigPayload) {
        try {
            this.eServiceConfig.setConfigPayload = setConfigPayload;
        }
        catch {
            // invalid config will not be set -> do not process any further, an error event was already sent
            return;
        }
        await OauthService.setConfig(setConfigPayload);
        const ready = await this.updateStateAfterConfigSet();
        log.debug('oiam-oauth-wc: handleConfiguredEvent ready? ', ready);
        // send only if there are no errors
        if (ready) {
            // event to stop loaded event
            OauthEventService.emitter.emit(OauthEventService.CONFIGURED_EVENT);
            OauthEventService.doDispatchEvent(OiamEvents.READYFORLOGIN);
        }
        else {
            log.warn('Error while processing SetConfig, oiam-oauth-wc is not ReadyForLogin');
        }
    }
    /**
     * @returns true if oiamReadyForLogin should be sent afterward
     */
    async updateStateAfterConfigSet() {
        // check error responses in url
        // can be either from authorization code redirects or browser back -> check it independent of state
        const urlSearchParams = new URLSearchParams(window.location.search);
        if (OidcErrorService.isHandleableError(urlSearchParams.get('error'))) {
            // We wanted to get a code, but have received a more or less expected error instead.
            const errorCheckResult = await this.handleOidcErrors(urlSearchParams.get('state'));
            if (errorCheckResult === 'redirect') {
                // Allow some seconds for navigating away. Only return/continue in case the navigation fails (2 seconds timeout)
                await new Promise(resolve => setTimeout(resolve, 2000));
                return false;
            }
        }
        // do we have to resume an auth flow or create a profile first?
        const finishSessionStart = await this.isUnfinishedSessionStart();
        if (finishSessionStart) {
            const success = await this.handleAuthorizationCodeRequestedState();
            // could not get JWT
            if (!success) {
                return false;
            }
        }
        else {
            if (this.useMotp) {
                await this.changeState(OauthStates.LOGINREQUESTED);
                return;
            }
            if (this.forceProfileSelectionOnStartup) {
                this.eServiceConfig.setLoginAcrValues({ forceProfileSelection: true });
            }
            // check BA-SessionId, oiamsession and old BaLoginSession cookie
            // some conditions lead to authorization code request and redirect here (login, relogin)
            await this.checkCookies(StoreService.readState(), {
                forceProfileSelectionOnCompatibleProfileChange: false,
            });
        }
        // token event to communicate current login state
        await this.handleResendTokenState();
        return true;
    }
    async changeState(toState) {
        // prevent null state
        if (!toState) {
            return false;
        }
        // get current state
        let currentState = StoreService.readState();
        log.debug('oiam-oauth-wc: try changeState to ', toState, 'current state is ', currentState);
        switch (toState) {
            case OauthStates.LOGOUTAFTERIDLESESSIONTIMEOUT: {
                // handle redirect from IAM at logout after session expired
                this.handleLogoutAfterIdleSessionTimeoutState();
                break;
            }
            case OauthStates.LOGOUTAFTERMAXSESSIONTIMEOUT: {
                // handle redirect from IAM at logout after session expired
                this.handleLogoutAfterMaxSessionTimeoutState();
                break;
            }
            case OauthStates.LOGINREQUESTED: {
                /*
                start login if not already logged in
                 */
                await this.handleLoginrequestedState(currentState);
                break;
            }
            case OauthStates.RELOGINREQUESTED: {
                await this.handleReloginrequestedState('(re-)login');
                break;
            }
            case OauthStates.REAUTHREQUIRED: {
                await this.handleReauthRequiredState();
                break;
            }
            case OauthStates.PROFILESELECTIONREQUESTED: {
                await this.handleReloginrequestedState('profile-selection');
                break;
            }
            case OauthStates.ACCOUNTLINK_REQUESTED: {
                await this.handleReloginrequestedState('account-link');
                break;
            }
            case OauthStates.IDLETIMEDOUT: {
                // start logout if logged in
                await this.handleInternalIdleTimeoutState(currentState);
                break;
            }
            case OauthStates.MAXTIMEDOUT: {
                /*
                start logout if logged in
                 */
                await this.handleMaxTimeoutState(currentState);
                break;
            }
            case OauthStates.LOGOUTREQUESTED: {
                // start logout if logged in
                await this.handleLogoutRequestedState();
                break;
            }
            case OauthStates.LOGGEDOUTEXTERNAL: {
                // remove user session if logged in
                await this.handleExternalLoggedoutState(currentState);
                break;
            }
            case OauthStates.RENEWTOKEN: {
                // request a new JWT from IAM and send it as an event
                await this.handleRenewtokenState();
                break;
            }
            case OauthStates.LOGGEDIN: {
                // only one occurrence: cancel inside authorization code flow (profile selection)
                // reset the jwt inside SessionService
                const user = await OauthService.getUser();
                if (user) {
                    const baJwt = new BaJwt(user.access_token);
                    this.sessionService.setJwt(baJwt);
                }
                // save correct state
                StoreService.saveState(OauthStates.LOGGEDIN);
                // we have to strip the url from oidc query parameters,
                // check previous invocation and send custom state if any
                const prev = StoreService.getPreviousInvocation();
                OauthService.handleUrlAndState(prev?.customstate);
                break;
            }
        }
        return true;
    }
    async isSessionTerminated(oauthState) {
        if (oauthState === OauthStates.LOGGEDIN) {
            if (this.sessionService.isMaxSessionExpired()) {
                // logout with redirect to IAM
                await this.changeState(OauthStates.MAXTIMEDOUT);
                return true;
            }
            if (this.sessionService.isIdleSessionExpired()) {
                // logout with redirect to IAM
                await this.changeState(OauthStates.IDLETIMEDOUT);
                return true;
            }
            if (this.sessionService.hasSessionBeenTerminated()) {
                await this.changeState(OauthStates.LOGGEDOUTEXTERNAL);
                return true;
            }
        }
        return false;
    }
    async checkCookies(oauthState, options) {
        // check for login/logout in another tab and update idle session from another tab
        if (oauthState === OauthStates.EMPTY ||
            oauthState === OauthStates.LOGGEDIN ||
            oauthState === OauthStates.LOGGEDOUTEXTERNAL) {
            return await this.checkOiamSession(options);
        }
        return false;
    }
    // ### Internal State handler #####################################
    async handleResendTokenState() {
        const jwt = await OauthService.askForToken(false);
        const sendTokenBody = {
            clientid: StoreService.getClientId(),
            jwt: jwt,
        };
        OauthEventService.doDispatchEvent(OiamEvents.TOKEN, sendTokenBody);
    }
    async handleRenewtokenState() {
        // there is a token check at the session timer
        // if there is an error here this check leads to a new token request each second
        // -> handle errors and ignore calls for some time
        if (this.renewTokenErrorTimeout > 0) {
            this.renewTokenErrorTimeout--;
            return;
        }
        // store new jwt at SessionService for token refresh and check of authn-level change
        const jwt = await OauthService.askForToken(true).catch(async (e) => {
            // log at nginx
            NginxErrorLogger.logError({ ...ERR_NO_TOKEN, details: e?.error });
            // retry if no specific error
            if (!e || !e.error) {
                // skip session timer renew token calls for some seconds
                // extend the time at each error (2s, 4s, 6s ...)
                // it depends on "grace time" how often a retry occurs before invalid_grant is returned from keycloak
                this.renewTokenErrorCount++;
                this.renewTokenErrorTimeout = this.renewTokenErrorCount * 2;
            }
            else {
                // We have to authenticate anew. If we reliably had an up-to-date customstate,
                // we could relogin. But because this is not guaranteed, we "press F5" to re-enter
                // the current eService at the current URL.
                // But before, we throw away all tokens to enforce a relogin at reentry.
                this.renewTokenErrorCount = 0;
                this.renewTokenErrorTimeout = 0;
                await OauthService.removeUser();
                await OauthEventService.sendBeforeNavigationEventWithWait();
                window.location.reload();
            }
            return '';
        });
        if (jwt) {
            this.renewTokenErrorCount = 0;
            this.renewTokenErrorTimeout = 0;
            this.sessionService.setJwt(new BaJwt(jwt));
        }
    }
    async handleLoginrequestedState(oauthState) {
        if (oauthState === OauthStates.EMPTY ||
            oauthState === OauthStates.LOGGEDIN ||
            oauthState === OauthStates.LOGGEDOUTEXTERNAL) {
            let requestedLoginStep = '(re-)login';
            // only check jwt if internal state is logged-in
            // in all other cases there is no jwt or the state is stale and must be validated at IAM with new auth code flow
            if (oauthState === OauthStates.LOGGEDIN) {
                requestedLoginStep = await this.decideRequestedLoginStep(this.eServiceConfig);
            }
            if (requestedLoginStep !== 'none') {
                await OauthService.doLogin(requestedLoginStep, this.eServiceConfig).catch(err => {
                    this.resetAtErrorOrExternalLogout({ ...ERR_LOGIN, details: err });
                });
            }
            else {
                // protected eServices just send oiamLoginEvent not an oiamSendTokenEvent
                // the oiamTokenEvent is not automatically send if we are already logged in e.g. on reload page
                // resend token for protected eServices at oiamLoginEvent
                await this.handleResendTokenState();
            }
        }
    }
    async handleReloginrequestedState(loginStep) {
        await OauthService.doLogin(loginStep, this.eServiceConfig).catch(err => {
            this.resetAtErrorOrExternalLogout({ ...ERR_RELOGIN, details: err });
        });
    }
    /**
     * @returns true if the eService can continue to work, be it with a suiting JWT or ReadyForLogin
     *   (maybe after cleaning up an errorneous state).
     *   False if a redirect to profile-selection has been initiated.
     */
    async handleAuthorizationCodeRequestedState() {
        const urlSearchParams = new URLSearchParams(window.location.search);
        let user;
        const prev = StoreService.getPreviousInvocation();
        if (prev) {
            // If there is a previous invocation pending, use its customstate and ACR values.
            // Purpose: when getting directed to profile creation, the original deep link has to be remembered, and
            // when returning from profile creation, the deep link is no longer known (there hasn't been a CustomStateEvent
            // inbetween because there hasn't been a code but an unmet_authentication_requirements from the last OIAM redirect).
            // This customstate might have its specific ACR values attached, thus check against them.
            log.debug('Continuing previous invocation', prev);
            this.eServiceConfig.setLoginAcrValues(prev);
        }
        if (this.eServiceConfig.forcedActingOnBehalfOf?.length > 0) {
            // return from FSCE:
            //  check the forced acting-on-behalf-of against eService requirements
            //  if not matching, redirect to Profil-Online
            if (!fulfillsAnyForceActingOnBehalfOfRequirements(this.eServiceConfig.forcedActingOnBehalfOf, this.eServiceConfig.acrValues.actingOnBehalfOf, this.eServiceConfig.acrValues.actingType)) {
                const url = this.calcProfilonlineUrl(this.eServiceConfig.forcedActingOnBehalfOf.join(','));
                OidcErrorService.redirectTo(url);
                // Allow some seconds for navigating away. Only return/continue in case the navigation fails (2 seconds timeout)
                await new Promise(resolve => setTimeout(resolve, 2000));
                return false;
            }
        }
        else if (!urlSearchParams.get('code')) {
            log.warn('oiam-oauth-wc: Authorization request was sent but after redirect authorization code is missing in query parameters!');
        }
        else {
            user = await OauthService.getToken().catch(async (err) => {
                // error at getting tokens -> handle the error
                await this.resetAtErrorOrExternalLogout(OiamErrorPayloadException.extract(err));
                return null;
            });
            if (user) {
                this.sessionService.startSession(new BaJwt(user.access_token));
                // set correct internal state
                StoreService.saveState(OauthStates.LOGGEDIN);
            }
        }
        // check for correct profile
        // missing or wrong profile triggers an authorization code request and thus a redirect
        const continueProcessing = await this.checkProfile();
        if (continueProcessing === 'redirect') {
            return false;
        }
        // remove code, state from query params to prevent errors on relogin, bookmarks etc.
        OauthService.handleUrlAndState(user?.state?.customstate);
        return true;
    }
    async handleLogoutRequestedState() {
        // if (oauthState === OauthStates.LOGGEDIN || oauthState === OauthStates.AUTHCODEREQUESTED) {
        // clear session storage to delete all user data for the session (also data stored by eServices)
        // it must not be any user data available after session is logged out.
        // We have to delete now even though Keycloak might choose to display a logout confirmation page.
        // Clearing on come back is error-prone since the newly entered eService (like https://www.arbeitsagentur.de/)
        // could already have written entries into sessionStorage which shouldn't be deleted when
        // entering this page. Therefore we have to clear before leaving.
        StoreService.deleteUserData(false);
        this.sessionService.stopSession(false);
        return OauthService.doLogout().catch(err => {
            this.resetAtErrorOrExternalLogout(OiamErrorPayloadException.extract(err));
        });
    }
    async handleReauthRequiredState() {
        // logout, but keep retry invocation
        StoreService.deleteUserData(true);
        this.sessionService.stopSession(true);
        StoreService.copyRetryToPreviousInvocation();
        // set logoutredirecturi to redirecturi to immediately cause a login afterwards
        const updatedConfig = {
            ...this.eServiceConfig.setConfigPayload,
            logouturi: this.eServiceConfig.setConfigPayload.redirecturi,
        };
        await OauthService.setConfig(updatedConfig);
        return OauthService.doLogout().catch(err => {
            this.resetAtErrorOrExternalLogout(OiamErrorPayloadException.extract(err));
        });
    }
    async handleMaxTimeoutState(oauthState) {
        if (oauthState === OauthStates.LOGGEDIN) {
            this.sessionService.stopSession(false);
            // clear session storage to delete all user data for the session (also data stored by eServices)
            // it must not be any user data available after session is logged out
            StoreService.deleteUserData(false);
            // after logout we emit an max session timed out event so we have to know that state
            // to emit the event also after redirect to default logouturi www.arbeitsagentur.de we save at oiamsession event
            StoreService.setAdditionalInfoInOiamSessionCookie('loggedout-max-timeout');
            // actually we do not need a logout at max session time out (the session times out at server)
            // but:
            // - only a logout deletes the BA-SessionId cookie so the state in browser is clean and dispatched to all other BA tabs
            await OauthService.doLogout();
        }
    }
    async handleInternalIdleTimeoutState(oauthState) {
        if (oauthState === OauthStates.LOGGEDIN) {
            this.sessionService.stopSession(false);
            // clear session storage to delete all user data for the session (also data stored by eServices)
            // it must not be any user data available after session is logged out
            StoreService.deleteUserData(false);
            // after logout we emit an idle session timed out event so we have to know that state
            // to emit the event also after redirect to default logouturi www.arbeitsagentur.de we save at oiamsession event
            StoreService.setAdditionalInfoInOiamSessionCookie('loggedout-idle-timeout');
            await OauthService.doLogout();
        }
    }
    async handleLogoutAfterIdleSessionTimeoutState() {
        StoreService.deleteState();
        this.dispatchIdleSessionExpired();
    }
    dispatchIdleSessionExpired() {
        if (FeatureToggle.supportDeprecatedExpireEvents()) {
            OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIRED);
        }
        else {
            OauthEventService.doDispatchEvent(OiamEvents.IDLESESSIONEXPIRATION);
        }
    }
    async handleLogoutAfterMaxSessionTimeoutState() {
        StoreService.deleteState();
        OauthEventService.doDispatchEvent(OiamEvents.MAXSESSIONEXPIRATION);
    }
    async handleOidcErrors(state) {
        const profileSelectionRedirectUrls = OauthService.getProfileSelectionRedirectUrls();
        const tdaUrl = this.eServiceConfig.tdaUrl;
        const redirecturi = OauthService.getRedirecturi();
        // Check for errors from OIAM in the URL
        const oidcErrorService = new OidcErrorService(StoreService.getAuthority(), profileSelectionRedirectUrls, redirecturi, tdaUrl, window.location.href);
        const oidcError = oidcErrorService.checkForOidcErrors();
        if (typeof oidcError === 'boolean') {
            return 'continue';
        }
        OauthService.removePendingCodeFlow(state); // we know, what to do and it is no getToken
        if ('url' in oidcError) {
            // we have to redirect to profile creation or similar, login isn't completed yet
            log.debug('profile to be created/linked', oidcError.url);
            OidcErrorService.redirectTo(oidcError.url);
            return 'redirect'; // Redirection takes time, as the caller, prevent further processing!
        }
        else if ('targetState' in oidcError) {
            await this.changeState(oidcError.targetState);
            return 'continue'; // no real error
        }
        else if ('id' in oidcError) {
            // send event to eService
            OauthEventService.doDispatchEvent(OiamEvents.ERROR, oidcError);
            StoreService.deletePreviousInvocation();
            StoreService.deleteRetryInvocation();
            return 'continue';
        }
    }
    // ### external state handler ########################################
    async handleExternalLoggedoutState(oauthState) {
        if (oauthState === OauthStates.LOGGEDIN) {
            await this.resetAtErrorOrExternalLogout();
        }
        StoreService.deleteState();
    }
    /**
     * Checks for special query parameters that trigger an action on startup
     * - `motp` which contains a one-time login token. If present, use it for logging in immediately.
     * - `force-profile-selection` which triggers an immediate forced profile selection
     * These query parameters get read destructively, i.e. they get removed after storing them
     * in suiting attributes.
     */
    rememberStartupParameters() {
        const url = new URL(window.location.href);
        const motp = url.searchParams.get('motp');
        if (motp) {
            OauthService.setLoginMotp(motp);
            url.searchParams.delete('motp');
            window.history.replaceState(null, '', url);
        }
        const force = url.searchParams.get('force-profile-selection');
        if (force) {
            this.forceProfileSelectionOnStartup = true;
            url.searchParams.delete('force-profile-selection');
            window.history.replaceState(null, '', url);
        }
        this.useMotp = !!motp;
        // Explicitly does not remove acting-on-behalf-of, is also needed for baidmatch in combination with
        // unmet_authentication_requirements.
        // In that case, setting forcedActingOnBehalfOf is ignored, because there will be a redirect,
        // also ignored at the baidmatch page (identified by parameter identity)
        // no authentication request as the follow-up.
        if (!url.searchParams.has('identity') &&
            url.searchParams.has('acting-on-behalf-of') &&
            url.searchParams.get('acting-on-behalf-of')) {
            this.eServiceConfig.forcedActingOnBehalfOf = url.searchParams.get('acting-on-behalf-of').split(',');
        }
    }
    // ### error handler ############################################
    async resetAtErrorOrExternalLogout(err) {
        // dispatch error if exists
        if (err) {
            // log at nginx
            NginxErrorLogger.logError(OiamErrorPayloadException.extract(err));
            // send event for eService
            OauthEventService.doDispatchEvent(OiamEvents.ERROR, OiamErrorPayloadException.extract(err));
        }
        await this.cleanUpLocal();
        // send empty token to applications
        const sendTokenBody = {
            clientid: StoreService.getClientId(),
            jwt: '',
        };
        OauthEventService.doDispatchEvent(OiamEvents.TOKEN, sendTokenBody);
    }
    async cleanUpLocal() {
        // reset user in oidc-client-ts
        await OauthService.removeUser();
        // reset user session
        this.sessionService.stopSession(false);
        // remove any user data
        StoreService.deleteUserData(false);
    }
    // ### handle messages from other tabs ###################
    /**
     * Mega-method to check if the session as a whole and this eService are a match
     * or if the session has to be altered to match (login, relogin, different profile, ...)
     * @returns true if a new token has been requested and shall be sent as an event
     */
    async checkOiamSession(options = {}) {
        let resendtoken;
        const user = await OauthService.getUser();
        const parsedSessionCookie = StoreService.getOiamSessionCookie();
        const baJwt = (user?.access_token && new BaJwt(user?.access_token)) || undefined;
        // Resend token on visibility change, if there was a token refresh in another tab with the same clientid.
        // Check is based on the jwt got from token storage and the (possible old) jwt attribute in the session service.
        // We have to do the check here, because "checkForValidSession" resets the jwt inside the session service.
        resendtoken = this.sessionService.haveToResendTokenOnActivating(baJwt);
        const action = this.sessionService.checkForValidSession(baJwt, parsedSessionCookie);
        log.debug('oiam-oauth-wc: Session check finished', action);
        switch (action) {
            case 'idletimeout': {
                await this.changeState(OauthStates.LOGOUTAFTERIDLESESSIONTIMEOUT);
                break;
            }
            case 'maxtimeout': {
                await this.changeState(OauthStates.LOGOUTAFTERMAXSESSIONTIMEOUT);
                break;
            }
            case 'cleanup': {
                // remove stale user in oidc-client-ts
                if (user) {
                    await OauthService.removeUser();
                }
                StoreService.deleteState();
                // need to resend (empty) token (done in updateAfterConfigSet, updateAfterVisibilityChange)
                resendtoken = true;
                break;
            }
            case 'cleanup-after-logout': {
                // remove user and state
                // we might have an origin change at logout,
                // so the clear done before has no effect of the logout page
                if (user) {
                    await OauthService.removeUser();
                    StoreService.deleteState();
                }
                StoreService.deleteOiamCookie(OiamCookie.SESSIONCOOKIE);
                break;
            }
            case 'login': {
                // redirect needs time; do not resend an old token in between
                resendtoken = false;
                await this.changeState(OauthStates.LOGINREQUESTED);
                break;
            }
            case 'relogin': {
                // redirect needs time; do not resend an old token in between
                resendtoken = false;
                await this.changeState(OauthStates.RELOGINREQUESTED);
                break;
            }
            case 'session-valid': {
                await this.ensureLoggedIn();
                if (!baJwt.hasAtLeastAuthnLevel(this.eServiceConfig.acrValues.authnLevel)) {
                    // redirect needs time; do not resend an old token in between
                    resendtoken = false;
                    await this.changeState(OauthStates.RELOGINREQUESTED);
                    break;
                }
                // Session is still valid, no stepup needed. Now look at the content/profile of the JWT!
                const profileAction = checkOwnAndExternalProfile({
                    ownCurrent: {
                        actingType: baJwt?.payload?.['acting-type'],
                        actingOnBehalfOf: baJwt?.payload?.['acting-on-behalf-of'],
                    },
                    externalCurrent: {
                        actingType: parsedSessionCookie.actingType,
                        actingOnBehalfOf: parsedSessionCookie.actingOnBehalfOf,
                    },
                    ownRequested: {
                        ...this.eServiceConfig.acrValues,
                        forcedActingOnBehalfOf: this.eServiceConfig.forcedActingOnBehalfOf,
                    },
                });
                log.debug('oiam-oauth-wc: profile check finished', profileAction);
                switch (profileAction) {
                    case 'irrelevant': {
                        await this.changeState(OauthStates.RENEWTOKEN);
                        resendtoken = true;
                        break;
                    }
                    case 'incompatible': {
                        // redirect needs time; do not resend an old token in between
                        resendtoken = false;
                        await this.changeState(OauthStates.PROFILESELECTIONREQUESTED);
                        break;
                    }
                    case 'compatible': {
                        if (options.forceProfileSelectionOnCompatibleProfileChange) {
                            this.eServiceConfig.mergeLoginAcrValues({ forceProfileSelection: true });
                            // redirect needs time; do not resend an old token in between
                            resendtoken = false;
                            await this.changeState(OauthStates.PROFILESELECTIONREQUESTED);
                        }
                        else {
                            await this.changeState(OauthStates.RENEWTOKEN);
                            resendtoken = true;
                        }
                        break;
                    }
                    case 'force': {
                        this.eServiceConfig.mergeLoginAcrValues({ forceProfileSelection: true });
                        // redirect needs time; do not resend an old token in between
                        resendtoken = false;
                        await this.changeState(OauthStates.PROFILESELECTIONREQUESTED);
                        break;
                    }
                    case 'none': {
                        // Now that we know there is no reason outside of the JWT to renew it,
                        // we have to check whether it is still valid.
                        // TODO in BaJwt auslagern
                        const tokenExpired = baJwt.expirationDate * 1000 < new Date().getTime();
                        if (tokenExpired) {
                            log.debug('oiam-oauth-wc: token expired -> renew');
                            await this.changeState(OauthStates.RENEWTOKEN);
                            resendtoken = true;
                            break;
                        }
                    }
                }
                break;
            }
        }
        return resendtoken;
    }
    /**
     * Combination of logout_requested and browser back can lead to an erased state with a valid session.
     * Restore this state if needed.
     */
    async ensureLoggedIn() {
        const currentState = StoreService.readState();
        if (currentState != OauthStates.LOGGEDIN) {
            log.debug('StateService, session-valid, but currentState=', currentState);
            StoreService.saveState(OauthStates.LOGGEDIN);
        }
    }
    /**
     * Once we have a JWT, we can compare whether it is enough or we need a different profile.
     */
    async checkProfile() {
        const sessionid = StoreService.checkForOiamSessionIdCookie();
        // we are at the end of authorization-code-requested state after token request
        // if everything is ok we have a valid session
        if (sessionid) {
            // Check if a profile has to be selected, or we have to step-up
            const nextStep = await this.decideRequestedLoginStep(this.eServiceConfig);
            if (nextStep === 'none') {
                return 'stay';
            }
            else {
                const nextState = this.nextStepToState(nextStep);
                await this.changeState(nextState);
                return 'redirect';
            }
        }
        return 'stay'; // error state without redirecting
    }
    nextStepToState(nextStep) {
        switch (nextStep) {
            case 'profile-selection':
                return OauthStates.PROFILESELECTIONREQUESTED;
            case 'account-link':
                return OauthStates.ACCOUNTLINK_REQUESTED;
            case '(re-)login':
                return OauthStates.RELOGINREQUESTED;
        }
    }
    async decideRequestedLoginStep(eServiceConfig) {
        const user = await OauthService.getUser();
        if (!user) {
            return '(re-)login';
        }
        if (eServiceConfig.accountLink) {
            return 'account-link';
        }
        const baJwt = new BaJwt(user.access_token);
        if (!baJwt.hasAtLeastAuthnLevel(eServiceConfig.acrValues?.authnLevel)) {
            return '(re-)login';
        }
        if (!baJwt.hasMatchingAuthnMethod(eServiceConfig.acrValues?.authnMethod)) {
            return '(re-)login';
        }
        if (!baJwt.fulfills(eServiceConfig.acrValues) ||
            eServiceConfig.acrValues.forceProfileSelection ||
            !baJwt.hasMatchingActingOnBehalfOf(eServiceConfig.forcedActingOnBehalfOf)) {
            return 'profile-selection';
        }
        return 'none';
    }
    calcProfilonlineUrl(actingOnBehalfOf) {
        const profileSelectionRedirectUrls = OauthService.getProfileSelectionRedirectUrls();
        const urlPattern = profileSelectionRedirectUrls[StoreService.getAuthority()]?.profilonline ??
            profileSelectionRedirectUrls.default.profilonline;
        const url = urlPattern.replace('${ACTING_ON_BEHALF_OF}', actingOnBehalfOf);
        return new URL(url);
    }
    async isAuthCodeFlowPending() {
        const urlSearchParams = new URLSearchParams(window.location.search);
        if (!urlSearchParams.has('state')) {
            return false;
        }
        const state = urlSearchParams.get('state');
        const pending = await OauthService.isPendingCodeFlow(state);
        return pending;
    }
    /**
     * In case of an unfinished authentication code flow or when there are query parameters
     * which force a change in the current profile or when there is a previous invocation
     * pending, the caller has to complete the session start.
     * @returns true if we have to complete the session start (maybe by obtaining a JWT
     * or by checking the current JWT whether it matches all requirement) or we have to
     * redirect away.
     */
    async isUnfinishedSessionStart() {
        const sessionid = StoreService.checkForOiamSessionIdCookie();
        const prev = StoreService.getPreviousInvocation();
        if (prev && sessionid) {
            // without sessionid, we have to start anew, thus no continuation of a SessionStart
            return true;
        }
        if (this.eServiceConfig.forcedActingOnBehalfOf?.length > 0) {
            return true;
        }
        if (await this.isAuthCodeFlowPending()) {
            return true;
        }
        return false;
    }
    async clearUnfinishedSessionStart() {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const state = urlSearchParams.get('state');
        await OauthService.removePendingCodeFlow(state);
        StoreService.deletePreviousInvocation();
        StoreService.deleteRetryInvocation();
        const strippedUrl = stripSpecialQueryparams(window.location.href, {});
        log.debug('Stripped query parameter: ' + strippedUrl);
        const u = new URL(strippedUrl);
        window.history.replaceState(null, '', u);
    }
}

/**
 * Class gets external events from the EventQueue and dispatches it to the correct handler method
 */
class EventActionDispatcher {
    constructor() {
        this.stateService = new StateService();
    }
    async dispatchEvent(name, payload) {
        log.debug('oiam-oauth-wc: handle received event', name, payload ? payload : '');
        switch (name) {
            case OiamEvents.SETCONFIG: {
                await this.stateService.handleSetConfigEvent(payload);
                break;
            }
            case OiamEvents.DOLOGIN: {
                await this.stateService.handleLoginEvent(payload ? payload : undefined);
                break;
            }
            case OiamEvents.DOLOGOUT: {
                await this.stateService.handleLogoutEvent(payload ? payload : undefined);
                break;
            }
            case OiamEvents.SENDTOKEN: {
                await this.stateService.handleSendTokenEvent(payload ? payload : undefined);
                break;
            }
            case OiamEvents.SENDSESSION: {
                await this.stateService.handleSendSessionEvent();
                break;
            }
            case 'oiamHasBecomeVisible': {
                await this.stateService.updateStateAfterVisibleChange();
                break;
            }
            case 'loadedFromCache': {
                await this.stateService.handleBackForwardCache();
                break;
            }
            default: {
                log.warn('oiam-oauth-wc: event not supported', name);
            }
        }
    }
}

/**
 * Queue for all external events the oiam-oauth-wc does handle
 * Buffers incoming events until the first oiamSetConfigEvent is received.
 * Handles this event first before the buffered events get handled in order of their arrival.
 */
class EventQueue {
    constructor(eventActionDispatcher) {
        this.eventActionDispatcher = eventActionDispatcher;
        this.queuedEvents = [];
    }
    async push(event) {
        const eventToQueue = new QueuedEvent(event);
        // first oiamSetConfigEvent
        if (event.type === OiamEvents.SETCONFIG && !this.currentConfig) {
            await this.setEventToProcess(eventToQueue);
        }
        else {
            // ignore if event already in queue
            if (this.isAlreadyIn(eventToQueue)) {
                return;
            }
            this.queuedEvents.push(eventToQueue);
            // execute immediately if initial configured and not currently processing
            if (this.currentConfig && !this.eventCurrentlyProcessing) {
                await this.setEventToProcess(this.queuedEvents.shift());
                return;
            }
        }
    }
    async setEventToProcess(event) {
        this.eventCurrentlyProcessing = event;
        // no more queuedEvents -> end
        if (!event) {
            return;
        }
        // JS objects can be sent via events and give both sender and receiver a reference to
        // the same object and both can modify it with the other side seeing the changes immediately.
        // Semantic of an event is a snapshot, not changeable retroactively.
        // Therefore clone the payload.
        const clonedPayload = event.payload && JSON.parse(JSON.stringify(event.payload));
        // special case oiamSetConfigEvent: we have to remember current config
        if (event.name === OiamEvents.SETCONFIG) {
            this.currentConfig = clonedPayload;
        }
        await this.eventActionDispatcher.dispatchEvent(event.name, clonedPayload);
        // recursive call with next item in queue or undefined if queue is empty
        await this.setEventToProcess(this.queuedEvents.shift());
    }
    /**
     * Check if a given event is either
     * - the same that currently is processed
     * or
     * - it is a setConfigEvent with same config currently set
     * or
     * - it is already in the event queue waiting to be processed
     */
    isAlreadyIn(eventToQueue) {
        // first check against currently processed event
        const current = this.eventCurrentlyProcessing;
        if (current && current.name === eventToQueue.name) {
            if (compareObjectAnyOrder(eventToQueue.payload, current.payload)) {
                return true;
            }
        }
        // special case oiamSetConfigEvent: check against current set config
        if (eventToQueue.name === OiamEvents.SETCONFIG &&
            this.currentConfig &&
            compareObjectAnyOrder(eventToQueue.payload, this.currentConfig)) {
            return true;
        }
        // check queuedEvents
        let retval = false;
        this.queuedEvents
            .filter((qE) => qE.name === eventToQueue.name)
            .forEach((qE) => {
            if (compareObjectAnyOrder(qE.payload, eventToQueue.payload)) {
                retval = true;
            }
        });
        return retval;
    }
}
class QueuedEvent {
    constructor(event) {
        this.name = event.type;
        this.payload = event.detail;
    }
}

class Config {
    constructor() {
        this.authority = '';
        this.logLevel = 'info';
        this.extraQueryParams = {};
        this.metadataUrl = '';
        this.devmode = true;
        this.deprecatedExpireEvents = true;
    }
}

const fallbackConfig = () => {
    const u = new URL(window.location.href);
    switch (u.hostname) {
        case 'localhost':
        case 'web.dev.ocp.webapp.idst.ibaintern.de':
            log.info('oiam-oauth-wc: FallbackComponent: nonProd environment detected, selecting main/S-INT as fallback target');
            return fallbackConfigMain();
        default:
            log.info('oiam-oauth-wc: FallbackComponent: selecting prod as fallback target');
            return fallbackConfigProd();
    }
};
const fallbackConfigProd = () => {
    const config = new Config();
    config.authority = 'https://sso.arbeitsagentur.de/auth/realms/OCP';
    config.profileSelectionRedirectUrls = {
        default: {
            profilanlage: {
                'urn:bafor:step:person:': 'https://web.arbeitsagentur.de/kusos/stokure-webcomponents/?redirect-to=${CALLBACKURL}',
                'urn:bafor:kiwi:': 'https://web.arbeitsagentur.de/kostaf/fallzuordnung/start?redirect-to=${CALLBACKURL}',
                'urn:bafor:step:betrieb:': 'https://web.arbeitsagentur.de/portal/kontakt/de/unternehmen/unternehmensanfrage',
                'urn:bafor:pmventerprise:': 'https://web.arbeitsagentur.de/TODO/hinweisAufPmve',
            },
            baidmatch: {
                'urn:bafor:kiwi:': 'https://web.arbeitsagentur.de/kostaf/fallzuordnung/identitaet?identity=${IDENTITY}&fallnr=${ACTING_ON_BEHALF_OF}&redirect-to=${CALLBACKURL}',
                'urn:bafor:step:person:': 'https://web.arbeitsagentur.de/kusos/bsi-service/?identity=${IDENTITY}&acting-on-behalf-of=${ACTING_ON_BEHALF_OF}&redirect-to=${CALLBACKURL}',
            },
            freischaltung: 'https://web.arbeitsagentur.de/oiambk/oiambk-freischaltcode-einloesen-ui/freischaltcode?redirect-to=${CALLBACKURL}',
            profilonline: 'https://web.arbeitsagentur.de/profil/profil-ui/pd/?acting-on-behalf-of=${ACTING_ON_BEHALF_OF}',
        },
    };
    config.devmode = false;
    config.logLevel = 'info';
    return config;
};
const fallbackConfigMain = () => {
    const config = new Config();
    config.authority = 'https://sso.dev.ocp.webapp.idst.ibaintern.de/auth/realms/OCP';
    config.profileSelectionRedirectUrls = {
        default: {
            profilanlage: {
                'urn:bafor:step:person:': 'https://web.dev.ocp.webapp.idst.ibaintern.de/kusos-sint/stokure-webcomponents?redirect-to=${CALLBACKURL}',
                'urn:bafor:kiwi:': 'https://web.dev.ocp.webapp.idst.ibaintern.de/kostaf/fallzuordnung/sint/start?redirect-to=${CALLBACKURL}',
                'urn:bafor:step:betrieb:': 'https://web.arbeitsagentur.de/portal/kontakt/de/unternehmen/unternehmensanfrage',
                'urn:bafor:pmventerprise:': 'https://web.dev.ocp.webapp.idst.ibaintern.de/TODO/zur/authority/passende/url/hinweisAufPmve',
            },
            baidmatch: {
                'urn:bafor:kiwi:': 'https://web.dev.ocp.webapp.idst.ibaintern.de/kostaf/fallzuordnung/sint/identitaet?identity=${IDENTITY}&acting-on-behalf-of=${ACTING_ON_BEHALF_OF}&redirect-to=${CALLBACKURL}',
                'urn:bafor:step:person:': 'https://web.dev.ocp.webapp.idst.ibaintern.de/kusos-sint/bsi-service/?identity=${IDENTITY}&acting-on-behalf-of=${ACTING_ON_BEHALF_OF}&redirect-to=${CALLBACKURL}',
            },
            freischaltung: 'https://web.dev.ocp.webapp.idst.ibaintern.de/oiambk-sint/oiambk-freischaltcode-einloesen-ui/freischaltcode?redirect-to=${CALLBACKURL}',
            profilonline: 'https://web.dev.ocp.webapp.idst.ibaintern.de/profil-sint/profil-ui/pd/?acting-on-behalf-of=${ACTING_ON_BEHALF_OF}',
        },
    };
    config.devmode = false;
    config.logLevel = 'info';
    return config;
};

const OiamOauthFallbackComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.isOiamOauthComponetLoaded = false;
    }
    async componentWillLoad() {
        log.debug('oiam-oauth-fallback-wc: will-load');
        // wait time for hosted component from environment if set; default 2 seconds
        const waittime = Number.parseInt(Env.FALLBACK_WAIT_TIME) ;
        // check oiam-oauth-wc exists
        window.self.addEventListener(OiamEvents.LOADED, () => {
            this.isOiamOauthComponetLoaded = true;
        }, { once: true });
        // just wait 2s
        await new Promise(resolve => {
            setTimeout(resolve, waittime);
        });
        // we might miss the loaded event
        // additional check for hydrated attribute
        if (!this.isOiamOauthComponetLoaded) {
            const oauthWcs = document.getElementsByTagName('oiam-oauth-component');
            if (oauthWcs.length > 0) {
                const attrClass = oauthWcs[0].getAttribute('class');
                if (attrClass && attrClass.includes('hydrated')) {
                    this.isOiamOauthComponetLoaded = true;
                }
            }
        }
        // check hosted web component is loaded
        if (this.isOiamOauthComponetLoaded) {
            // return never resolving promise -> component will never load
            return new Promise(() => { });
        }
        // make sure only one instance is loaded
        if (!StoreService.createFallbackInstance()) {
            // return never resolving promise -> component will never load
            return new Promise(() => { });
        }
        const config = fallbackConfig();
        // getting path dynamically might not work on a npm loaded component
        // set with hard coded path
        NginxErrorLogger.setLogUrl('/oiambk/oiam-oauth-wc/ct/mt/clienterror/');
        this.eventQueue = new EventQueue(new EventActionDispatcher());
        await OauthService.init(config);
    }
    render() {
        return '';
    }
    componentDidLoad() {
        log.debug('oiam-oauth-wc did-render: register events');
        window.self.addEventListener(OiamEvents.DOLOGIN, e => this.eventQueue.push(e));
        window.self.addEventListener(OiamEvents.SETCONFIG, e => this.eventQueue.push(e));
        window.self.addEventListener(OiamEvents.DOLOGOUT, e => this.eventQueue.push(e));
        window.self.addEventListener(OiamEvents.SENDTOKEN, e => this.eventQueue.push(e));
        window.self.addEventListener(OiamEvents.SENDSESSION, e => this.eventQueue.push(e));
        document.addEventListener('visibilitychange', () => this.doStateActionsIfVisible());
        // handle loading from back-forward-cache
        window.addEventListener('pageshow', async (event) => {
            if (event.persisted) {
                // validate the correct state
                await this.eventQueue.push(new CustomEvent('loadedFromCache'));
            }
        });
        // loaded
        OauthEventService.doDispatchEvent(OiamEvents.LOADED, true);
    }
    async doStateActionsIfVisible() {
        // render component anew
        if (document.visibilityState === 'visible') {
            await this.eventQueue.push(new CustomEvent('oiamHasBecomeVisible'));
        }
    }
};

export { OiamOauthFallbackComponent as O };

